import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, managementAPI, trucksAPI, newsAPI, rentalAPI, profileAPI, companyDocsAPI, applicationsAPI, paymentSubmissionsAPI, broadcastAPI, preferencesAPI, mechanicAPI, channelSetupAPI, staffNotesAPI, loaAPI, tutorialsAPI, interviewAPI, teamAPI, cdlAPI, inventoryAPI } from '../api';
import {
  CheckCircle, XCircle, BookOpen, FileText, Award,
  LayoutDashboard, Users, LogOut, Truck, ClipboardList,
  Check, Clock, AlertTriangle, RefreshCw, UserCheck, AlertCircle,
  Newspaper, Trash2, Key, Copy, Plus, User, Upload, Shield, FolderOpen, Pencil, X,
  UserPlus, Ban, ChevronRight, DollarSign, Send, Wrench, Hash, NotebookPen, CalendarOff,
  BookMarked, ChevronDown, GripVertical, Radio, MessageSquare, Smile,
  AlignLeft, AlignCenter, AlignRight, Highlighter, ImageIcon, Code2, List, ListOrdered, Quote, Minus, Undo2, Redo2, Bold as BoldIcon, Italic as ItalicIcon, Strikethrough,
  Reply, Pin, Package, Boxes, Search, ArrowDownToLine, ArrowUpFromLine, Edit3, Tag, History, Palette,
  Lock, LayoutGrid, Rows3, MousePointer2, CheckSquare, Square, Activity, Move, RotateCcw,
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import DOMPurify from 'dompurify';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { createWorker } from 'tesseract.js';

const MANAGEMENT_ROLES = ['Management', 'Owner', 'Co-Owner', 'Admin'];

// ─── Sidebar config ───────────────────────────────────────────────────────────

const NAV_GENERAL = [
  { key: 'overview', label: 'Overview',      icon: LayoutDashboard },
  { key: 'news',     label: 'News & Updates', icon: Newspaper },
];
const NAV_MYWORK = [
  { key: 'documents',   label: 'Documents',  icon: BookOpen },
  { key: 'trucks',      label: 'Trucks',     icon: Truck },
  { key: 'profile',     label: 'My Profile', icon: User },
  { key: 'my_payments', label: 'Payments',   icon: DollarSign },
  { key: 'loa',         label: 'LOA Request', icon: CalendarOff },
];
const NAV_MANAGEMENT = [
  { key: 'drivers',        label: 'Driver Status',   icon: Users },
  { key: 'applications',   label: 'Applications',    icon: UserPlus },
  { key: 'payments',       label: 'Payments',        icon: DollarSign },
  { key: 'mechanic',       label: 'Mechanic',        icon: Wrench },
  { key: 'staff_notes',    label: 'My Notes',        icon: NotebookPen },
  { key: 'loa_mgmt',      label: 'LOA',             icon: CalendarOff },
  { key: 'cdl_mgmt',      label: 'CDL Requests',    icon: ClipboardList },
];

const NAV_TOOLS = [
  { key: 'mgmt_docs',      label: 'Archive',         icon: FolderOpen },
  { key: 'driver_names',   label: 'Driver Names',    icon: Shield },
  { key: 'badge_gen',      label: 'Badge Generator', icon: Award },
  { key: 'broadcast',      label: 'Broadcast',       icon: Send },
  { key: 'create_channel', label: 'Create Channel',  icon: Hash },
  { key: 'team_editor',    label: 'Team Page',       icon: Users },
];

const PAGE_TITLES = {
  overview:        'Overview',
  news:            'News & Updates',
  documents:       'Documents',
  trucks:          'Fleet Status',
  drivers:         'Driver Status',
  rental:          'Truck Rental',
  profile:         'My Profile',
  driver_names:    'Driver Names',
  badge_gen:       'Badge Generator',
  applications:    'Applications',
  mgmt_docs:       'Archive',
  payments:        'Payments',
  my_payments:     'Payments',
  broadcast:       'Broadcast',
  mechanic:        'Mechanic',
  create_channel:  'Create Channel',
  staff_notes:     'My Notes',
  loa:             'LOA Request',
  loa_mgmt:        'LOA Management',
  cdl_mgmt:        'CDL Requests',
  tutorials:       'Tutorials',
};

// ─── Overview ─────────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, iconColor, value, label }) => (
  <div className="brutalist-card p-6 flex items-center gap-4">
    <div className={`p-3 rounded-lg ${iconColor} shrink-0`}>
      <Icon className="w-5 h-5" strokeWidth={2} />
    </div>
    <div>
      <div className="text-3xl font-black">{value}</div>
      <div className="text-[var(--gty-text-secondary)] text-xs uppercase tracking-widest mt-0.5">{label}</div>
    </div>
  </div>
);

const RosterStats = () => {
  const [rosterStats, setRosterStats] = React.useState(null);

  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/roster-stats`, { credentials: 'include' })
      .then(r => r.json())
      .then(setRosterStats)
      .catch(() => {});
  }, []);

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      <StatCard icon={Users}       iconColor="bg-purple-900 text-purple-300" value={rosterStats?.total_drivers  ?? '—'} label="Total Drivers"  />
      <StatCard icon={UserCheck}   iconColor="bg-green-900  text-green-300"  value={rosterStats?.active_drivers ?? '—'} label="Active Drivers" />
      <StatCard icon={Clock}       iconColor="bg-yellow-900 text-yellow-300" value={rosterStats?.pending_dues   ?? '—'} label="Pending Dues"   />
      <StatCard icon={AlertCircle} iconColor="bg-red-900    text-red-300"    value={rosterStats?.overdue        ?? '—'} label="Overdue"        />
      <StatCard icon={CheckCircle} iconColor="bg-blue-900   text-blue-300"   value={rosterStats?.paid_this_week ?? '—'} label="Paid This Week" />
    </div>
  );
};

const OverviewContent = ({ user, paymentStatus, drivers, isManagement, newsPosts, onOpenNews }) => {
  const paidCount   = drivers.filter(d => d.has_paid).length;
  const unpaidCount = drivers.length - paidCount;
  const avatarUrl   = user?.custom_avatar_url
    ? (user.custom_avatar_url.startsWith('http') ? user.custom_avatar_url : `${process.env.REACT_APP_BACKEND_URL || ''}${user.custom_avatar_url}`)
    : user?.avatar
      ? `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`
      : `https://cdn.discordapp.com/embed/avatars/0.png`;

  const latestNews = newsPosts.slice(0, 3);

  const formatDate = (dateStr) => {
    try { return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return dateStr; }
  };

  return (
    <div className="space-y-8">
      <p className="text-[var(--gty-text-secondary)]">Welcome back, <span className="text-white font-bold">{user?.display_name || user?.username}</span></p>

      {isManagement && <RosterStats />}

      {!isManagement && (
        <div className={`p-6 rounded-xl border ${paymentStatus?.has_paid ? 'status-paid' : 'status-unpaid'}`}>
          <div className="flex items-start gap-4">
            <div className="shrink-0">
              {paymentStatus?.has_paid ? <CheckCircle className="w-10 h-10" /> : <XCircle className="w-10 h-10" />}
            </div>
            <div>
              <h3 className="text-xl font-bold mb-1">{paymentStatus?.has_paid ? 'Membership Paid' : 'Payment Required'}</h3>
              <p className="opacity-90 text-sm leading-relaxed">{paymentStatus?.message}</p>
            </div>
          </div>
        </div>
      )}

      <div className="brutalist-card p-6 flex items-center gap-5">
        <img src={avatarUrl} alt={user?.username} className="w-16 h-16 rounded-full border-4 border-[var(--gty-accent)] shrink-0" />
        <div>
          <div className="text-lg font-black">{user?.display_name || user?.username}</div>
          <div className="flex flex-wrap gap-2 mt-1">
            {user?.role_names?.map((role, i) => <span key={i} className="status-badge">{role}</span>)}
          </div>
        </div>
      </div>

      {/* Latest News Preview */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-secondary)]">Latest News</h2>
          <button onClick={onOpenNews} className="text-xs text-[var(--gty-accent)] hover:text-[var(--gty-accent)] uppercase tracking-wider font-bold transition-colors">
            View All →
          </button>
        </div>
        {latestNews.length === 0 ? (
          <p className="text-[var(--gty-text-faint)] text-sm">No announcements yet.</p>
        ) : (
          <div className="space-y-3">
            {latestNews.map(post => (
              <div key={post.id} className="bg-[var(--gty-bg-card)] border border-[var(--gty-border)] rounded p-4">
                <div className="flex items-start justify-between gap-4 mb-1">
                  <span className="font-bold text-sm">{post.title}</span>
                  <span className="text-xs text-[var(--gty-text-faint)] shrink-0">{formatDate(post.created_at)}</span>
                </div>
                <p className="text-[var(--gty-text-secondary)] text-xs leading-relaxed line-clamp-2">{post.content}</p>
                <p className="text-[var(--gty-text-faint)] text-xs mt-2">— {post.author_name}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Trucks ───────────────────────────────────────────────────────────────────

const issueTypes = ['Vehicle Blew Up','Vehicle Smoking','Vehicle On Fire','Vehicle Wont Start','Flat or Broken Tire','Visual Damage'];
const inventoryItems = ['Cones','Repair kit','Tire repair kit','Fire extinguisher',"5x Mike's Jerky"];

const TrucksContent = ({ user }) => {
  const [trucks,   setTrucks]   = useState([]);
  const [stats,    setStats]    = useState({ available: 0, in_use: 0, issues: 0 });
  const [loading,  setLoading]  = useState(true);
  const [repairOpen,  setRepairOpen]  = useState(false);
  const [missingOpen, setMissingOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [repairData,  setRepairData]  = useState({ issue_type: 'Vehicle blew up', description: '' });
  const [missingItems, setMissingItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([trucksAPI.getTrucks(), trucksAPI.getTruckStats()]);
      setTrucks(t.data);
      setStats(s.data);
    } catch { toast.error('Failed to load trucks'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUse = async (truck) => {
    if (truck.status === 'in_use') {
      if (truck.current_user_id === user?.id) {
        if (window.confirm('Return this truck?')) {
          try { await trucksAPI.returnTruck(truck.id); toast.success('Returned!'); load(); }
          catch (e) { toast.error(e.response?.data?.detail || 'Failed to return'); }
        }
      }
    } else {
      try { await trucksAPI.useTruck(truck.id); toast.success('Checked out!'); load(); }
      catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    }
  };

  const handleOverrideReturn = async (truck) => {
    if (!window.confirm(`Override return "${truck.name}"?\n(checked out by ${truck.current_user_name || 'someone else'})`)) return;
    try {
      await trucksAPI.overrideReturn(truck.id);
      toast.success(`"${truck.name}" returned via override.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Override failed — insufficient permissions');
    }
  };

  const submitRepair = async () => {
    if (!selected) return;
    setSubmitting(true);
    try { await trucksAPI.reportRepair(selected.id, repairData); toast.success('Repair reported!'); setRepairOpen(false); load(); }
    catch { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  const submitMissing = async () => {
    if (!selected || missingItems.length === 0) { toast.error('Select at least one item'); return; }
    setSubmitting(true);
    try { await trucksAPI.reportMissingItems(selected.id, { missing_items: missingItems }); toast.success('Reported!'); setMissingOpen(false); setMissingItems([]); }
    catch { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  const toggle = (item) => setMissingItems(p => p.includes(item) ? p.filter(i => i !== item) : [...p, item]);

  if (loading) return <div className="text-[var(--gty-text-muted)] text-xs uppercase">Loading...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="grid grid-cols-3 gap-4 flex-1 mr-4">
          <div className="bg-[var(--gty-bg-card)] p-4 rounded">
            <div className="flex items-center gap-2 mb-1"><Check className="w-4 h-4 text-green-400" /><span className="text-xs uppercase text-[var(--gty-text-muted)]">Available</span></div>
            <div className="text-3xl font-black">{stats.available}</div>
          </div>
          <div className="bg-[var(--gty-bg-card)] p-4 rounded">
            <div className="flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-yellow-400" /><span className="text-xs uppercase text-[var(--gty-text-muted)]">In Use</span></div>
            <div className="text-3xl font-black">{stats.in_use}</div>
          </div>
          <div className="bg-[var(--gty-bg-card)] p-4 rounded">
            <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4 text-red-400" /><span className="text-xs uppercase text-[var(--gty-text-muted)]">Issues</span></div>
            <div className="text-3xl font-black">{stats.issues}</div>
          </div>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 py-2 bg-[var(--gty-bg-card)] hover:bg-[var(--gty-bg-hover)] text-xs uppercase rounded">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {trucks.map(truck => (
          <div key={truck.id} className="bg-[var(--gty-bg-card)] rounded overflow-hidden">
            <div className="relative">
              <img src="/PhantomCustom.png" alt={truck.name} className="w-full h-28 object-cover" />
              <div className="absolute top-2 right-2">
                {truck.status === 'under_maintenance'
                  ? <Wrench className="w-5 h-5 text-orange-400" strokeWidth={3} />
                  : truck.status === 'available'
                    ? <Check className="w-5 h-5 text-green-400" strokeWidth={3} />
                    : <Clock className="w-5 h-5 text-yellow-400" strokeWidth={3} />}
              </div>
              {truck.has_open_issue && (
                <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-red-900/80 border border-red-500 rounded px-1.5 py-0.5">
                  <X className="w-3 h-3 text-red-400" strokeWidth={3} />
                  <span className="text-[10px] font-bold text-red-300 uppercase">Mechanic needed</span>
                </div>
              )}
            </div>
            <div className="p-3">
              <div className="text-sm font-bold uppercase mb-0.5">{truck.name}</div>
              <div className="text-xs text-[var(--gty-text-muted)] mb-3">{truck.number}</div>
              {truck.status === 'under_maintenance' && (
                <div className="mb-2 text-xs font-bold text-orange-400 uppercase flex items-center gap-1">
                  <Wrench className="w-3 h-3" /> Under Maintenance
                </div>
              )}
              <div className="space-y-1.5">
                {truck.status === 'under_maintenance' ? (
                  <div className="w-full py-1.5 text-xs font-bold uppercase rounded text-center bg-[var(--gty-bg-hover)] text-orange-400">In Repair</div>
                ) : truck.status === 'available' ? (
                  <button onClick={() => handleUse(truck)}
                    className="w-full py-1.5 text-xs font-bold uppercase rounded bg-yellow-500 hover:bg-yellow-400 text-black">
                    Use
                  </button>
                ) : truck.current_user_id === user?.id ? (
                  <button onClick={() => handleUse(truck)}
                    className="w-full py-1.5 text-xs font-bold uppercase rounded bg-green-500 hover:bg-green-400 text-black">
                    Return
                  </button>
                ) : (
                  <>
                    <div className="w-full py-1.5 text-xs font-bold uppercase rounded text-center bg-[var(--gty-bg-hover)] text-[var(--gty-text-muted)]">
                      In Use — {truck.current_user_name || '?'}
                    </div>
                    <button onClick={() => handleOverrideReturn(truck)}
                      className="w-full py-1.5 text-xs font-bold uppercase rounded bg-purple-700 hover:bg-purple-600 text-white flex items-center justify-center gap-1.5">
                      <RotateCcw className="w-3 h-3" /> Override Return
                    </button>
                  </>
                )}
                <button onClick={() => { setSelected(truck); setRepairData({ issue_type: 'Vehicle blew up', description: '' }); setRepairOpen(true); }}
                  className="w-full py-1.5 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] text-xs font-bold uppercase rounded">Report Issue</button>
                <button onClick={() => { setSelected(truck); setMissingItems([]); setMissingOpen(true); }}
                  className="w-full py-1.5 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] text-xs font-bold uppercase rounded">Missing Items</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Repair Modal */}
      <Dialog open={repairOpen} onOpenChange={setRepairOpen}>
        <DialogContent className="bg-[#111114] border border-zinc-800">
          <DialogHeader>
            <DialogTitle>Report Issue — {selected?.name}</DialogTitle>
            <DialogDescription className="text-xs text-slate-400">Select the type of issue</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1">
              {issueTypes.map(t => (
                <label key={t} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="radio" name="issue_type" value={t} checked={repairData.issue_type === t}
                    onChange={e => setRepairData({ ...repairData, issue_type: e.target.value })} className="w-3 h-3" />
                  <span className="text-slate-300">{t}</span>
                </label>
              ))}
            </div>
            <div>
              <Label className="text-xs mb-1 block">Details (optional)</Label>
              <Textarea placeholder="Additional info..." value={repairData.description}
                onChange={e => setRepairData({ ...repairData, description: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-sm" rows={3} />
            </div>
            <button onClick={submitRepair} disabled={submitting}
              className="w-full py-2 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-dim)] text-sm font-bold uppercase rounded disabled:opacity-50">
              {submitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Missing Items Modal */}
      <Dialog open={missingOpen} onOpenChange={setMissingOpen}>
        <DialogContent className="bg-[#111114] border border-zinc-800">
          <DialogHeader>
            <DialogTitle>Missing Items — {selected?.name}</DialogTitle>
            <DialogDescription className="text-xs text-slate-400">Select missing items</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {inventoryItems.map(item => (
              <label key={item} className="flex items-center gap-3 cursor-pointer text-sm">
                <input type="checkbox" checked={missingItems.includes(item)} onChange={() => toggle(item)} className="w-4 h-4" />
                <span className="text-slate-300">{item}</span>
              </label>
            ))}
            <button onClick={submitMissing} disabled={submitting}
              className="w-full mt-4 py-2 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-dim)] text-sm font-bold uppercase rounded disabled:opacity-50">
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Logs ─────────────────────────────────────────────────────────────────────

const LogsContent = () => {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trucksAPI.getTruckLogs()
      .then(r => setLogs(r.data))
      .catch(() => toast.error('Failed to load logs'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[var(--gty-text-muted)] text-xs uppercase">Loading...</div>;

  return logs.length === 0 ? (
    <p className="text-[var(--gty-text-secondary)] text-sm">No logs yet.</p>
  ) : (
    <div className="space-y-2">
      {logs.map(log => (
        <div key={log.id} className="flex items-center justify-between p-3 bg-[var(--gty-bg-card)] rounded text-sm">
          <div className="flex items-center gap-3">
            <span className="font-bold">{log.truck_name}</span>
            <span className="text-[var(--gty-text-muted)]">#{log.truck_number}</span>
            <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${log.action === 'checked_out' ? 'bg-yellow-900 text-yellow-300' : 'bg-green-900 text-green-300'}`}>
              {log.action === 'checked_out' ? 'Out' : 'Returned'}
            </span>
          </div>
          <div className="text-right">
            <div className="text-xs font-semibold">{log.driver_name}</div>
            <div className="text-xs text-[var(--gty-text-muted)]">{new Date(log.timestamp).toLocaleString()}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Company Documents ────────────────────────────────────────────────────────

const DOC_CATEGORIES = ['Policy', 'SOP', 'Rank Structure', 'General'];

const DocumentsContent = ({ isManagement }) => {
  const [docs, setDocs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeDoc, setActiveDoc] = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [editDoc, setEditDoc]     = useState(null);
  const [form, setForm]           = useState({ title: '', category: 'Policy', content: '' });
  const [saving, setSaving]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await companyDocsAPI.getDocuments();
      setDocs(r.data);
    } catch { toast.error('Failed to load documents'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditDoc(null);
    setForm({ title: '', category: 'Policy', content: '' });
    setShowForm(true);
  };

  const openEdit = (doc) => {
    setEditDoc(doc);
    setForm({ title: doc.title, category: doc.category, content: doc.content });
    setShowForm(true);
    setActiveDoc(null);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error('Title and content required'); return; }
    setSaving(true);
    try {
      if (editDoc) {
        await companyDocsAPI.updateDocument(editDoc.id, form);
        toast.success('Document updated');
      } else {
        await companyDocsAPI.createDocument(form);
        toast.success('Document created');
      }
      setShowForm(false);
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    try {
      await companyDocsAPI.deleteDocument(doc.id);
      toast.success('Deleted');
      if (activeDoc?.id === doc.id) setActiveDoc(null);
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const grouped = DOC_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = docs.filter(d => d.category === cat);
    return acc;
  }, {});

  const inputCls = 'w-full bg-[var(--gty-bg-card)] border border-[var(--gty-border2)] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--gty-accent)] transition-colors';

  if (loading) return <div className="text-[var(--gty-text-muted)] text-xs uppercase">Loading...</div>;

  if (showForm) return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-sm font-black uppercase tracking-widest">{editDoc ? 'Edit Document' : 'New Document'}</h2>
        <button onClick={() => setShowForm(false)} className="p-1.5 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] rounded transition-colors"><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-[var(--gty-text-secondary)] mb-1.5">Title</label>
          <input className={inputCls} value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="Document title" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-[var(--gty-text-secondary)] mb-1.5">Category</label>
          <select className={inputCls} value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))}>
            {DOC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-[var(--gty-text-secondary)] mb-1.5">Content</label>
          <textarea className={inputCls + ' resize-none'} rows={14} value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} placeholder="Write document content here..." />
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-hover)] text-sm font-bold uppercase rounded-lg transition-colors disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={() => setShowForm(false)} className="px-6 py-2 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] text-sm font-bold uppercase rounded-lg transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );

  if (activeDoc) return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => setActiveDoc(null)} className="p-1.5 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] rounded transition-colors text-xs font-bold uppercase tracking-widest flex items-center gap-1.5">
          ← Back
        </button>
        <span className="text-xs text-[var(--gty-text-muted)] uppercase tracking-widest">{activeDoc.category}</span>
        {isManagement && (
          <div className="ml-auto flex gap-2">
            <button onClick={() => openEdit(activeDoc)} className="p-1.5 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] rounded transition-colors"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => handleDelete(activeDoc)} className="p-1.5 bg-[var(--gty-bg-hover)] hover:bg-red-900 text-red-400 rounded transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      <h2 className="text-2xl font-black mb-1">{activeDoc.title}</h2>
      <p className="text-xs text-[var(--gty-text-muted)] mb-6">
        {activeDoc.author_name && `by ${activeDoc.author_name} · `}
        {new Date(activeDoc.updated_at).toLocaleDateString()}
      </p>
      <div className="bg-[var(--gty-bg-card)] border border-[var(--gty-border)] rounded-lg p-6 text-sm text-[#d4d4d8] leading-relaxed whitespace-pre-wrap">
        {activeDoc.content}
      </div>
    </div>
  );

  return (
    <div>
      {isManagement && (
        <div className="flex justify-end mb-6">
          <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-hover)] text-sm font-bold uppercase rounded-lg transition-colors">
            <Plus className="w-4 h-4" /> New Document
          </button>
        </div>
      )}

      {docs.length === 0 ? (
        <p className="text-[var(--gty-text-secondary)] text-sm">No documents yet.{isManagement ? ' Create the first one.' : ''}</p>
      ) : (
        <div className="space-y-8">
          {DOC_CATEGORIES.filter(cat => grouped[cat].length > 0).map(cat => (
            <div key={cat}>
              <h3 className="text-xs font-black uppercase tracking-widest text-[var(--gty-text-muted)] mb-3 pb-2 border-b border-[var(--gty-border)]">{cat}</h3>
              <div className="space-y-2">
                {grouped[cat].map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-4 bg-[var(--gty-bg-card)] hover:bg-[#1c1c1f] border border-[var(--gty-border)] hover:border-[var(--gty-border2)] rounded-lg transition-colors cursor-pointer group"
                    onClick={() => setActiveDoc(doc)}>
                    <div>
                      <div className="font-semibold text-sm">{doc.title}</div>
                      <div className="text-xs text-[var(--gty-text-muted)] mt-0.5">
                        {doc.author_name && `${doc.author_name} · `}
                        {new Date(doc.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isManagement && (
                        <>
                          <button onClick={e => { e.stopPropagation(); openEdit(doc); }} className="p-1.5 opacity-0 group-hover:opacity-100 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] rounded transition-all"><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={e => { e.stopPropagation(); handleDelete(doc); }} className="p-1.5 opacity-0 group-hover:opacity-100 bg-[var(--gty-bg-hover)] hover:bg-red-900 text-red-400 rounded transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                      <FileText className="w-4 h-4 text-[var(--gty-text-faint)] group-hover:text-[var(--gty-accent)] transition-colors" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Applications (management) ────────────────────────────────────────────────

const InterviewChatOverlay = ({ app, onClose }) => {
  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState('');
  const [sending, setSending]     = useState(false);
  const [loading, setLoading]     = useState(true);
  const bottomRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const r = await interviewAPI.getMessages(app.id);
      setMessages(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [app.id]);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const r = await interviewAPI.sendMessage(app.id, input.trim());
      setInput('');
      await fetchMessages();
      if (r.data?.dm_sent === false) {
        toast.warning('Saved, but Discord DM not delivered — user may not be in the server yet.', { duration: 6000 });
      }
    } catch { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  const fmt = (ts) => new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.96, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        className="w-full max-w-4xl h-[80vh] bg-[#0f0f11] border border-[#27272a] flex overflow-hidden"
      >
        {/* Left panel — applicant info */}
        <div className="w-72 shrink-0 border-r border-[#27272a] flex flex-col bg-[#0a0a0c]">
          <div className="p-5 border-b border-[#1c1c1e]">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#7c3aed] mb-3">Applicant</div>
            <div className="text-lg font-black text-white leading-tight">{app.first_name} {app.last_name}</div>
            <div className="text-xs text-[#71717a] mt-0.5">@{app.discord_username}</div>
            <div className="mt-3">
              <StatusBadge status={app.status} />
            </div>
            <div className="text-[10px] font-mono text-[#52525b] mt-2">{app.app_number}</div>
          </div>

          {/* Interview availability — prominent */}
          <div className="p-5 border-b border-[#1c1c1e]">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-3.5 h-3.5 text-[#a78bfa]" />
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#a78bfa]">Interview Availability</span>
            </div>
            <p className="text-sm text-[#d4d4d8] leading-relaxed">
              {app.interview_availability || <span className="text-[#52525b] italic">Not provided</span>}
            </p>
          </div>

          {/* Discord invite fallback */}
          <div className="p-5 border-b border-[#1c1c1e]">
            <div className="text-[9px] font-black uppercase tracking-[0.2em] text-[#52525b] mb-2">Discord Invite Link</div>
            <p className="text-[10px] text-[#52525b] mb-2 leading-relaxed">
              DMs only work if applicant is already in server. Share this manually if needed.
            </p>
            <button
              onClick={() => { navigator.clipboard.writeText('https://discord.gg/gcRvtxfANb'); toast.success('Invite link copied!'); }}
              className="w-full text-left text-[10px] font-mono text-[#a78bfa] bg-[#1c1c1e] border border-[#27272a] px-3 py-2 hover:border-[#7c3aed] transition-colors truncate"
            >
              discord.gg/gcRvtxfANb ↗
            </button>
          </div>

          <div className="p-5 flex-1 overflow-y-auto">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#52525b] mb-3">Quick Info</div>
            <div className="space-y-2">
              {[
                ['Timezone', app.timezone],
                ['CDL', app.has_cdl],
                ['Time in City', app.time_in_city],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="text-[9px] font-bold uppercase tracking-widest text-[#3f3f46]">{k}</div>
                  <div className="text-xs text-[#a1a1aa]">{v || '—'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right panel — chat */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#1c1c1e] shrink-0">
            <div>
              <div className="text-sm font-black text-white">Interview Chat</div>
              <div className="text-[10px] text-[#52525b]">Messages relay via Discord DM · auto-refreshes every 5s</div>
            </div>
            <button onClick={onClose} className="text-[#52525b] hover:text-white transition-colors p-1">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {loading && <div className="text-xs text-[#52525b] text-center pt-8">Loading messages…</div>}
            {!loading && messages.length === 0 && (
              <div className="text-center pt-12">
                <div className="text-[#3f3f46] text-xs uppercase tracking-widest font-black mb-2">No messages yet</div>
                <div className="text-[#2d2d30] text-xs">Send a message to start the interview conversation</div>
              </div>
            )}
            {messages.map(msg => (
              <div key={msg.id} className={`flex flex-col ${msg.sender_type === 'management' ? 'items-end' : 'items-start'}`}>
                <div className={`max-w-[75%] px-4 py-2.5 ${
                  msg.sender_type === 'management'
                    ? 'bg-[#7c3aed] text-white'
                    : 'bg-[#1c1c1e] border border-[#27272a] text-[#d4d4d8]'
                }`}>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                </div>
                <div className="flex items-center gap-2 mt-1 px-1">
                  <span className="text-[10px] font-bold text-[#52525b]">{msg.sender_name}</span>
                  <span className="text-[9px] text-[#3f3f46]">{fmt(msg.sent_at)}</span>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="shrink-0 border-t border-[#1c1c1e] p-4">
            <div className="flex gap-3">
              <textarea
                className="flex-1 bg-[#141417] border border-[#27272a] text-sm text-white placeholder-[#3f3f46] px-4 py-2.5 resize-none focus:outline-none focus:border-[#7c3aed] transition-colors"
                rows={2}
                placeholder="Type a message… (Shift+Enter for new line)"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              />
              <button
                onClick={send}
                disabled={sending || !input.trim()}
                className="shrink-0 px-5 bg-[#7c3aed] hover:bg-[#6d28d9] disabled:opacity-40 disabled:cursor-not-allowed text-white font-black uppercase tracking-wider text-xs transition-colors flex items-center gap-2"
              >
                <Send className="w-3.5 h-3.5" />
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
            <p className="text-[10px] text-[#3f3f46] mt-1.5">Message will be sent to applicant via Discord DM · Enter to send</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const STATUS_META = {
  pending:      { label: 'Pending',      color: 'bg-yellow-900 text-yellow-300 border-yellow-700' },
  under_review: { label: 'Under Review', color: 'bg-blue-900 text-blue-300 border-blue-700' },
  accepted:     { label: 'Accepted',     color: 'bg-green-900 text-green-300 border-green-700' },
  rejected:     { label: 'Rejected',     color: 'bg-red-900 text-red-300 border-red-700' },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, color: 'bg-zinc-800 text-zinc-300 border-zinc-700' };
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded ${m.color}`}>{m.label}</span>;
};

const ApplicationsContent = () => {
  const [apps, setApps]           = useState([]);
  const [stats, setStats]         = useState(null);
  const [selected, setSelected]   = useState(null); // full detail
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('applications'); // 'applications' | 'blocked'
  const [blocked, setBlocked]     = useState([]);
  const [blockInput, setBlockInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [changingStatus, setChangingStatus] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([applicationsAPI.list(), applicationsAPI.stats()]);
      setApps(a.data);
      setStats(s.data);
    } catch { toast.error('Failed to load applications'); }
    finally { setLoading(false); }
  }, []);

  const loadBlocked = useCallback(async () => {
    try { const r = await applicationsAPI.listBlocked(); setBlocked(r.data); }
    catch { toast.error('Failed to load blocked list'); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (tab === 'blocked') loadBlocked(); }, [tab, loadBlocked]);

  const openApp = async (app) => {
    try {
      const r = await applicationsAPI.get(app.id);
      setSelected(r.data);
      setApps(prev => prev.map(a => a.id === app.id ? { ...a, is_read: true } : a));
    } catch { toast.error('Failed to load application'); }
  };

  const changeStatus = async (status) => {
    if (!selected) return;
    setChangingStatus(true);
    try {
      await applicationsAPI.setStatus(selected.id, status);
      toast.success('Status updated');
      setSelected(prev => ({ ...prev, status }));
      loadAll();
    } catch { toast.error('Failed to update status'); }
    finally { setChangingStatus(false); }
  };

  const addNote = async () => {
    if (!noteInput.trim()) return;
    try {
      await applicationsAPI.addNote(selected.id, noteInput.trim());
      toast.success('Note added');
      setNoteInput('');
      const r = await applicationsAPI.get(selected.id);
      setSelected(r.data);
    } catch { toast.error('Failed to add note'); }
  };

  const blockUser = async () => {
    if (!blockInput.trim()) return;
    try {
      await applicationsAPI.block({ discord_username: blockInput.trim() });
      toast.success('User blocked');
      setBlockInput('');
      loadBlocked();
    } catch { toast.error('Failed to block user'); }
  };

  const unblockUser = async (id) => {
    try {
      await applicationsAPI.unblock(id);
      toast.success('Unblocked');
      loadBlocked();
    } catch { toast.error('Failed to unblock'); }
  };

  const triggerOnboarding = async () => {
    if (!selected) return;
    try {
      await applicationsAPI.triggerOnboarding(selected.discord_id);
      toast.success('Channel created & role assigned!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Onboarding failed');
    }
  };

  const inputCls = 'bg-[var(--gty-bg-card)] border border-[var(--gty-border2)] rounded px-3 py-2 text-sm text-white placeholder-[var(--gty-text-faint)] focus:outline-none focus:border-[var(--gty-accent)] transition-colors';

  if (loading) return <div className="text-[var(--gty-text-muted)] text-xs uppercase">Loading…</div>;

  // ── Detail view ──
  if (selected) return (
    <div className="relative">
      <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[var(--gty-text-muted)] hover:text-white mb-6 transition-colors">
        ← Back
      </button>

      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="text-xs font-mono text-[var(--gty-text-muted)]">{selected.app_number}</span>
            <StatusBadge status={selected.status} />
          </div>
          <h2 className="text-xl font-black">{selected.first_name} {selected.middle_name} {selected.last_name}</h2>
          <p className="text-xs text-[var(--gty-text-muted)] mt-0.5">@{selected.discord_username} · {new Date(selected.submitted_at).toLocaleString()}</p>
        </div>

        {/* Status actions */}
        <div className="flex gap-2 flex-wrap">
          {selected.status === 'pending' && (
            <button onClick={() => changeStatus('under_review')} disabled={changingStatus}
              className="px-3 py-1.5 bg-blue-800 hover:bg-blue-700 text-xs font-bold uppercase rounded transition-colors disabled:opacity-50">
              → Under Review
            </button>
          )}
          {selected.status === 'under_review' && (
            <>
              <button onClick={() => changeStatus('accepted')} disabled={changingStatus}
                className="px-3 py-1.5 bg-green-800 hover:bg-green-700 text-xs font-bold uppercase rounded transition-colors disabled:opacity-50">
                ✓ Accept
              </button>
              <button onClick={() => changeStatus('rejected')} disabled={changingStatus}
                className="px-3 py-1.5 bg-red-900 hover:bg-red-800 text-xs font-bold uppercase rounded transition-colors disabled:opacity-50">
                ✕ Reject
              </button>
            </>
          )}
          {selected.status === 'accepted' && (
            <button onClick={triggerOnboarding}
              className="px-3 py-1.5 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-hover)] text-xs font-bold uppercase rounded transition-colors">
              ⚡ Setup Channel
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {[
          ['Timezone', selected.timezone],
          ['Date of Birth', selected.dob],
          ['SSN / F6#', selected.ssn],
          ['Phone', selected.phone],
          ['Bank Account', selected.bank_account],
          ['Address', selected.address],
          ['CDL#', selected.cdl_number],
          ['Has CDL', selected.has_cdl],
          ['Time in City', selected.time_in_city],
        ].map(([k, v]) => (
          <div key={k} className="bg-[var(--gty-bg-card)] border border-[var(--gty-border)] rounded p-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-muted)] mb-1">{k}</div>
            <div className="text-sm text-white">{v || '—'}</div>
          </div>
        ))}
      </div>

      {[
        ['Experience with Trucking / Driving', selected.experience],
        ['Why Join GTY', selected.why_join],
        ['Understands Subscription', selected.understands_subscription],
        ['Willing to Comply', selected.willing_to_comply],
      ].map(([k, v]) => (
        <div key={k} className="bg-[var(--gty-bg-card)] border border-[var(--gty-border)] rounded p-4 mb-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-muted)] mb-2">{k}</div>
          <p className="text-sm text-[#d4d4d8] whitespace-pre-wrap">{v || '—'}</p>
        </div>
      ))}

      {/* Staff Notes */}
      <div className="mt-6">
        <h3 className="text-xs font-black uppercase tracking-widest text-[var(--gty-text-muted)] mb-3">Staff Notes</h3>
        <div className="space-y-2 mb-3">
          {(selected.notes || []).length === 0
            ? <p className="text-xs text-[var(--gty-text-faint)]">No notes yet.</p>
            : selected.notes.map(n => (
              <div key={n.id} className="bg-[var(--gty-bg-card)] border border-[var(--gty-border)] rounded p-3">
                <div className="flex justify-between text-[10px] text-[var(--gty-text-muted)] mb-1">
                  <span className="font-bold">{n.author_name}</span>
                  <span>{new Date(n.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-[#d4d4d8]">{n.content}</p>
              </div>
            ))}
        </div>
        <div className="flex gap-2">
          <input className={inputCls + ' flex-1'} value={noteInput} onChange={e => setNoteInput(e.target.value)}
            placeholder="Add a staff note…" onKeyDown={e => e.key === 'Enter' && addNote()} />
          <button onClick={addNote} className="px-4 py-2 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-hover)] text-xs font-bold uppercase rounded transition-colors">
            Add
          </button>
        </div>
      </div>

    </div>
  );

  // ── List view ──
  return (
    <div>
      {/* Stats bar */}
      {stats && (
        <div className="flex gap-3 flex-wrap mb-6">
          {[
            { label: 'Pending',      val: stats.pending,      color: 'text-yellow-400' },
            { label: 'Under Review', val: stats.under_review, color: 'text-blue-400' },
            { label: 'Accepted (mo)',val: stats.accepted_month,color: 'text-green-400' },
            { label: 'Rejected',     val: stats.rejected,     color: 'text-red-400' },
            { label: 'Unread',       val: stats.unread,       color: 'text-[var(--gty-accent)]' },
          ].map(s => (
            <div key={s.label} className="bg-[var(--gty-bg-card)] border border-[var(--gty-border)] rounded px-4 py-2">
              <div className={`text-xl font-black ${s.color}`}>{s.val}</div>
              <div className="text-[10px] text-[var(--gty-text-muted)] uppercase tracking-widest">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-[var(--gty-bg-card)] p-1 rounded-lg w-fit">
        {[['applications','Applications'],['blocked','Blocked Users']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-1.5 rounded-md text-xs font-bold uppercase transition-colors ${tab===k ? 'bg-[var(--gty-accent-dim)] text-white' : 'text-[var(--gty-text-muted)] hover:text-white'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Applications list */}
      {tab === 'applications' && (
        apps.length === 0
          ? <p className="text-[var(--gty-text-secondary)] text-sm">No applications yet.</p>
          : <div className="space-y-2">
              {apps.map(app => (
                <div key={app.id} onClick={() => openApp(app)}
                  className="flex items-center justify-between p-4 bg-[var(--gty-bg-card)] hover:bg-[#1c1c1f] border border-[var(--gty-border)] hover:border-[var(--gty-border2)] rounded-lg cursor-pointer transition-colors group">
                  <div className="flex items-center gap-3">
                    {!app.is_read && <span className="w-2 h-2 rounded-full bg-purple-500 shrink-0" title="Unread" />}
                    {app.is_read  && <span className="w-2 h-2 shrink-0" />}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{app.first_name} {app.last_name}</span>
                        <span className="text-[10px] font-mono text-[var(--gty-text-faint)]">{app.app_number}</span>
                      </div>
                      <div className="text-xs text-[var(--gty-text-muted)]">@{app.discord_username} · {new Date(app.submitted_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={app.status} />
                    <ChevronRight className="w-4 h-4 text-[var(--gty-text-faint)] group-hover:text-white transition-colors" />
                  </div>
                </div>
              ))}
            </div>
      )}

      {/* Blocked users */}
      {tab === 'blocked' && (
        <div>
          <div className="flex gap-2 mb-4">
            <input className={inputCls + ' flex-1'} value={blockInput} onChange={e => setBlockInput(e.target.value)}
              placeholder="Discord username to block…" onKeyDown={e => e.key === 'Enter' && blockUser()} />
            <button onClick={blockUser} className="flex items-center gap-1.5 px-4 py-2 bg-red-900 hover:bg-red-800 text-xs font-bold uppercase rounded transition-colors">
              <Ban className="w-3.5 h-3.5" /> Block
            </button>
          </div>
          {blocked.length === 0
            ? <p className="text-[var(--gty-text-secondary)] text-sm">No blocked users.</p>
            : <div className="space-y-2">
                {blocked.map(b => (
                  <div key={b.id} className="flex items-center justify-between p-3 bg-[var(--gty-bg-card)] border border-[var(--gty-border)] rounded-lg text-sm">
                    <div>
                      <span className="font-semibold">@{b.discord_username}</span>
                      <span className="text-xs text-[var(--gty-text-muted)] ml-3">Blocked by {b.blocked_by} · {new Date(b.blocked_at).toLocaleDateString()}</span>
                    </div>
                    <button onClick={() => unblockUser(b.id)} className="px-3 py-1 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] text-xs font-bold uppercase rounded transition-colors">
                      Unblock
                    </button>
                  </div>
                ))}
              </div>
          }
        </div>
      )}
    </div>
  );
};

// ─── Payments ────────────────────────────────────────────────────────────────

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const BANK_ACCOUNT = '5715451';
// screenshot_url may be a full Supabase URL or a legacy relative /uploads/... path
const resolveImgUrl = (url) => (!url ? '' : url.startsWith('http') ? url : BACKEND_URL + url);

const PAY_STATUS_COLORS = {
  pending:   'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  confirmed: 'bg-green-500/10 text-green-400 border-green-500/20',
  rejected:  'bg-red-500/10 text-red-400 border-red-500/20',
  archived:  'bg-[var(--gty-bg-hover)] text-[var(--gty-text-muted)] border-[var(--gty-border2)]',
  approved:  'bg-green-500/10 text-green-400 border-green-500/20',
};

const PayStatusBadge = ({ status }) => (
  <span className={`px-2 py-0.5 rounded border text-xs font-bold uppercase tracking-wider ${PAY_STATUS_COLORS[status] || PAY_STATUS_COLORS.pending}`}>
    {status === 'confirmed' || status === 'approved' ? '✓ Confirmed' : status === 'rejected' ? '✕ Rejected' : status === 'archived' ? 'Archived' : '⏳ Pending'}
  </span>
);

const PaymentsContent = ({ isManagement, user, paymentStatus }) => {
  // ── shared state ──
  const [tab, setTab] = useState('pending');             // management filter tab
  const [history, setHistory] = useState([]);
  const [mgmtList, setMgmtList] = useState([]);
  const [stats, setStats] = useState({ pending: 0, unread: 0 });
  const [selected, setSelected] = useState(new Set());  // bulk select IDs
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewImg, setViewImg] = useState(null);          // full-screen image URL
  const [rejectModal, setRejectModal] = useState(null);  // { id, name }
  const [rejectReason, setRejectReason] = useState('');
  const [copied, setCopied] = useState(false);
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (isManagement) {
        const [listRes, statsRes] = await Promise.all([
          paymentSubmissionsAPI.list(),
          paymentSubmissionsAPI.stats(),
        ]);
        setMgmtList(listRes.data);
        setStats(statsRes.data);
      } else {
        const res = await paymentSubmissionsAPI.myHistory();
        setHistory(res.data);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [isManagement]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyBank = () => {
    navigator.clipboard.writeText(BANK_ACCOUNT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      await paymentSubmissionsAPI.submit(fd);
      toast.success('Payment submitted! Management will review it shortly.');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed.');
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleConfirm = async (id) => {
    try {
      await paymentSubmissionsAPI.confirm(id);
      toast.success('Payment confirmed.');
      fetchData();
    } catch { toast.error('Failed to confirm.'); }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    try {
      await paymentSubmissionsAPI.reject(rejectModal.id, rejectReason);
      toast.success('Payment rejected.');
      setRejectModal(null);
      setRejectReason('');
      fetchData();
    } catch { toast.error('Failed to reject.'); }
  };

  const handleBulkConfirm = async () => {
    if (!selected.size) return;
    try {
      const res = await paymentSubmissionsAPI.bulkConfirm([...selected]);
      toast.success(`${res.data.count} payment(s) confirmed.`);
      setSelected(new Set());
      fetchData();
    } catch { toast.error('Bulk confirm failed.'); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filteredList = mgmtList.filter(p => {
    if (tab === 'all') return true;
    if (tab === 'pending') return p.status === 'pending';
    if (tab === 'confirmed') return p.status === 'confirmed' || p.status === 'approved';
    if (tab === 'rejected') return p.status === 'rejected';
    if (tab === 'archived') return p.status === 'archived';
    return true;
  });

  const pendingList = mgmtList.filter(p => p.status === 'pending');

  if (loading) return <div className="flex items-center justify-center h-64"><span className="text-[var(--gty-text-muted)] text-sm uppercase tracking-widest">Loading…</span></div>;

  // ──────────────────────────────────────────────────────────
  // MANAGEMENT VIEW
  // ──────────────────────────────────────────────────────────
  if (isManagement) return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="brutalist-card p-5 text-center">
          <div className="text-3xl font-black text-yellow-400">{stats.pending}</div>
          <div className="text-xs text-[var(--gty-text-muted)] uppercase tracking-widest mt-1">Pending</div>
        </div>
        <div className="brutalist-card p-5 text-center">
          <div className="text-3xl font-black text-[var(--gty-accent)]">{stats.unread}</div>
          <div className="text-xs text-[var(--gty-text-muted)] uppercase tracking-widest mt-1">Unread</div>
        </div>
        <div className="brutalist-card p-5 text-center">
          <div className="text-3xl font-black text-white">{mgmtList.length}</div>
          <div className="text-xs text-[var(--gty-text-muted)] uppercase tracking-widest mt-1">Total</div>
        </div>
      </div>

      {/* Bulk confirm bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-purple-900/30 border border-[var(--gty-accent)]/30 rounded-lg px-4 py-3">
          <span className="text-sm text-[var(--gty-accent)]">{selected.size} selected</span>
          <button onClick={handleBulkConfirm}
            className="ml-auto px-4 py-1.5 bg-green-700 hover:bg-green-600 text-white text-xs font-bold uppercase rounded transition-colors">
            ✓ Confirm All Selected
          </button>
          <button onClick={() => setSelected(new Set())}
            className="px-3 py-1.5 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] text-[var(--gty-text-secondary)] text-xs rounded transition-colors">
            Clear
          </button>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-1 border-b border-[var(--gty-border)]">
        {['pending', 'confirmed', 'rejected', 'archived', 'all'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 -mb-px
              ${tab === t ? 'text-white border-[var(--gty-accent)]' : 'text-[var(--gty-text-muted)] border-transparent hover:text-white'}`}>
            {t}{t === 'pending' && stats.pending > 0 ? ` (${stats.pending})` : ''}
          </button>
        ))}
      </div>

      {/* Payment list */}
      {filteredList.length === 0 ? (
        <div className="text-center py-16 text-[var(--gty-text-muted)] text-sm">No payments in this category.</div>
      ) : (
        <div className="space-y-3">
          {filteredList.map(p => (
            <div key={p.id} className={`brutalist-card p-4 flex items-center gap-4 ${!p.is_read && p.status === 'pending' ? 'border-[var(--gty-accent)]/40' : ''}`}>
              {/* Checkbox (only for pending) */}
              {p.status === 'pending' && (
                <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)}
                  className="w-4 h-4 accent-purple-500 shrink-0" />
              )}

              {/* Thumbnail */}
              <button onClick={() => setViewImg(resolveImgUrl(p.screenshot_url))}
                className="w-16 h-16 shrink-0 rounded overflow-hidden border border-[var(--gty-border2)] hover:border-[var(--gty-accent)] transition-colors">
                <img src={resolveImgUrl(p.screenshot_url)} alt="payment" className="w-full h-full object-cover" />
              </button>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {!p.is_read && p.status === 'pending' && <span className="w-2 h-2 rounded-full bg-purple-400 shrink-0" />}
                  <span className="text-sm font-bold text-white truncate">{p.driver_name}</span>
                  <PayStatusBadge status={p.status} />
                </div>
                <div className="text-xs text-[var(--gty-text-muted)] mt-1">
                  Period: {p.period_id} · Submitted: {new Date(p.submitted_at).toLocaleDateString()}
                  {p.reviewed_by && ` · Reviewed by ${p.reviewed_by}`}
                </div>
                {p.reject_reason && <div className="text-xs text-red-400 mt-0.5">Reason: {p.reject_reason}</div>}
              </div>

              {/* Actions */}
              {p.status === 'pending' && (
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => handleConfirm(p.id)}
                    className="px-3 py-1.5 bg-green-700 hover:bg-green-600 text-white text-xs font-bold rounded transition-colors">
                    ✓ Confirm
                  </button>
                  <button onClick={() => { setRejectModal({ id: p.id, name: p.driver_name }); setRejectReason(''); }}
                    className="px-3 py-1.5 bg-red-900/60 hover:bg-red-800/60 text-red-300 text-xs font-bold rounded transition-colors">
                    ✕ Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Full-screen image viewer */}
      <AnimatePresence>
        {viewImg && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setViewImg(null)}>
            <img src={viewImg} alt="Payment screenshot" className="max-w-full max-h-full rounded-lg object-contain" />
            <button onClick={() => setViewImg(null)}
              className="absolute top-4 right-4 text-white text-2xl hover:text-gray-300">✕</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reject modal */}
      <AnimatePresence>
        {rejectModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }}
              className="bg-[var(--gty-bg-card)] border border-[var(--gty-border2)] rounded-xl p-6 w-full max-w-md space-y-4">
              <h3 className="text-white font-bold">Reject Payment</h3>
              <p className="text-[var(--gty-text-secondary)] text-sm">Driver: <span className="text-white">{rejectModal.name}</span></p>
              <Textarea
                placeholder="Reason for rejection (optional — will be sent to driver via DM)"
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                className="bg-[var(--gty-bg-base)] border-[var(--gty-border2)] text-white min-h-[100px]"
              />
              <div className="flex gap-3 justify-end">
                <button onClick={() => setRejectModal(null)}
                  className="px-4 py-2 bg-[var(--gty-bg-hover)] text-[var(--gty-text-secondary)] text-sm rounded hover:bg-[var(--gty-bg-hover2)] transition-colors">
                  Cancel
                </button>
                <button onClick={handleReject}
                  className="px-4 py-2 bg-red-700 hover:bg-red-600 text-white text-sm font-bold rounded transition-colors">
                  Confirm Reject
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // ──────────────────────────────────────────────────────────
  // DRIVER VIEW
  // ──────────────────────────────────────────────────────────
  const isPaid = paymentStatus?.has_paid;

  return (
    <div className="space-y-6 max-w-2xl">
      {/* PAID / UNPAID status */}
      <div className={`brutalist-card p-5 flex items-center gap-4 ${isPaid ? 'border-green-500/40' : 'border-red-500/40'}`}>
        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl ${isPaid ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
          {isPaid ? '✓' : '!'}
        </div>
        <div>
          <div className={`text-lg font-black uppercase tracking-widest ${isPaid ? 'text-green-400' : 'text-red-400'}`}>
            {isPaid ? 'PAID' : 'UNPAID'}
          </div>
          <div className="text-xs text-[var(--gty-text-muted)]">
            {isPaid ? 'Your dues for this week are confirmed.' : 'Your dues for this week have not been confirmed yet.'}
          </div>
        </div>
      </div>

      {/* Bank account */}
      <div className="brutalist-card p-5 space-y-2">
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-muted)]">Payment Details</p>
        <div className="flex items-center gap-3">
          <span className="text-sm text-[var(--gty-text-secondary)]">Bank Account:</span>
          <span className="text-white font-mono font-bold text-lg">{BANK_ACCOUNT}</span>
          <button onClick={copyBank}
            className="ml-auto px-3 py-1 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] text-xs text-[var(--gty-text-secondary)] rounded transition-colors">
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
        <p className="text-xs text-[var(--gty-text-muted)]">Use the <span className="font-mono text-white">/pay</span> command or submit your screenshot below.</p>
      </div>

      {/* Upload */}
      <div className="brutalist-card p-5 space-y-4">
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-muted)]">Submit Payment Screenshot</p>
        <p className="text-sm text-[var(--gty-text-secondary)]">Upload a clear screenshot showing the payment confirmation. Management will review and confirm it.</p>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="w-full py-3 border-2 border-dashed border-[var(--gty-border2)] hover:border-[var(--gty-accent)] rounded-lg text-sm text-[var(--gty-text-muted)] hover:text-white transition-colors disabled:opacity-50">
          {uploading ? '⏳ Uploading…' : '📎 Click to upload screenshot'}
        </button>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="brutalist-card p-5 space-y-3">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-muted)]">Payment History</p>
          <div className="space-y-3">
            {history.map(p => (
              <div key={p.id} className="flex items-center gap-3 py-2 border-b border-[var(--gty-border)] last:border-0">
                <button onClick={() => setViewImg(resolveImgUrl(p.screenshot_url))}
                  className="w-12 h-12 shrink-0 rounded overflow-hidden border border-[var(--gty-border2)] hover:border-[var(--gty-accent)] transition-colors">
                  <img src={resolveImgUrl(p.screenshot_url)} alt="payment" className="w-full h-full object-cover" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[var(--gty-text-muted)]">Period: {p.period_id}</div>
                  <div className="text-xs text-[var(--gty-text-faint)]">{new Date(p.submitted_at).toLocaleDateString()}</div>
                  {p.reject_reason && <div className="text-xs text-red-400 mt-0.5">{p.reject_reason}</div>}
                </div>
                <PayStatusBadge status={p.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full-screen image viewer */}
      <AnimatePresence>
        {viewImg && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setViewImg(null)}>
            <img src={viewImg} alt="Payment screenshot" className="max-w-full max-h-full rounded-lg object-contain" />
            <button className="absolute top-4 right-4 text-white text-2xl">✕</button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Broadcast ───────────────────────────────────────────────────────────────

const BROADCAST_TEMPLATES = [
  { label: 'Payment Reminder', message: `**Payment Reminder**\n\nWe haven't received your payment for last week's pay period.\n\nPlease submit it tonight to avoid contract termination. If this is a mistake, let us know.\n\n— GTY Management` },
  { label: 'Server Update',    message: `**Server Update**\n\nHey team — the server will be coming down briefly tonight for maintenance. We'll post when it's back up.\n\n— GTY Management` },
  { label: 'Weekly Check-in',  message: `**Weekly Check-in**\n\nHope everyone's had a solid week on the road. Remember to log your hours and check your truck before the weekend.\n\n— GTY Management` },
];

const BroadcastContent = () => {
  const [drivers, setDrivers]   = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [message, setMessage]   = useState('');
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(false);
  const [sendDm, setSendDm]     = useState(true);
  const [report, setReport]     = useState(null);
  const [search, setSearch]     = useState('');

  useEffect(() => {
    broadcastAPI.getDrivers()
      .then(r => {
        const normalized = (r.data || [])
          .map(m => ({
            ...m,
            memberId: m.driver_id ?? m.id,
            displayName: m.display_name ?? m.username ?? 'Unknown',
            username: m.username ?? '',
            hasEmployeeFile: Boolean(m.has_employee_file),
            hasContractorChannel: Boolean(m.has_contractor_channel),
            contractorChannelName: m.contractor_channel_name ?? '',
            employeeFileDriverId: m.employee_file_driver_id ?? '',
            employeeFileName: m.employee_file_name ?? '',
            employeeFileStatus: m.employee_file_status ?? '',
            employeeFileMatch: m.employee_file_match ?? '',
          }))
          .filter(m => m.memberId != null);
        setDrivers(normalized);
      })
      .catch(() => toast.error('Failed to load drivers.'))
      .finally(() => setLoading(false));
  }, []);

  const searchTerm = search.toLowerCase();
  const filtered = drivers.filter(d =>
    d.displayName.toLowerCase().includes(searchTerm) ||
    d.username.toLowerCase().includes(searchTerm) ||
    d.employeeFileName.toLowerCase().includes(searchTerm) ||
    d.contractorChannelName.toLowerCase().includes(searchTerm)
  );
  const allSelected = filtered.length > 0 && filtered.every(d => selected.has(d.memberId));

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () => {
    if (allSelected) {
      setSelected(prev => { const n = new Set(prev); filtered.forEach(d => n.delete(d.memberId)); return n; });
    } else {
      setSelected(prev => { const n = new Set(prev); filtered.forEach(d => n.add(d.memberId)); return n; });
    }
  };

  const handleSend = async () => {
    if (!message.trim() || !selected.size || sending) return;
    setSending(true);
    try {
      const nameMap = Object.fromEntries(drivers.map(d => [d.memberId, d.displayName]));
      const fileMap = Object.fromEntries(drivers.map(d => [d.memberId, d.employeeFileName]));
      const employeeFileDriverIds = Object.fromEntries(
        drivers
          .filter(d => selected.has(d.memberId) && d.employeeFileDriverId)
          .map(d => [d.memberId, d.employeeFileDriverId])
      );
      const res = await broadcastAPI.sendToDrivers({
        driver_ids: [...selected],
        employee_file_driver_ids: employeeFileDriverIds,
        message: message.trim(),
        send_dm: sendDm,
      });
      setReport({ ...res.data, nameMap, fileMap });
      setMessage('');
      setSelected(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Broadcast failed.');
    }
    setSending(false);
  };

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-1 h-8 bg-purple-600" />
        <div>
          <h2 style={{ fontFamily: "'Abolition', sans-serif", letterSpacing: '0.14em' }}
            className="text-white text-2xl uppercase">Broadcast Terminal</h2>
          <p className="text-[var(--gty-text-faint)] text-[10px] font-mono uppercase tracking-widest">
            Send to employee files + optional Discord DM
          </p>
        </div>
      </div>

      {/* Transmission report */}
      <AnimatePresence>
        {report && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="mb-5 border border-[var(--gty-border)] bg-[var(--gty-bg-card)]">
            <div className="flex items-center gap-4 px-4 py-3 border-b border-[var(--gty-border)]">
              <Radio size={14} className="text-purple-400" />
              <span className="text-[11px] font-mono uppercase tracking-widest text-[var(--gty-text-muted)]">Transmission Report</span>
              <span className="text-emerald-400 font-mono text-xs ml-auto">✓ {report.sent} delivered</span>
              {report.failed > 0 && <span className="text-red-400 font-mono text-xs">✕ {report.failed} failed</span>}
              <button onClick={() => setReport(null)} className="text-[var(--gty-text-faint)] hover:text-white ml-2"><X size={12} /></button>
            </div>
            <div className="px-4 py-2 max-h-36 overflow-y-auto font-mono text-xs space-y-0.5">
              {report.results.map(r => (
                <div key={r.driver_id} className="flex items-center gap-2">
                  <span className={r.ok ? 'text-emerald-400' : 'text-red-400'}>{r.ok ? '▶' : '✕'}</span>
                  <span className="text-[var(--gty-text-secondary)]">{report.nameMap[r.driver_id] || r.driver_id}</span>
                  {r.employee_file_saved && (
                    <span className="text-emerald-400/70 text-[10px]">
                      (saved to {r.employee_file_name || report.fileMap?.[r.driver_id] || 'employee file'})
                    </span>
                  )}
                  {r.room_sent && (
                    <span className="text-emerald-400/70 text-[10px]">
                      (sent to #{r.contractor_channel_name || 'contractor room'})
                    </span>
                  )}
                  {r.employee_file_driver_id && r.employee_file_driver_id !== r.driver_id && (
                    <span className="text-[var(--gty-text-faint)] text-[10px]">file id: {r.employee_file_driver_id}</span>
                  )}
                  {r.reason === 'no_employee_file' && <span className="text-red-400/80 text-[10px]">(no employee file found)</span>}
                  {r.reason === 'no_contractor_channel' && <span className="text-red-400/80 text-[10px]">(no contractor room found)</span>}
                  {r.reason === 'room_send_failed' && <span className="text-red-400/80 text-[10px]">(room send failed)</span>}
                  {r.ok && !r.dm_sent && <span className="text-amber-400/70 text-[10px]">(file only — DM failed)</span>}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        {/* ── Left: Compose ── */}
        <div className="space-y-4">
          {/* Templates */}
          <div className="border border-[var(--gty-border)] bg-[var(--gty-bg-card)] p-4">
            <p className="text-[9px] font-mono uppercase tracking-widest text-[var(--gty-text-faint)] mb-3">Quick Templates</p>
            <div className="flex flex-wrap gap-2">
              {BROADCAST_TEMPLATES.map(t => (
                <button key={t.label} onClick={() => setMessage(t.message)}
                  className="px-3 py-1.5 border border-[var(--gty-border)] text-[10px] font-mono text-[var(--gty-text-muted)] hover:text-white hover:border-purple-600/50 transition-colors uppercase tracking-widest">
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Message composer */}
          <div className="border border-[var(--gty-border)] bg-[var(--gty-bg-card)]">
            <div className="px-4 py-2.5 border-b border-[var(--gty-border)] flex items-center justify-between">
              <p className="text-[9px] font-mono uppercase tracking-widest text-[var(--gty-text-faint)]">Message</p>
              <span className={`text-[9px] font-mono ${message.length > 1800 ? 'text-red-400' : 'text-[var(--gty-text-faint)]'}`}>
                {message.length}/2000
              </span>
            </div>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Write your broadcast…&#10;Supports **bold** *italic* `code`"
              rows={8}
              style={{ resize: 'none' }}
              className="w-full bg-transparent text-white text-sm px-4 py-3 font-mono focus:outline-none placeholder:text-[var(--gty-text-faint)]"
            />
          </div>

          {/* Options */}
          <label className="flex items-center gap-3 cursor-pointer group">
            <div onClick={() => setSendDm(v => !v)}
              className={`w-9 h-5 rounded-full border transition-colors flex items-center relative ${sendDm ? 'bg-purple-600 border-purple-600' : 'bg-transparent border-[var(--gty-border)]'}`}>
              <div className={`w-3.5 h-3.5 rounded-full bg-white absolute transition-all ${sendDm ? 'left-[18px]' : 'left-[3px]'}`} />
            </div>
            <span className="text-xs font-mono text-[var(--gty-text-muted)] group-hover:text-white transition-colors">
              Also send Discord DM to each driver
            </span>
          </label>

          {/* Transmit button */}
          <button onClick={handleSend}
            disabled={sending || !message.trim() || !selected.size || message.length > 2000}
            className="w-full py-3.5 bg-purple-600 hover:bg-purple-700 disabled:bg-[var(--gty-bg-hover)] disabled:text-[var(--gty-text-faint)] text-white font-black uppercase tracking-widest transition-colors text-sm flex items-center justify-center gap-3 disabled:cursor-not-allowed">
            <Radio size={16} />
            {sending ? 'Transmitting…' : selected.size > 0 ? `Transmit to ${selected.size} driver${selected.size !== 1 ? 's' : ''}` : 'Select Recipients'}
          </button>
        </div>

        {/* ── Right: Recipients ── */}
        <div className="brutalist-card p-5 space-y-3 flex flex-col" style={{ maxHeight: 520 }}>
          <div className="flex items-center justify-between flex-shrink-0">
            <p className="text-[9px] font-mono uppercase tracking-widest text-[var(--gty-text-faint)]">
              Recipients <span className="text-purple-400">{selected.size}/{drivers.length}</span>
            </p>
            <button onClick={toggleAll} className="text-[9px] font-mono text-[var(--gty-accent)] hover:text-white transition-colors uppercase tracking-widest">
              {allSelected ? 'None' : 'All'}
            </button>
          </div>
          <div className="flex-shrink-0">
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search name or username..."
              className="w-full px-3 py-1.5 bg-[var(--gty-bg-hover)] border border-[var(--gty-border)] rounded text-sm text-white placeholder-[var(--gty-text-faint)] focus:outline-none focus:border-[var(--gty-accent)]" />
          </div>
          <div className="flex-1 overflow-y-auto space-y-0.5 pr-1">
            {loading && <p className="text-[var(--gty-text-faint)] text-xs font-mono p-4">Loading…</p>}
            {!loading && filtered.length === 0 && (
              <p className="text-[var(--gty-text-faint)] text-xs font-mono p-4 text-center">No active drivers found.</p>
            )}
            {filtered.map(d => (
              <label key={d.memberId}
                className="flex items-center gap-3 px-3 py-2 rounded hover:bg-[var(--gty-bg-hover)] cursor-pointer transition-colors">
                <input type="checkbox" checked={selected.has(d.memberId)} onChange={() => toggle(d.memberId)}
                  className="w-4 h-4 accent-[var(--gty-accent)] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="text-sm text-white truncate">{d.displayName}</div>
                    <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded border shrink-0 ${
                      d.hasEmployeeFile
                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                        : d.hasContractorChannel
                          ? 'border-amber-500/30 bg-amber-500/10 text-amber-300'
                        : 'border-red-500/30 bg-red-500/10 text-red-300'
                    }`}>
                      {d.hasEmployeeFile ? 'File' : d.hasContractorChannel ? 'Channel' : 'No File'}
                    </span>
                  </div>
                  <div className="text-[10px] text-[var(--gty-text-faint)] truncate">
                    {d.hasEmployeeFile ? d.employeeFileName : d.hasContractorChannel ? d.contractorChannelName : 'No employee file found'}
                    {d.employeeFileStatus && <span className="text-[var(--gty-text-muted)]"> · {d.employeeFileStatus}</span>}
                    {d.employeeFileMatch === 'linked' && <span className="text-emerald-300/80"> · linked</span>}
                    {d.employeeFileMatch === 'name' && <span className="text-amber-300/80"> · name match</span>}
                    {d.employeeFileMatch === 'channel' && <span className="text-amber-300/80"> · channel match</span>}
                    {d.employeeFileMatch === 'direct' && <span className="text-emerald-300/70"> · direct match</span>}
                    {!d.hasEmployeeFile && d.hasContractorChannel && <span className="text-amber-300/80"> · no DB file linked</span>}
                  </div>
                  {d.username && <div className="text-[10px] text-[var(--gty-text-faint)] truncate">@{d.username}</div>}
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Mechanic ─────────────────────────────────────────────────────────────────

const INCIDENT_STATUS_COLORS = {
  pending:     'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  in_progress: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  resolved:    'bg-green-500/10 text-green-400 border-green-500/20',
};

const MechanicContent = () => {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('active'); // active | resolved | all

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await mechanicAPI.getIncidents();
      setIncidents(r.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStart = async (id) => {
    try { await mechanicAPI.startRepair(id); toast.success('Repair started — truck set to Under Maintenance.'); load(); }
    catch { toast.error('Failed to start repair.'); }
  };

  const handleFinish = async (id) => {
    try { await mechanicAPI.finishRepair(id); toast.success('Repair finished — truck is available again.'); load(); }
    catch { toast.error('Failed to finish repair.'); }
  };

  const filtered = incidents.filter(i => {
    if (filter === 'active')   return i.status !== 'resolved';
    if (filter === 'resolved') return i.status === 'resolved';
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><span className="text-[var(--gty-text-muted)] text-sm uppercase tracking-widest">Loading…</span></div>;

  return (
    <div className="space-y-6">
      {/* Filter tabs */}
      <div className="flex gap-1 border-b border-[var(--gty-border)]">
        {[['active','Active'],['resolved','Resolved'],['all','All']].map(([k,l]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors
              ${filter===k ? 'text-white border-[var(--gty-accent)]' : 'text-[var(--gty-text-muted)] border-transparent hover:text-white'}`}>
            {l}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-[var(--gty-text-muted)] text-sm">No incidents in this category.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(inc => (
            <div key={inc.id} className="brutalist-card p-5 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-bold text-sm">{inc.truck_name} {inc.truck_number}</span>
                    <span className={`px-2 py-0.5 rounded border text-xs font-bold uppercase tracking-wider ${INCIDENT_STATUS_COLORS[inc.status] || INCIDENT_STATUS_COLORS.pending}`}>
                      {inc.status.replace('_',' ')}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--gty-text-muted)]">
                    Reported by <span className="text-[var(--gty-text-secondary)]">{inc.driver_name}</span> · {new Date(inc.reported_at).toLocaleDateString()}
                  </p>
                  <p className="text-xs font-bold text-[var(--gty-text-secondary)] uppercase tracking-wider">{inc.issue_type}</p>
                  {inc.description && <p className="text-sm text-[var(--gty-text-secondary)] leading-relaxed">{inc.description}</p>}
                  {inc.mechanic_name && <p className="text-xs text-[var(--gty-text-faint)]">Mechanic: {inc.mechanic_name}</p>}
                  {inc.repair_started_at && <p className="text-xs text-[var(--gty-text-faint)]">Started: {new Date(inc.repair_started_at).toLocaleString()}</p>}
                  {inc.repair_finished_at && <p className="text-xs text-green-400">Finished: {new Date(inc.repair_finished_at).toLocaleString()}</p>}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {inc.status === 'pending' && (
                    <button onClick={() => handleStart(inc.id)}
                      className="px-4 py-2 bg-blue-700 hover:bg-blue-600 text-white text-xs font-bold uppercase rounded transition-colors whitespace-nowrap">
                      🔧 Start Repair
                    </button>
                  )}
                  {inc.status === 'in_progress' && (
                    <button onClick={() => handleFinish(inc.id)}
                      className="px-4 py-2 bg-green-700 hover:bg-green-600 text-white text-xs font-bold uppercase rounded transition-colors whitespace-nowrap">
                      ✓ Finish Repair
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Create Channel ──────────────────────────────────────────────────────────

const CHANNEL_PREFIXES_LIST = ['contractor','mechanic','vip','loa-dispatcher','so'];
const PAYMENT_REMINDER_TEMPLATE = `**Hello!**\n\nLooks like we haven't received payment from you for last weeks pay period.\n\nPlease get this done by tonight to avoid termination of contract.\n\n*If this is a mistake please let us know and we will look over it.*\n\nThanks!\n\n-# Go Truck Yourself Management`;
const WELCOME_CONTRACTOR_SAMPLE_DATA = `GTY Employee File

Employee Information (IC):
First Name: Oskar
Middle Name: N/A
Last Name: Morgan
Date of Birth: 08/10/87
Contractor SSN#(player ID/F6#): 202508
Contractor Phone Number: 870-0235
Contractor Main Bank Account: #234025
Contractor Address: 100 Calais Ave (State Housing)
Contractor CDL#: 37054109
Hire Date: 6/3/26
Hiring Manager: Richard Davis

Employee Information (OOC):
Discord Username: RoffleWaffles
Time-Zone: EST
=============================`;

const WELCOME_CONTRACTOR_TEMPLATE = {
  flags: 32768,
  components: [
    {
      type: 17,
      accent_color: 9568511,
      components: [
        { type: 10, content: '# **🚛GTY Employee File**' },
        { type: 14, spacing: 1, divider: true },
        {
          type: 10,
          content: `**👤 Employee Information (IC)**\n\n> **First Name:** {{first_name}}\n> **Middle Name:** {{middle_name}}\n> **Last Name:** {{last_name}}\n> **Date of Birth:** {{date_of_birth}}\n> **Contractor Social Security Number:** {{ssn}}\n> **Contractor Phone Number:** {{phone}}\n> **Contractor Main Bank Account:** {{bank_account}}\n> **Contractor Address:** {{address}}\n> **Contractor CDL#:** {{cdl}}\n> **Hire Date:** {{hire_date}}\n> **Hiring Manager:** {{hiring_manager}}\n\n\n\n**🌐 Employee Information (OOC)**\n> **Discord Username:** {{discord_username}}\n> **Time-Zone:** {{time_zone}}\n\n`,
        },
        { type: 14, spacing: 1, divider: true },
        {
          type: 10,
          content: '*🤝 This channel is used to maintain a direct line of contact between you and the management team. Feel free to contact us about any questions, concerns, or any issues you may come across!*\n\n### **Thank you for your interest in GTY and we hope you enjoy your stay!**',
        },
        { type: 14, spacing: 2, divider: true },
        {
          type: 10,
          content: "# **⚠️Important Info**\n\n## **💵 Weekly Dues**\nPayment is due every week with a payment window of **Friday – Sunday.**\n\n* **Contractors: $20,000**\n* **Operators: $15,000**\n\n\n💡*You can choose to pay your hard-earned cash or work your dues off instead. If you'd like to work your dues off, please contact a member of management who will guide you through the process.*\n\n\n\n**🛠️ For Cash Payments**\n1. In this channel, run the command: `/pay screenshot:<your payment screenshot>`\n2. Attach the payment screenshot directly to the command.\n3. Management will review it and you will be notified here.",
        },
        { type: 14, spacing: 2, divider: true },
        {
          type: 10,
          content: '# **🔑 Truck Usage**\nPlease use the company **website** to check fleet vehicles in and out. Please also note any **damage** or **missing items**. Each truck should come stocked with **1 repair kit and 1 tire repair kit** in the trunk.',
        },
        { type: 14, spacing: 2, divider: true },
        { type: 10, content: '# **🌐 Official Platform**' },
        {
          type: 1,
          components: [
            {
              type: 2,
              style: 5,
              label: 'Access the GTY Portal Here',
              url: 'https://gty-website.pages.dev',
            },
          ],
        },
      ],
    },
  ],
};

const CHANNEL_EMBED_TEMPLATES = [
  {
    id: 'welcome-contractor',
    name: 'Welcome Contractor',
    description: 'Components V2 employee file welcome message',
    payload: WELCOME_CONTRACTOR_TEMPLATE,
    sampleData: WELCOME_CONTRACTOR_SAMPLE_DATA,
  },
];

const EMPLOYEE_DATA_FIELDS = [
  ['first_name', ['first name']],
  ['middle_name', ['middle name']],
  ['last_name', ['last name']],
  ['date_of_birth', ['date of birth']],
  ['ssn', ['contractor ssn', 'social security', 'player id', 'f6']],
  ['phone', ['contractor phone number', 'phone number']],
  ['bank_account', ['contractor main bank account', 'main bank account', 'bank account']],
  ['address', ['contractor address', 'address']],
  ['cdl', ['contractor cdl', 'cdl#', 'cdl']],
  ['hire_date', ['hire date']],
  ['hiring_manager', ['hiring manager']],
  ['discord_username', ['discord username']],
  ['time_zone', ['time-zone', 'time zone', 'timezone']],
];

const normalizeEmployeeKey = (value = '') => value
  .toLowerCase()
  .replace(/[#()/:]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const parseEmployeeTemplateData = (raw = '') => {
  const keyed = {};
  raw.replace(/\r/g, '').split('\n').forEach(line => {
    const match = line.match(/^\s*([^:]+):\s*(.*?)\s*$/);
    if (!match) return;
    keyed[normalizeEmployeeKey(match[1])] = match[2];
  });

  return Object.fromEntries(EMPLOYEE_DATA_FIELDS.map(([field, aliases]) => {
    const value = Object.entries(keyed).find(([key]) =>
      aliases.some(alias => key.includes(normalizeEmployeeKey(alias)))
    )?.[1] || '';
    return [field, value || ''];
  }));
};

const fillTemplatePlaceholders = (value, data) => {
  if (typeof value === 'string') {
    return value.replace(/\{\{(\w+)\}\}/g, (_, key) => data[key] || '');
  }
  if (Array.isArray(value)) return value.map(item => fillTemplatePlaceholders(item, data));
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, fillTemplatePlaceholders(item, data)]));
  }
  return value;
};

// ─── Team Editor ─────────────────────────────────────────────────────────────

const TIER_LABELS = {
  founder:    'Founder',
  ownership:  'Ownership',
  management: 'Management Team',
  operations: 'Operations Management',
  senior:     'Senior Operators',
};

const TeamEditorContent = () => {
  const [teamData, setTeamData]     = useState({});
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState({});       // { [id]: true }
  const [deleting, setDeleting]     = useState({});       // { [id]: true }
  const [adding, setAdding]         = useState({});       // { [tierId]: true }
  const [confirmDel, setConfirmDel] = useState(null);     // member id pending delete confirm
  const [uploading, setUploading]   = useState({});       // { [id]: true }
  const [edits, setEdits]           = useState({});       // { [id]: { name, slogan } }
  const [photos, setPhotos]         = useState({});       // { [id]: url } — live preview

  const loadTeam = () => {
    setLoading(true);
    teamAPI.getTeam()
      .then(r => {
        setTeamData(r.data);
        const initEdits = {}, initPhotos = {};
        Object.values(r.data).flat().forEach(m => {
          initEdits[m.id]  = { name: m.name, slogan: m.slogan };
          initPhotos[m.id] = m.photo_url || null;
        });
        setEdits(initEdits);
        setPhotos(initPhotos);
      })
      .catch(() => toast.error('Failed to load team data.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadTeam(); }, []);

  const handlePhotoUpload = async (id, file) => {
    if (!file) return;
    setUploading(u => ({ ...u, [id]: true }));
    try {
      const r = await teamAPI.uploadPhoto(id, file);
      setPhotos(p => ({ ...p, [id]: r.data.photo_url }));
      toast.success('Photo uploaded.');
    } catch {
      toast.error('Upload failed.');
    } finally {
      setUploading(u => ({ ...u, [id]: false }));
    }
  };

  const handleSave = async (id) => {
    setSaving(s => ({ ...s, [id]: true }));
    try {
      await teamAPI.updateMember(id, edits[id]);
      toast.success('Saved.');
    } catch {
      toast.error('Failed to save.');
    } finally {
      setSaving(s => ({ ...s, [id]: false }));
    }
  };

  const handleAdd = async (tierId) => {
    setAdding(a => ({ ...a, [tierId]: true }));
    try {
      const r = await teamAPI.addMember({ tier: tierId });
      const newMember = r.data;
      setTeamData(td => ({ ...td, [tierId]: [...(td[tierId] || []), newMember] }));
      setEdits(e => ({ ...e, [newMember.id]: { name: newMember.name, slogan: newMember.slogan } }));
      setPhotos(p => ({ ...p, [newMember.id]: null }));
      toast.success('Member added.');
    } catch {
      toast.error('Failed to add member.');
    } finally {
      setAdding(a => ({ ...a, [tierId]: false }));
    }
  };

  const handleDelete = async (id) => {
    setDeleting(d => ({ ...d, [id]: true }));
    setConfirmDel(null);
    try {
      await teamAPI.deleteMember(id);
      setTeamData(td => {
        const next = {};
        for (const [tier, members] of Object.entries(td)) {
          next[tier] = members.filter(m => m.id !== id);
        }
        return next;
      });
      setEdits(e => { const n = { ...e }; delete n[id]; return n; });
      setPhotos(p => { const n = { ...p }; delete n[id]; return n; });
      toast.success('Member removed.');
    } catch {
      toast.error('Failed to delete member.');
    } finally {
      setDeleting(d => ({ ...d, [id]: false }));
    }
  };

  const setField = (id, field, val) =>
    setEdits(e => ({ ...e, [id]: { ...e[id], [field]: val } }));

  if (loading) return (
    <div className="p-6 text-[var(--gty-text-muted)] text-sm font-mono">Loading team data...</div>
  );

  return (
    <div className="p-6 space-y-10 max-w-3xl">
      <div>
        <h2 className="text-lg font-black uppercase tracking-widest text-white mb-1">Team Page Editor</h2>
        <p className="text-[var(--gty-text-muted)] text-xs font-mono">
          Edit names, slogans and photos shown on the public team page. Changes are live immediately.
        </p>
      </div>

      {Object.entries(TIER_LABELS).map(([tierId, tierLabel]) => {
        const members = teamData[tierId] || [];
        return (
          <div key={tierId}>
            {/* Tier header */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[var(--gty-accent)] font-mono">
                {tierLabel} <span className="text-[var(--gty-text-faint)] font-normal ml-1">({members.length})</span>
              </h3>
              <button
                onClick={() => handleAdd(tierId)}
                disabled={adding[tierId]}
                className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 border border-[var(--gty-border)] text-[var(--gty-accent)] hover:bg-[var(--gty-accent)] hover:text-black transition-colors disabled:opacity-40"
              >
                <Plus size={11} />
                {adding[tierId] ? 'Adding...' : 'Add Member'}
              </button>
            </div>

            <div className="space-y-3">
              {members.length === 0 && (
                <p className="text-[var(--gty-text-faint)] text-[11px] font-mono italic py-2">
                  No members yet — click Add Member to start.
                </p>
              )}
              {members.map(m => {
                const e           = edits[m.id] || { name: m.name, slogan: m.slogan };
                const isSaving    = saving[m.id];
                const isDeleting  = deleting[m.id];
                const isUploading = uploading[m.id];
                const photoUrl    = photos[m.id];
                const isPendingDel = confirmDel === m.id;
                return (
                  <div key={m.id}
                    className={`bg-[var(--gty-bg-card)] border p-4 flex flex-col sm:flex-row gap-4 items-start transition-colors ${isPendingDel ? 'border-red-600/60' : 'border-[var(--gty-border)]'}`}>

                    {/* Photo */}
                    <div className="flex-shrink-0 flex flex-col items-center gap-2">
                      <div className="w-14 h-[4.5rem] bg-[var(--gty-bg-hover)] border border-[var(--gty-border)] overflow-hidden flex items-end justify-center relative">
                        {photoUrl ? (
                          <img src={photoUrl} alt="" className="absolute inset-0 w-full h-full object-cover object-top" />
                        ) : (
                          <svg viewBox="0 0 100 115" fill="none" className="w-3/4 opacity-20">
                            <circle cx="50" cy="33" r="22" fill="white" />
                            <path d="M0 115 C0 78 18 60 50 60 C82 60 100 78 100 115Z" fill="white" />
                          </svg>
                        )}
                        {isUploading && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <span className="text-white text-[10px] font-mono">...</span>
                          </div>
                        )}
                      </div>
                      <label className="cursor-pointer text-[9px] font-mono text-[var(--gty-accent)] hover:text-white transition-colors uppercase tracking-widest">
                        {isUploading ? 'Uploading' : 'Upload'}
                        <input type="file" accept="image/*" className="hidden"
                          onChange={ev => handlePhotoUpload(m.id, ev.target.files?.[0])} />
                      </label>
                    </div>

                    {/* Fields */}
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                      <div>
                        <label className="text-[var(--gty-text-faint)] text-[10px] uppercase tracking-widest font-mono block mb-1">Name</label>
                        <input
                          className="w-full bg-[var(--gty-bg-hover)] border border-[var(--gty-border)] text-white text-xs px-3 py-2 font-mono focus:outline-none focus:border-[var(--gty-accent)]"
                          value={e.name}
                          onChange={ev => setField(m.id, 'name', ev.target.value)}
                          placeholder="First Name Last Name"
                        />
                      </div>
                      <div>
                        <label className="text-[var(--gty-text-faint)] text-[10px] uppercase tracking-widest font-mono block mb-1">Slogan</label>
                        <input
                          className="w-full bg-[var(--gty-bg-hover)] border border-[var(--gty-border)] text-white text-xs px-3 py-2 font-mono focus:outline-none focus:border-[var(--gty-accent)] italic"
                          value={e.slogan}
                          onChange={ev => setField(m.id, 'slogan', ev.target.value)}
                          placeholder="Their personal motto..."
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 flex-shrink-0 self-end">
                      <button onClick={() => handleSave(m.id)} disabled={isSaving || isDeleting}
                        className="btn-primary text-[11px] px-4 py-2 disabled:opacity-50">
                        {isSaving ? '...' : 'Save'}
                      </button>

                      {isPendingDel ? (
                        <div className="flex gap-1">
                          <button onClick={() => handleDelete(m.id)} disabled={isDeleting}
                            className="flex-1 text-[10px] font-mono px-2 py-1.5 bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50">
                            {isDeleting ? '...' : 'Confirm'}
                          </button>
                          <button onClick={() => setConfirmDel(null)}
                            className="flex-1 text-[10px] font-mono px-2 py-1.5 border border-[var(--gty-border)] text-[var(--gty-text-muted)] hover:text-white transition-colors">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => setConfirmDel(m.id)} disabled={isDeleting}
                          className="flex items-center justify-center gap-1 text-[10px] font-mono px-4 py-1.5 border border-red-800/50 text-red-500 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors disabled:opacity-40">
                          <Trash2 size={11} />
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CreateChannelContent = () => {
  const [members, setMembers]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [sortBy, setSortBy]           = useState('name'); // name | role
  const [selected, setSelected]       = useState(new Set());
  const [prefix, setPrefix]           = useState('contractor');
  const [chanName, setChanName]       = useState('');
  const [message, setMessage]         = useState('');
  const [messageType, setMessageType] = useState('text');
  const [embedJson, setEmbedJson]     = useState('');
  const [embedError, setEmbedError]   = useState('');
  const [templateOpen, setTemplateOpen] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState(CHANNEL_EMBED_TEMPLATES[0].id);
  const [templateDataRaw, setTemplateDataRaw] = useState(CHANNEL_EMBED_TEMPLATES[0].sampleData);
  const [templateJson, setTemplateJson] = useState('');
  const [templateError, setTemplateError] = useState('');
  const [creating, setCreating]       = useState(false);
  const [result, setResult]           = useState(null);

  useEffect(() => {
    channelSetupAPI.getMembers()
      .then(r => setMembers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleMember = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const filtered = members
    .filter(m => m.display_name.toLowerCase().includes(search.toLowerCase()) || m.username.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'role') return (a.roles[0] || 'zzz').localeCompare(b.roles[0] || 'zzz');
      return a.display_name.localeCompare(b.display_name);
    });

  // Permission preview: selected members + management
  const selectedMembers = members.filter(m => selected.has(m.id));
  const activeTemplate = CHANNEL_EMBED_TEMPLATES.find(t => t.id === activeTemplateId) || CHANNEL_EMBED_TEMPLATES[0];

  const buildTemplateJson = useCallback((template = activeTemplate, raw = templateDataRaw) => {
    const data = parseEmployeeTemplateData(raw);
    const payload = fillTemplatePlaceholders(template.payload, data);
    const nextJson = JSON.stringify(payload, null, 2);
    setTemplateJson(nextJson);
    setTemplateError('');
    return nextJson;
  }, [activeTemplate, templateDataRaw]);

  const openTemplateModal = () => {
    setTemplateOpen(true);
    setTemplateError('');
    buildTemplateJson(activeTemplate, templateDataRaw);
  };

  const selectTemplate = (template) => {
    setActiveTemplateId(template.id);
    const nextRaw = template.sampleData || '';
    setTemplateDataRaw(nextRaw);
    const data = parseEmployeeTemplateData(nextRaw);
    const payload = fillTemplatePlaceholders(template.payload, data);
    setTemplateJson(JSON.stringify(payload, null, 2));
    setTemplateError('');
  };

  const handleApplyTemplate = () => {
    const nextJson = templateJson.trim() ? templateJson : buildTemplateJson();
    try {
      const parsed = JSON.parse(nextJson);
      if (parsed.flags !== 32768 || !Array.isArray(parsed.components)) {
        setTemplateError('Template must be Discord Components V2 JSON with flags 32768 and components.');
        return;
      }
    } catch {
      setTemplateError('Template JSON is invalid. Edit it before applying.');
      return;
    }
    setMessageType('embed');
    setEmbedJson(nextJson);
    setEmbedError('');
    setTemplateOpen(false);
    toast.success(`${activeTemplate.name} loaded.`);
  };

  const handleAddTemplateComponent = () => {
    const nextTemplateJson = buildTemplateJson(activeTemplate, templateDataRaw);
    try {
      const current = templateJson.trim()
        ? JSON.parse(templateJson)
        : { flags: 32768, components: [] };
      const addition = JSON.parse(nextTemplateJson);
      const nextPayload = {
        ...current,
        flags: 32768,
        components: [
          ...(Array.isArray(current.components) ? current.components : []),
          ...(Array.isArray(addition.components) ? addition.components : []),
        ],
      };
      setTemplateJson(JSON.stringify(nextPayload, null, 2));
      setTemplateError('');
    } catch {
      setTemplateError('Current JSON is invalid. Fix it before adding another template.');
    }
  };

  const handleCreate = async () => {
    if (!chanName.trim() || !selected.size) return;

    let embedPayload = null;
    if (messageType === 'embed') {
      if (!embedJson.trim()) { toast.error('Vlož embed JSON.'); return; }
      try {
        embedPayload = JSON.parse(embedJson);
      } catch {
        setEmbedError('Neplatný JSON.');
        return;
      }
    }

    setCreating(true);
    try {
      const r = await channelSetupAPI.create({
        prefix,
        channel_name: chanName.trim(),
        member_ids: [...selected],
        initial_message: messageType === 'text' ? message : '',
        embed_payload: embedPayload,
      });
      setResult(r.data);
      toast.success(`Channel #${r.data.channel_name} created!`);
      setSelected(new Set()); setChanName(''); setMessage(''); setEmbedJson(''); setEmbedError('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Channel creation failed.');
    }
    setCreating(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><span className="text-[var(--gty-text-muted)] text-sm uppercase tracking-widest">Loading members…</span></div>;

  return (
    <div className="space-y-6 max-w-3xl">
      {result && (
        <motion.div initial={{ opacity:0,y:-10 }} animate={{ opacity:1,y:0 }}
          className="brutalist-card p-4 border-green-500/40 flex items-center justify-between">
          <span className="text-green-400 text-sm font-bold">✓ #{result.channel_name} created successfully</span>
          <button onClick={() => setResult(null)} className="text-[var(--gty-text-faint)] text-xs hover:text-white">Dismiss</button>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left: config */}
        <div className="space-y-4">
          {/* Prefix */}
          <div className="brutalist-card p-5 space-y-3">
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-muted)]">Channel Prefix</p>
            <div className="flex flex-wrap gap-2">
              {CHANNEL_PREFIXES_LIST.map(p => (
                <button key={p} onClick={() => setPrefix(p)}
                  className={`px-3 py-1.5 text-xs font-bold rounded border transition-colors
                    ${prefix===p ? 'border-[var(--gty-accent)] text-[var(--gty-accent)] bg-[var(--gty-accent-soft)]' : 'border-[var(--gty-border)] text-[var(--gty-text-muted)] hover:border-[var(--gty-border2)]'}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Channel name */}
          <div className="brutalist-card p-5 space-y-2">
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-muted)]">Channel Name</p>
            <div className="flex items-center gap-2 bg-[var(--gty-bg-hover)] rounded px-3 py-2 text-sm">
              <span className="text-[var(--gty-text-faint)]">#{prefix}-</span>
              <input value={chanName} onChange={e => setChanName(e.target.value.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,''))}
                placeholder="john-doe"
                className="bg-transparent flex-1 text-white focus:outline-none" />
            </div>
            {chanName && <p className="text-xs text-[var(--gty-text-faint)]">Preview: <span className="font-mono text-[var(--gty-text-secondary)]">#{prefix}-{chanName}</span></p>}
          </div>

         {/* Initial message / embed */}
          <div className="brutalist-card p-5 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-muted)]">
                Initial Message <span className="text-[var(--gty-text-faint)]">(pinned)</span>
              </p>
              <div className="flex flex-wrap justify-end gap-1">
                <button onClick={openTemplateModal}
                  className="px-2 py-1 text-[10px] font-bold rounded border border-[var(--gty-accent)] text-[var(--gty-accent)] hover:bg-[var(--gty-accent-soft)] transition-colors inline-flex items-center gap-1">
                  <Copy size={12} />
                  Load Template
                </button>
                {['text', 'embed'].map(t => (
                  <button key={t} onClick={() => { setMessageType(t); setEmbedError(''); }}
                    className={`px-2 py-1 text-[10px] font-bold rounded border transition-colors
                      ${messageType === t
                        ? 'border-[var(--gty-accent)] text-[var(--gty-accent)] bg-[var(--gty-accent-soft)]'
                        : 'border-[var(--gty-border)] text-[var(--gty-text-muted)] hover:border-[var(--gty-border2)]'}`}>
                    {t === 'text' ? 'Plain Text' : 'Embed JSON'}
                  </button>
                ))}
              </div>
            </div>

            {messageType === 'text' ? (
              <>
                <Textarea value={message} onChange={e => setMessage(e.target.value)}
                  placeholder="Optional message to pin in the channel…"
                  className="bg-[var(--gty-bg-hover)] border-[var(--gty-border)] text-white min-h-[100px] resize-none" />
              </>
            ) : (
              <>
                <Textarea value={embedJson} onChange={e => { setEmbedJson(e.target.value); setEmbedError(''); }}
                  placeholder={'import Discord Components V2 JSON...\n{"flags":32768,"components":[...]}'}
                  className="bg-[var(--gty-bg-hover)] border-[var(--gty-border)] text-white min-h-[140px] resize-none font-mono text-xs" />
                {embedError && <p className="text-red-400 text-xs">{embedError}</p>}
              </>
            )}
          </div>

          {/* Permission preview */}
          <div className="brutalist-card p-5 space-y-2">
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-muted)]">Permission Preview</p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2 text-red-400"><span>✕</span><span>@everyone — no access</span></div>
              <div className="flex items-center gap-2 text-green-400"><span>✓</span><span>Management roles — full access</span></div>
              {selectedMembers.map(m => (
                <div key={m.id} className="flex items-center gap-2 text-[var(--gty-accent)]"><span>✓</span><span>{m.display_name}</span></div>
              ))}
              {selected.size === 0 && <p className="text-[var(--gty-text-faint)] italic">No members selected yet</p>}
            </div>
          </div>

          <button onClick={handleCreate} disabled={creating || !chanName.trim() || !selected.size}
            className="w-full py-3 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-hover)] text-white font-black uppercase tracking-widest rounded-lg transition-colors disabled:opacity-40 text-sm">
            {creating ? 'Creating…' : `Create #${prefix}-${chanName || '…'}`}
          </button>
        </div>

        {/* Right: member picker */}
        <div className="brutalist-card p-5 space-y-3 flex flex-col" style={{maxHeight:'520px'}}>
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-muted)]">
            Members <span className="text-[var(--gty-accent)]">{selected.size} selected</span>
          </p>
          <div className="flex gap-2">
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search name…"
              className="flex-1 px-3 py-1.5 bg-[var(--gty-bg-hover)] border border-[var(--gty-border)] rounded text-sm text-white placeholder-[var(--gty-text-faint)] focus:outline-none focus:border-[var(--gty-accent)]" />
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              className="px-2 py-1.5 bg-[var(--gty-bg-hover)] border border-[var(--gty-border)] rounded text-xs text-[var(--gty-text-secondary)] focus:outline-none">
              <option value="name">A–Z</option>
              <option value="role">By Role</option>
            </select>
          </div>
          <div className="flex-1 overflow-y-auto space-y-0.5 pr-1">
            {filtered.map(m => (
              <label key={m.id} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-[var(--gty-bg-hover)] cursor-pointer transition-colors">
                <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleMember(m.id)}
                  className="w-4 h-4 accent-[var(--gty-accent)] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{m.display_name}</div>
                  {m.roles.length > 0 && <div className="text-[10px] text-[var(--gty-text-faint)] truncate">{m.roles.join(', ')}</div>}
                </div>
              </label>
            ))}
            {filtered.length === 0 && <p className="text-center text-[var(--gty-text-faint)] text-xs py-4">No members found.</p>}
          </div>
        </div>
      </div>

      <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
        <DialogContent className="!max-w-6xl bg-[var(--gty-bg-card)] border-[var(--gty-border)] text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm font-black uppercase tracking-[0.25em] flex items-center gap-2">
              <Copy className="w-4 h-4 text-[var(--gty-accent)]" />
              Load Channel Template
            </DialogTitle>
            <DialogDescription className="text-xs text-[var(--gty-text-faint)]">
              Build a Discord Components V2 payload, adjust the employee data or JSON manually, then apply it to the pinned message.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 xl:grid-cols-[220px_1fr_1.15fr] gap-4">
            <div className="space-y-3">
              <Label className="text-[10px] uppercase tracking-widest text-[var(--gty-text-muted)]">Templates</Label>
              <div className="space-y-2">
                {CHANNEL_EMBED_TEMPLATES.map(t => (
                  <button key={t.id} onClick={() => selectTemplate(t)}
                    className={`w-full text-left p-3 rounded border transition-colors ${
                      activeTemplateId === t.id
                        ? 'border-[var(--gty-accent)] bg-[var(--gty-accent-soft)]'
                        : 'border-[var(--gty-border)] bg-[var(--gty-bg-hover)] hover:border-[var(--gty-border2)]'
                    }`}>
                    <div className="text-xs font-black uppercase tracking-widest text-white">{t.name}</div>
                    <div className="text-[10px] text-[var(--gty-text-faint)] mt-1">{t.description}</div>
                  </button>
                ))}
              </div>
              <button onClick={handleAddTemplateComponent}
                className="w-full px-3 py-2 border border-dashed border-[var(--gty-border2)] text-[var(--gty-text-muted)] hover:text-white hover:border-[var(--gty-accent)] rounded text-[10px] font-black uppercase tracking-widest transition-colors inline-flex items-center justify-center gap-2">
                <Plus size={13} />
                Add Embed
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-[10px] uppercase tracking-widest text-[var(--gty-text-muted)]">Employee data fallback</Label>
                <button onClick={() => buildTemplateJson()}
                  className="px-3 py-1.5 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-hover)] text-white rounded text-[10px] font-black uppercase tracking-widest transition-colors">
                  Fill JSON
                </button>
              </div>
              <Textarea value={templateDataRaw} onChange={e => { setTemplateDataRaw(e.target.value); setTemplateError(''); }}
                className="bg-[var(--gty-bg-hover)] border-[var(--gty-border)] text-white min-h-[430px] resize-none font-mono text-xs leading-relaxed"
                placeholder="Paste employee file data here..." />
              <p className="text-[10px] text-[var(--gty-text-faint)]">
                Edit these values before filling JSON if the contractor data changes.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-[10px] uppercase tracking-widest text-[var(--gty-text-muted)]">Components V2 JSON</Label>
                <span className="text-[10px] uppercase tracking-widest text-[var(--gty-text-faint)]">Manual fallback enabled</span>
              </div>
              <Textarea value={templateJson} onChange={e => { setTemplateJson(e.target.value); setTemplateError(''); }}
                className="bg-[#07070a] border-[var(--gty-border)] text-white min-h-[430px] resize-none font-mono text-xs leading-relaxed"
                placeholder="Generated Components V2 JSON appears here..." />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-h-5">
                  {templateError && <p className="text-red-400 text-xs">{templateError}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setTemplateOpen(false)}
                    className="px-4 py-2 border border-[var(--gty-border)] text-[var(--gty-text-muted)] hover:text-white hover:border-[var(--gty-border2)] rounded text-xs font-black uppercase tracking-widest transition-colors">
                    Cancel
                  </button>
                  <button onClick={handleApplyTemplate}
                    className="px-4 py-2 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-hover)] text-white rounded text-xs font-black uppercase tracking-widest transition-colors">
                    Apply Template
                  </button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Staff Notes ──────────────────────────────────────────────────────────────

const StaffNotesContent = () => {
  const [content, setContent]   = useState('');
  const [images, setImages]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [viewImg, setViewImg]   = useState(null);
  const saveTimerRef            = useRef(null);
  const editorRef               = useRef(null);

  useEffect(() => {
    staffNotesAPI.get()
      .then(r => {
        setContent(r.data.content || '');
        const imgs = r.data.images;
        setImages(Array.isArray(imgs) ? imgs : []);
        setLastSaved(r.data.updated_at);
        if (editorRef.current) editorRef.current.innerText = r.data.content || '';
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const scheduleSave = useCallback((newContent, newImages) => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await staffNotesAPI.save(newContent, newImages);
        setLastSaved(new Date().toISOString());
      } catch {}
      setSaving(false);
    }, 1000);
  }, []);

  const handleInput = () => {
    const text = editorRef.current?.innerText || '';
    setContent(text);
    scheduleSave(text, images);
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        const fd = new FormData();
        fd.append('file', file);
        try {
          const r = await staffNotesAPI.uploadImage(fd);
          const url = r.data.url;
          const newImages = [...images, url];
          setImages(newImages);
          scheduleSave(content, newImages);
          toast.success('Image added to notes.');
        } catch { toast.error('Image upload failed.'); }
        break;
      }
    }
  };

  const removeImage = (url) => {
    const newImages = images.filter(i => i !== url);
    setImages(newImages);
    scheduleSave(content, newImages);
  };

  const formatTimestamp = (iso) => {
    if (!iso) return null;
    return new Date(iso).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
      hour12: true,
    });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><span className="text-[var(--gty-text-muted)] text-sm uppercase tracking-widest">Loading…</span></div>;

  return (
    <div className="space-y-4 max-w-3xl">
      {/* Editor */}
      <div className="brutalist-card p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-muted)]">Notes</p>
          <span className="text-xs text-[var(--gty-text-faint)]">
            {saving ? 'Saving…' : lastSaved ? `Last saved: ${formatTimestamp(lastSaved)} ET` : 'Not saved yet'}
          </span>
        </div>
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={handlePaste}
          className="min-h-[300px] text-sm text-white leading-relaxed focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-[var(--gty-text-faint)]"
          data-placeholder="Start writing your notes… paste images with Ctrl+V"
        />
        <p className="text-xs text-[var(--gty-text-faint)]">💡 Paste images directly with Ctrl+V</p>
      </div>

      {/* Image gallery */}
      {images.length > 0 && (
        <div className="brutalist-card p-5 space-y-3">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-muted)]">Images ({images.length})</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {images.map((url, i) => (
              <div key={i} className="relative group">
                <button onClick={() => setViewImg(url)} className="w-full aspect-square rounded overflow-hidden border border-[var(--gty-border)] hover:border-[var(--gty-accent)] transition-colors block">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </button>
                <button onClick={() => removeImage(url)}
                  className="absolute top-1 right-1 w-5 h-5 bg-red-600 hover:bg-red-500 text-white rounded text-xs hidden group-hover:flex items-center justify-center">
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fullscreen image viewer */}
      <AnimatePresence>
        {viewImg && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setViewImg(null)}>
            <img src={viewImg} alt="" className="max-w-full max-h-full rounded-lg object-contain" />
            <button className="absolute top-4 right-4 text-white text-2xl">✕</button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Driver Status ────────────────────────────────────────────────────────────

const PAYMENT_STATUS_BADGE = {
  paid:       { label: 'Paid',       cls: 'bg-green-900 text-green-300 border-green-700',           icon: '✓' },
  unpaid:     { label: 'Unpaid',     cls: 'bg-red-900/60 text-red-300 border-red-700/60',           icon: '✗' },
  new_hire:   { label: 'New Hire',   cls: 'bg-yellow-900/40 text-yellow-300 border-yellow-700/50',  icon: '⭐' },
  terminated: { label: 'Terminated', cls: 'bg-zinc-800 text-zinc-300 border-zinc-600',              icon: '💀' },
};

const DriversContent = ({ drivers, driversLoading }) => {
  const counts = drivers.reduce((acc, d) => {
    const s = d.payment_status || (d.has_paid ? 'paid' : d.is_new_hire ? 'new_hire' : 'unpaid');
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex gap-3 text-xs font-bold uppercase flex-wrap">
          {counts.paid       > 0 && <span className="text-green-400">{counts.paid} Paid</span>}
          {counts.unpaid     > 0 && <span className="text-red-400">{counts.unpaid} Unpaid</span>}
          {counts.new_hire   > 0 && <span className="text-yellow-400">{counts.new_hire} New Hire</span>}
          {counts.terminated > 0 && <span className="text-zinc-400">{counts.terminated} Terminated</span>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[var(--gty-text-secondary)] uppercase tracking-wider">
            {driversLoading ? 'Syncing...' : 'Auto-sync every 30s'}
          </span>
        </div>
      </div>
      {drivers.length === 0 && !driversLoading ? (
        <p className="text-[var(--gty-text-secondary)] text-sm">No drivers found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-[var(--gty-border)] text-left">
                <th className="pb-3 pr-4 text-xs font-bold uppercase tracking-widest text-[var(--gty-text-secondary)]">Driver</th>
                <th className="pb-3 text-xs font-bold uppercase tracking-widest text-[var(--gty-text-secondary)]">Status</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map(driver => {
                const status = driver.payment_status || (driver.has_paid ? 'paid' : driver.is_new_hire ? 'new_hire' : 'unpaid');
                const badge  = PAYMENT_STATUS_BADGE[status] || PAYMENT_STATUS_BADGE.unpaid;
                return (
                  <tr key={driver.discord_id} className="border-b border-[var(--gty-border)] hover:bg-[var(--gty-bg-card)] transition-colors">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        <img src={driver.avatar_url} alt={driver.username} className="w-9 h-9 rounded-full border-2 border-[var(--gty-border)]" onError={e => { e.target.src='https://cdn.discordapp.com/embed/avatars/0.png'; }} />
                        <span className="font-semibold">{driver.username}</span>
                      </div>
                    </td>
                    <td className="py-3">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold uppercase tracking-wider border rounded ${badge.cls}`}>
                        {badge.icon} {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── News ─────────────────────────────────────────────────────────────────────

const NewsContent = ({ isManagement }) => {
  const [posts, setPosts]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [title, setTitle]       = useState('');
  const [content, setContent]   = useState('');
  const [posting, setPosting]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await newsAPI.getNews(); setPosts(r.data); }
    catch { toast.error('Failed to load news'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePost = async () => {
    if (!title.trim() || !content.trim()) { toast.error('Title and content required'); return; }
    setPosting(true);
    try {
      await newsAPI.createNews({ title: title.trim(), content: content.trim() });
      toast.success('Update posted!');
      setTitle('');
      setContent('');
      load();
    } catch { toast.error('Failed to post update'); }
    finally { setPosting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try { await newsAPI.deleteNews(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const formatDate = (dateStr) => {
    try { return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return dateStr; }
  };

  return (
    <div className="space-y-8">
      {isManagement && (
        <div className="brutalist-card p-6 space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-secondary)]">Post an Update</h3>
          <input
            type="text"
            placeholder="Title..."
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full bg-[var(--gty-bg-card)] border border-[var(--gty-border)] rounded px-3 py-2 text-sm text-white placeholder-[var(--gty-text-faint)] focus:outline-none focus:border-[var(--gty-accent)]"
          />
          <textarea
            placeholder="Write your update..."
            value={content}
            onChange={e => setContent(e.target.value)}
            rows={4}
            className="w-full bg-[var(--gty-bg-card)] border border-[var(--gty-border)] rounded px-3 py-2 text-sm text-white placeholder-[var(--gty-text-faint)] focus:outline-none focus:border-[var(--gty-accent)] resize-none"
          />
          <button
            onClick={handlePost}
            disabled={posting}
            className="px-6 py-2 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-dim)] text-sm font-bold uppercase rounded disabled:opacity-50"
          >
            {posting ? 'Posting...' : 'Post Update'}
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-[var(--gty-text-muted)] text-xs uppercase">Loading...</div>
      ) : posts.length === 0 ? (
        <p className="text-[var(--gty-text-secondary)] text-sm">No updates posted yet.</p>
      ) : (
        <div className="space-y-4">
          {posts.map(post => (
            <div key={post.id} className="brutalist-card p-6">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 className="text-lg font-bold">{post.title}</h3>
                {isManagement && (
                  <button onClick={() => handleDelete(post.id)} className="text-[var(--gty-text-faint)] hover:text-red-400 transition-colors shrink-0 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-[#d4d4d8] text-sm leading-relaxed whitespace-pre-wrap mb-4">{post.content}</p>
              <div className="flex items-center gap-3 text-xs text-[var(--gty-text-faint)]">
                <span className="font-semibold text-[var(--gty-text-muted)]">{post.author_name}</span>
                <span>•</span>
                <span>{formatDate(post.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Truck Rental ─────────────────────────────────────────────────────────────

// Plates are permanently assigned — do NOT change order or values
const RENTAL_TRUCKS = [
  { id: 1,  name: 'Rental 01', plate: '10DRR016' },
  { id: 2,  name: 'Rental 02', plate: '17HDG164' },
  { id: 3,  name: 'Rental 03', plate: '21GEJ537' },
  { id: 4,  name: 'Rental 04', plate: '22TNL637' },
  { id: 5,  name: 'Rental 05', plate: '27ATZ423' },
  { id: 6,  name: 'Rental 06', plate: '39NWS262' },
  { id: 7,  name: 'Rental 07', plate: '5ZHL317'  },
  { id: 8,  name: 'Rental 08', plate: '54EMU200' },
  { id: 9,  name: 'Rental 09', plate: '54GLD144' },
  { id: 10, name: 'Rental 10', plate: '54LEW024' },
  { id: 11, name: 'Rental 11', plate: '56GU076'  },
  { id: 12, name: 'Rental 12', plate: '63ETK217' },
  { id: 13, name: 'Rental 13', plate: '69DJM324' },
  { id: 14, name: 'Rental 14', plate: '99CFX293' },
];

const VEHICLE_MODELS = [
  { label: 'Phantom Custom', image: '/PhantomCustom.png' },
  { label: 'Mule',           image: '/MuleCustom.png'    },
];

const BLANK_RENTAL = { name: '', license: '', phone: '', ssn: '', amount: '' };

const RentalField = ({ label, value, readOnly, onChange, placeholder }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-wider text-[var(--gty-text-secondary)] mb-1">{label}</label>
    <input
      type="text"
      value={value}
      readOnly={readOnly}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full px-3 py-2 rounded text-sm border ${
        readOnly
          ? 'bg-[var(--gty-bg-hover)] border-[var(--gty-border2)] text-[var(--gty-text-secondary)] cursor-not-allowed'
          : 'bg-[var(--gty-bg-card)] border-[var(--gty-border)] text-white focus:outline-none focus:border-[var(--gty-accent)]'
      }`}
    />
  </div>
);

const formatCountdown = (expiresAt) => {
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return 'Expired';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${m}m remaining`;
};

const VEHICLE_MODEL_IMAGE = {
  'Phantom Custom': '/PhantomCustom.png',
  'Mule':           '/MuleCustom.png',
};

const TruckRentalContent = ({ isManagement }) => {
  const [rentalMap,    setRentalMap]    = useState({});
  const [customTrucks, setCustomTrucks] = useState([]); // DB-backed extra trucks
  const [removedTruckIds, setRemovedTruckIds] = useState([]); // hidden built-in trucks
  const [selected,     setSelected]     = useState(null);
  const [modalOpen,    setModalOpen]    = useState(false);
  const [phase,        setPhase]        = useState('form'); // 'form' | 'document'
  const [form,         setForm]         = useState(BLANK_RENTAL);
  const [submitting,   setSubmitting]   = useState(false);
  const [ending,       setEnding]       = useState(null);
  const [deleting,     setDeleting]     = useState(null);
  const [docText,      setDocText]      = useState('');
  const [scanning,     setScanning]     = useState(false);
  const [cdlPreview,   setCdlPreview]   = useState(null);
  const [autoFilled,   setAutoFilled]   = useState({});
  // Fleet management modal
  const [addOpen,      setAddOpen]      = useState(false);
  const [addModel,     setAddModel]     = useState(VEHICLE_MODELS[0]);
  const [addPlate,     setAddPlate]     = useState('');
  const [addName,      setAddName]      = useState('');
  const [addSaving,    setAddSaving]    = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const r = await rentalAPI.getRentalStatus();
      const map = {};
      r.data.forEach(row => { map[row.truck_id] = row; });
      setRentalMap(map);
    } catch { /* silent */ }
  }, []);

  const loadFleet = useCallback(async () => {
    const [customResult, removedResult] = await Promise.allSettled([
      rentalAPI.getCustomTrucks(),
      rentalAPI.getRemovedTrucks(),
    ]);
    if (customResult.status === 'fulfilled') {
      setCustomTrucks(customResult.value.data.map(t => ({
        id:    t.id,          // already 10000 + db_id
        db_id: t.db_id,
        name:  t.name,
        plate: t.plate,
        model: t.vehicle_model,
        image: VEHICLE_MODEL_IMAGE[t.vehicle_model] || '/PhantomCustom.png',
      })));
    }
    if (removedResult.status === 'fulfilled') {
      setRemovedTruckIds(removedResult.value.data);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadFleet();
    const t = setInterval(loadStatus, 60000);
    return () => clearInterval(t);
  }, [loadStatus, loadFleet]);

  const openModal = (truck) => {
    setSelected(truck);
    setForm(BLANK_RENTAL);
    setPhase('form');
    setDocText('');
    setCdlPreview(null);
    setAutoFilled({});
    setModalOpen(true);
  };

  const preprocessCDL = (file) => new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width  = img.width  * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const id = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d  = id.data;
      for (let i = 0; i < d.length; i += 4) {
        const gray = 0.299 * d[i] + 0.587 * d[i+1] + 0.114 * d[i+2];
        const c    = Math.max(0, Math.min(255, (gray - 128) * 2 + 128));
        d[i] = d[i+1] = d[i+2] = c;
      }
      ctx.putImageData(id, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob(resolve, 'image/png');
    };
    img.onerror = reject;
    img.src = url;
  });

  const scanCDLFile = async (file) => {
    if (!file) return;
    setCdlPreview(URL.createObjectURL(file));
    setScanning(true);
    try {
      const processed = await preprocessCDL(file);
      const worker = await createWorker('eng', 1, {
        workerPath: 'https://unpkg.com/tesseract.js@5.1.1/dist/worker.min.js',
        langPath: 'https://tessdata.projectnaptha.com/4.0.0',
        corePath: 'https://unpkg.com/tesseract.js-core@5.0.0/tesseract-core-simd-lstm.wasm.js',
        logger: m => console.log('[Tesseract]', m),
      });
      const { data: { text } } = await worker.recognize(processed);
      await worker.terminate();

      console.log('[CDL OCR raw]', text);
      const full = text.replace(/\n/g, ' ');

      // Name: "Lastname, Firstname" pattern
      let name = '';
      const nameMatch = text.match(/([A-Za-z]{2,}),\s*([A-Za-z]{2,})/);
      if (nameMatch) name = nameMatch[2] + ' ' + nameMatch[1];

      // DL number — try label patterns first, then grab any 7-9 digit number
      let dl = '';
      const dlPatterns = [
        /D\.?L\.?\s*:\s*([0-9]{6,12})/i,
        /D\.?L\.?\s+([0-9]{6,12})/i,
        /LICENSE\s*[:#]?\s*([0-9]{6,12})/i,
      ];
      for (const pat of dlPatterns) {
        const m = full.match(pat);
        if (m) { dl = m[1]; break; }
      }
      if (!dl) {
        const nums = full.match(/[0-9]{7,9}/g);
        if (nums) dl = nums[0];
      }

      const filled = {};
      if (name) filled.name    = true;
      if (dl)   filled.license = true;

      setForm(prev => ({
        ...prev,
        ...(name ? { name }       : {}),
        ...(dl   ? { license: dl } : {}),
      }));
      setAutoFilled(filled);

      if (!name && !dl) toast.error('Could not extract data — check console for raw OCR');
      else toast.success('CDL scanned!');
    } catch (err) {
      console.error('[CDL scan error]', err);
      toast.error('Scan failed — check browser console for details');
    } finally {
      setScanning(false);
    }
  };

  const handleScanCDL = (e) => scanCDLFile(e.target.files?.[0]);

  const handleCDLPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        scanCDLFile(item.getAsFile());
        break;
      }
    }
  };

  const f = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.name || !form.license || !form.phone || !form.ssn || !form.amount) {
      toast.error('All fields are required');
      return;
    }
    setSubmitting(true);
    try {
      const r = await rentalAPI.submitRental({
        truck_id:     selected.id,
        truck_name:   selected.name,
        plate:        selected.plate,
        vehicle_model: selected.model || 'Phantom Custom',
        renter_name:  form.name,
        license_num:  form.license,
        phone:        form.phone,
        ssn:          form.ssn,
        amount:       form.amount,
      });
      setDocText(r.data.document);
      setPhase('document');
      toast.success('Rental agreement submitted!');
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit rental');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEndRental = async (truck) => {
    if (!window.confirm(`End rental for ${truck.name}?`)) return;
    setEnding(truck.id);
    try {
      await rentalAPI.endRental(truck.id);
      toast.success('Rental ended');
      loadStatus();
    } catch {
      toast.error('Failed to end rental');
    } finally {
      setEnding(null);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(docText)
      .then(() => toast.success('Copied to clipboard!'))
      .catch(() => toast.error('Copy failed'));
  };

  const handleAddVehicle = async () => {
    if (!addPlate.trim()) { toast.error('Enter a license plate'); return; }
    setAddSaving(true);
    try {
      await rentalAPI.addCustomTruck({
        name:          addName.trim() || addModel.label,
        plate:         addPlate.trim().replace(/^#/, ''),
        vehicle_model: addModel.label,
      });
      await loadFleet();
      setAddPlate('');
      setAddName('');
      setAddModel(VEHICLE_MODELS[0]);
      setAddOpen(false);
      toast.success('Vehicle added!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add vehicle');
    } finally {
      setAddSaving(false);
    }
  };

  const handleDeleteTruck = async (truck) => {
    const activeWarning = rentalMap[truck.id]
      ? '\n\nThis truck is currently rented. Removing it will also end the active rental.'
      : '';
    if (!window.confirm(`Remove ${truck.name} (#${truck.plate}) from the rental fleet?${activeWarning}`)) return;
    setDeleting(truck.id);
    try {
      await rentalAPI.deleteTruck(truck.id);
      if (truck.db_id) {
        setCustomTrucks(prev => prev.filter(t => t.id !== truck.id));
      } else {
        setRemovedTruckIds(prev => [...prev, truck.id]);
      }
      setRentalMap(prev => {
        const next = { ...prev };
        delete next[truck.id];
        return next;
      });
      toast.success('Vehicle removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove vehicle');
    } finally {
      setDeleting(null);
    }
  };

  const fleetTrucks = [
    ...RENTAL_TRUCKS.filter(truck => !removedTruckIds.includes(truck.id)),
    ...customTrucks,
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-[var(--gty-text-secondary)] text-sm">Select a rental truck to generate a signed agreement. Rentals last 24 hours.</p>
        <button
          onClick={() => setAddOpen(true)}
          className="flex items-center gap-2 px-3 py-2 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-dim)] text-xs font-bold uppercase rounded shrink-0"
        >
          <Truck className="w-4 h-4" /> Manage Fleet
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {fleetTrucks.map(truck => {
          const rental = rentalMap[truck.id];
          const isRented = !!rental;
          const truckImage = truck.image || '/PhantomCustom.png';
          return (
            <div key={truck.id} className={`rounded overflow-hidden border ${isRented ? 'bg-[var(--gty-bg-card)] border-red-900' : 'bg-[var(--gty-bg-card)] border-transparent'}`}>
              <div className="relative">
                <img src={truckImage} alt={truck.name} className="w-full h-28 object-cover" />
                <div className="absolute top-2 right-2">
                  {isRented
                    ? <span className="bg-red-900 text-red-300 text-[10px] font-bold uppercase px-2 py-0.5 rounded">Rented</span>
                    : <span className="bg-green-900 text-green-300 text-[10px] font-bold uppercase px-2 py-0.5 rounded">Available</span>}
                </div>
              </div>
              <div className="p-3">
                <div className="text-sm font-bold uppercase mb-0.5">{truck.name}</div>
                <div className="text-xs text-[var(--gty-text-muted)] font-mono mb-1">#{truck.plate}</div>
                {isRented && (
                  <div className="text-xs text-[var(--gty-text-secondary)] mb-2 space-y-0.5">
                    <div className="truncate">Renter: <span className="text-white">{rental.renter_name}</span></div>
                    <div className="text-yellow-500">{formatCountdown(rental.expires_at)}</div>
                  </div>
                )}
                {!isRented && <div className="mb-2" />}
                <div className="space-y-1.5">
                  {isRented ? (
                    <button
                      onClick={() => handleEndRental(truck)}
                      disabled={ending === truck.id}
                      className="w-full py-1.5 bg-red-700 hover:bg-red-600 text-xs font-bold uppercase rounded disabled:opacity-50"
                    >
                      {ending === truck.id ? 'Ending...' : 'End Rental'}
                    </button>
                  ) : (
                    <button
                      onClick={() => openModal(truck)}
                      className="w-full py-1.5 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-dim)] text-xs font-bold uppercase rounded"
                    >
                      Rent a Truck
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#111114] border border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          {phase === 'form' ? (
            <>
              <DialogHeader>
                <DialogTitle>Rental Agreement — {selected?.name}</DialogTitle>
                <DialogDescription className="text-xs text-slate-400">
                  Fill in renter details. Vehicle fields are pre-filled and locked.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-3">

                {/* CDL Scanner */}
                <div className="bg-[var(--gty-bg-card)] border border-[var(--gty-border2)] rounded-lg p-3 outline-none focus-within:border-[var(--gty-accent)]" onPaste={handleCDLPaste} tabIndex={0}>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-muted)] mb-2">📷 Scan CDL — Auto-fill</p>
                  <label className={`flex items-center justify-center gap-2 w-full py-2 rounded cursor-pointer text-xs font-bold uppercase transition-colors border
                    ${scanning ? 'border-purple-700 bg-purple-900/30 text-[var(--gty-accent)] opacity-60 pointer-events-none' : 'border-[var(--gty-border2)] bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] text-white'}`}>
                    {scanning ? <><span className="animate-spin inline-block">⟳</span> Scanning…</> : '📷 Upload CDL Screenshot'}
                    <input type="file" accept="image/*" className="hidden" onChange={handleScanCDL} disabled={scanning} />
                  </label>
                  <p className="text-[10px] text-[var(--gty-text-faint)] mt-1 text-center">or paste image with Ctrl+V</p>
                  {cdlPreview && (
                    <img src={cdlPreview} alt="CDL preview" className="mt-2 w-full rounded border border-[var(--gty-border2)] max-h-32 object-contain" />
                  )}
                  {Object.keys(autoFilled).length > 0 && (
                    <p className="text-[10px] text-green-400 mt-1.5">✓ Auto-filled: {Object.keys(autoFilled).join(', ')}</p>
                  )}
                </div>

                <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-faint)]">Renter Information</p>
                <div className="relative">
                  <RentalField label="Full Name" value={form.name} onChange={f('name')} placeholder="Brian Cooper" />
                  {autoFilled.name && <span className="absolute right-2 top-2 text-[10px] text-green-400 font-bold">AUTO</span>}
                </div>
                <div className="relative">
                  <RentalField label="Driver's License #" value={form.license} onChange={f('license')} placeholder="87303987" />
                  {autoFilled.license && <span className="absolute right-2 top-2 text-[10px] text-green-400 font-bold">AUTO</span>}
                </div>
                <RentalField label="Phone"              value={form.phone}   onChange={f('phone')}   placeholder="947-7085" />
                <RentalField label="SSN"                value={form.ssn}     onChange={f('ssn')}     placeholder="195501" />
                <div className="border-t border-[var(--gty-border)] pt-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-faint)] mb-3">Vehicle Details</p>
                  <div className="space-y-3">
                    <RentalField label="Vehicle Model"  value={selected?.model || 'Phantom Custom'} readOnly />
                    <RentalField label="License Plate"  value={`#${selected?.plate ?? ''}`} readOnly />
                    <RentalField label="Rental Fee ($)" value={form.amount} onChange={f('amount')} placeholder="500" />
                  </div>
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full py-2.5 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-dim)] text-sm font-bold uppercase rounded disabled:opacity-50 mt-2"
                >
                  {submitting ? 'Generating...' : 'Submit & Generate Agreement'}
                </button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Agreement Generated ✓</DialogTitle>
                <DialogDescription className="text-xs text-slate-400">
                  Logged to Discord. Copy below to share with the renter.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-3 space-y-3">
                <pre className="bg-[var(--gty-bg-base)] border border-[var(--gty-border)] rounded p-4 text-xs text-[#d4d4d8] leading-relaxed whitespace-pre-wrap break-words max-h-80 overflow-y-auto">
                  {docText}
                </pre>
                <button
                  onClick={handleCopy}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-dim)] text-sm font-bold uppercase rounded"
                >
                  <Copy className="w-4 h-4" /> Copy as Code Block
                </button>
                <button
                  onClick={() => setModalOpen(false)}
                  className="w-full py-2 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] text-sm font-bold uppercase rounded"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Add Vehicle Modal ── */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#111114] border border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Rental Fleet</DialogTitle>
            <DialogDescription className="text-xs text-slate-400">
              Add vehicles or remove existing trucks from the rental list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold uppercase tracking-wider text-[var(--gty-text-secondary)]">Current Fleet</label>
                <span className="text-[10px] font-mono text-[var(--gty-text-muted)]">{fleetTrucks.length} vehicles</span>
              </div>
              <div className="border border-[var(--gty-border)] rounded-lg divide-y divide-[var(--gty-border)] max-h-52 overflow-y-auto">
                {fleetTrucks.map(truck => (
                  <div key={truck.id} className="flex items-center gap-3 px-3 py-2.5 bg-[var(--gty-bg-card)]">
                    <div className="w-9 h-9 rounded bg-[var(--gty-bg-hover)] flex items-center justify-center shrink-0">
                      <Truck className="w-4 h-4 text-[var(--gty-accent)]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold uppercase truncate">{truck.name}</div>
                      <div className="text-[10px] font-mono text-[var(--gty-text-muted)]">#{truck.plate}</div>
                    </div>
                    {rentalMap[truck.id] && (
                      <span className="text-[9px] font-bold uppercase text-red-300 bg-red-950 px-1.5 py-0.5 rounded">Rented</span>
                    )}
                    {isManagement && (
                      <button
                        onClick={() => handleDeleteTruck(truck)}
                        disabled={deleting === truck.id}
                        className="p-2 rounded text-[var(--gty-text-muted)] hover:text-red-300 hover:bg-red-950 transition-colors disabled:opacity-40"
                        title={`Remove ${truck.name}`}
                        aria-label={`Remove ${truck.name}`}
                      >
                        {deleting === truck.id
                          ? <RefreshCw className="w-4 h-4 animate-spin" />
                          : <Trash2 className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                ))}
                {fleetTrucks.length === 0 && (
                  <div className="px-3 py-6 text-center text-xs text-[var(--gty-text-muted)]">No rental vehicles available.</div>
                )}
              </div>
              {!isManagement && (
                <p className="text-[10px] text-[var(--gty-text-faint)] mt-1.5">Only Management can remove vehicles.</p>
              )}
            </div>

            <div className="border-t border-[var(--gty-border)] pt-4 space-y-4">
              <p className="text-xs font-bold uppercase tracking-wider text-[var(--gty-text-secondary)]">Add Vehicle</p>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[var(--gty-text-secondary)] mb-2">Vehicle Model</label>
                <div className="grid grid-cols-2 gap-2">
                  {VEHICLE_MODELS.map(model => (
                    <button
                      key={model.label}
                      onClick={() => setAddModel(model)}
                      className={`relative overflow-hidden rounded border-2 transition-colors ${addModel.label === model.label ? 'border-[var(--gty-accent)]' : 'border-[var(--gty-border)] hover:border-[#52525b]'}`}
                    >
                      <img src={model.image} alt={model.label} className="w-full h-20 object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-end p-2">
                        <span className="text-xs font-bold text-white uppercase">{model.label}</span>
                      </div>
                      {addModel.label === model.label && (
                        <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              <RentalField
                label="Name (optional)"
                value={addName}
                onChange={e => setAddName(e.target.value)}
                placeholder="e.g. Rental 15"
              />
              <RentalField
                label="License Plate"
                value={addPlate}
                onChange={e => setAddPlate(e.target.value)}
                placeholder="54EMU200"
              />
              <button
                onClick={handleAddVehicle}
                disabled={addSaving}
                className="w-full py-2.5 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-dim)] text-sm font-bold uppercase rounded disabled:opacity-50"
              >
                {addSaving ? 'Saving...' : 'Add Vehicle'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Profile ──────────────────────────────────────────────────────────────────

const ProfileContent = ({ user, onProfileUpdate }) => {
  const [displayName,   setDisplayName]   = useState('');
  const [avatarFile,    setAvatarFile]    = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [uploading,     setUploading]     = useState(false);

  const currentAvatar = user?.custom_avatar_url
    ? (user.custom_avatar_url.startsWith('http') ? user.custom_avatar_url : `${process.env.REACT_APP_BACKEND_URL || ''}${user.custom_avatar_url}`)
    : user?.avatar
      ? `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`
      : `https://cdn.discordapp.com/embed/avatars/0.png`;

  useEffect(() => {
    setDisplayName(user?.display_name || user?.username || '');
  }, [user]);

  const handleSave = async () => {
    const name = displayName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await profileAPI.updateProfile({ display_name: name, sync_discord: false });
      toast.success('Display name updated!');
      onProfileUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAvatarFile(f);
    setAvatarPreview(URL.createObjectURL(f));
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;
    setUploading(true);
    try {
      await profileAPI.uploadAvatar(avatarFile);
      toast.success('Profile picture updated!');
      setAvatarFile(null);
      setAvatarPreview(null);
      onProfileUpdate();
    } catch {
      toast.error('Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-lg">

      {/* ── Avatar ── */}
      <div className="bg-[#111114] border border-[var(--gty-border)] rounded-xl p-6">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-faint)] mb-5">Profile Picture</p>
        <div className="flex items-center gap-6">
          <div className="relative shrink-0">
            <img
              src={avatarPreview || currentAvatar}
              alt="avatar"
              className="w-20 h-20 rounded-full border-4 border-[var(--gty-accent-dim)] object-cover"
            />
            {avatarPreview && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                <Upload className="w-2.5 h-2.5 text-black" strokeWidth={3} />
              </span>
            )}
          </div>
          <div className="space-y-2">
            <p className="text-xs text-[var(--gty-text-muted)] leading-relaxed">
              Upload a custom avatar for the GTY website.<br />Max 5 MB · JPG, PNG, GIF
            </p>
            <div className="flex gap-2 flex-wrap">
              <label className="cursor-pointer px-3 py-1.5 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] text-xs font-bold uppercase rounded transition-colors">
                <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                Choose File
              </label>
              {avatarFile && (
                <button
                  onClick={handleAvatarUpload}
                  disabled={uploading}
                  className="px-3 py-1.5 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-dim)] text-xs font-bold uppercase rounded disabled:opacity-50 transition-colors"
                >
                  {uploading ? 'Uploading…' : 'Upload'}
                </button>
              )}
            </div>
            {avatarFile && <p className="text-[10px] text-yellow-500">{avatarFile.name}</p>}
          </div>
        </div>
      </div>

      {/* ── Display Name ── */}
      <div className="bg-[#111114] border border-[var(--gty-border)] rounded-xl p-6">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-faint)] mb-1">Display Name</p>
        <p className="text-xs text-[var(--gty-text-muted)] mb-5 leading-relaxed">
          Shown in truck logs, reports, and across the website.
        </p>
        <div className="space-y-3">
          <input
            type="text"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            maxLength={32}
            placeholder="Your display name…"
            className="w-full px-3 py-2.5 bg-[var(--gty-bg-card)] border border-[var(--gty-border)] rounded-lg text-sm text-white placeholder-[var(--gty-text-faint)] focus:outline-none focus:border-[var(--gty-accent)] transition-colors"
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-[var(--gty-text-faint)]">{displayName.length}/32</span>
            <button
              onClick={handleSave}
              disabled={saving || !displayName.trim()}
              className="px-5 py-2 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-hover)] text-xs font-bold uppercase rounded disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

        {/* Appearance */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="brutalist-card p-6 space-y-5">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-muted)]">Appearance</h3>

          {/* Mode toggle */}
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-faint)]">Mode</p>
            <div className="flex gap-2">
              {['dark', 'light'].map(m => {
                const active = (localStorage.getItem('gty_theme') ? JSON.parse(localStorage.getItem('gty_theme')).mode : 'dark') === m;
                return (
                  <button key={m} onClick={async () => {
                    const current = localStorage.getItem('gty_theme') ? JSON.parse(localStorage.getItem('gty_theme')) : { mode: 'dark', accent: 'purple' };
                    const next = { ...current, mode: m };
                    document.documentElement.setAttribute('data-mode', m);
                    localStorage.setItem('gty_theme', JSON.stringify(next));
                    try { await preferencesAPI.update({ theme_mode: m }); } catch {}
                  }}
                    className={`px-4 py-2 text-xs font-bold uppercase rounded border transition-colors
                      ${active ? 'border-[var(--gty-accent)] text-[var(--gty-accent)] bg-[var(--gty-accent-soft)]' : 'border-[var(--gty-border)] text-[var(--gty-text-muted)] hover:border-[var(--gty-border2)]'}`}>
                    {m === 'dark' ? '🌙 Dark' : '☀️ Light'}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Accent color */}
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--gty-text-faint)]">Accent Color</p>
            <div className="flex gap-3 flex-wrap">
              {[
                { name: 'purple', color: '#a855f7', label: 'Purple' },
                { name: 'blue',   color: '#3b82f6', label: 'Blue'   },
                { name: 'amber',  color: '#f59e0b', label: 'Amber'  },
                { name: 'green',  color: '#22c55e', label: 'Green'  },
                { name: 'red',    color: '#ef4444', label: 'Red'    },
                { name: 'cyan',   color: '#06b6d4', label: 'Cyan'   },
              ].map(a => {
                const currentAccent = localStorage.getItem('gty_theme') ? JSON.parse(localStorage.getItem('gty_theme')).accent : 'purple';
                const active = currentAccent === a.name;
                return (
                  <button key={a.name} onClick={async () => {
                    const current = localStorage.getItem('gty_theme') ? JSON.parse(localStorage.getItem('gty_theme')) : { mode: 'dark', accent: 'purple' };
                    const next = { ...current, accent: a.name };
                    document.documentElement.setAttribute('data-accent', a.name);
                    localStorage.setItem('gty_theme', JSON.stringify(next));
                    try { await preferencesAPI.update({ theme_accent: a.name }); } catch {}
                  }}
                    title={a.label}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${active ? 'scale-125 border-white' : 'border-transparent hover:scale-110'}`}
                    style={{ backgroundColor: a.color }}
                  />
                );
              })}
            </div>
          </div>
        </motion.div>

    </div>
  );
};

// ─── Management Documents ──────────────────────────────────────────────────────

const TABS = [
  { key: 'repairs',       label: 'Repairs' },
  { key: 'missing_items', label: 'Missing Items' },
  { key: 'logs',          label: 'Usage Logs' },
];

const MgmtDocumentsContent = () => {
  const [tab, setTab]               = useState('repairs');
  const [repairs, setRepairs]       = useState([]);
  const [missing, setMissing]       = useState([]);
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [editId, setEditId]         = useState(null);
  const [editData, setEditData]     = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, m, l] = await Promise.all([
        managementAPI.getRepairReports(),
        managementAPI.getMissingItemsReports(),
        trucksAPI.getTruckLogs(),
      ]);
      setRepairs(r.data);
      setMissing(m.data);
      setLogs(l.data);
    } catch {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row, fields) => { setEditId(row.id); setEditData(fields); };
  const cancelEdit = () => { setEditId(null); setEditData({}); };

  const saveRepair = async (id) => {
    try {
      await managementAPI.updateRepairReport(id, editData);
      toast.success('Report updated');
      setEditId(null);
      load();
    } catch { toast.error('Failed to update'); }
  };

  const deleteRepair = async (id) => {
    if (!window.confirm('Delete this repair report?')) return;
    try {
      await managementAPI.deleteRepairReport(id);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const saveMissing = async (id) => {
    try {
      await managementAPI.updateMissingItemsReport(id, editData);
      toast.success('Report updated');
      setEditId(null);
      load();
    } catch { toast.error('Failed to update'); }
  };

  const deleteMissing = async (id) => {
    if (!window.confirm('Delete this missing items report?')) return;
    try {
      await managementAPI.deleteMissingItemsReport(id);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const thCls = 'text-left text-[10px] font-black uppercase tracking-widest text-[var(--gty-text-muted)] pb-2 pr-4';
  const tdCls = 'py-2 pr-4 text-sm align-top';
  const inputCls = 'w-full bg-[var(--gty-bg-hover)] border border-[var(--gty-border2)] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[var(--gty-accent)]';

  if (loading) return <div className="text-[var(--gty-text-muted)] text-xs uppercase">Loading...</div>;

  return (
    <div>
      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-[var(--gty-bg-card)] p-1 rounded-lg w-fit">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-md text-xs font-bold uppercase transition-colors ${tab === t.key ? 'bg-[var(--gty-accent-dim)] text-white' : 'text-[var(--gty-text-muted)] hover:text-white'}`}>
            {t.label}
            <span className="ml-2 text-[10px] opacity-60">
              {t.key === 'repairs' ? repairs.length : t.key === 'missing_items' ? missing.length : logs.length}
            </span>
          </button>
        ))}
      </div>

      {/* Repairs tab */}
      {tab === 'repairs' && (
        repairs.length === 0 ? <p className="text-[var(--gty-text-secondary)] text-sm">No repair reports.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={thCls}>Truck</th>
                  <th className={thCls}>Driver</th>
                  <th className={thCls}>Issue</th>
                  <th className={thCls}>Description</th>
                  <th className={thCls}>Date</th>
                  <th className={thCls}></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--gty-border)]">
                {repairs.map(row => (
                  <tr key={row.id}>
                    <td className={tdCls}><span className="font-semibold">{row.truck_name}</span> <span className="text-[var(--gty-text-muted)]">#{row.truck_number}</span></td>
                    <td className={tdCls}>{row.driver_name}</td>
                    <td className={tdCls}>
                      {editId === row.id
                        ? <input className={inputCls} value={editData.issue_type ?? ''} onChange={e => setEditData(d => ({...d, issue_type: e.target.value}))} />
                        : <span className="text-yellow-400 text-xs font-bold">{row.issue_type}</span>}
                    </td>
                    <td className={tdCls}>
                      {editId === row.id
                        ? <textarea className={inputCls} rows={2} value={editData.description ?? ''} onChange={e => setEditData(d => ({...d, description: e.target.value}))} />
                        : <span className="text-[var(--gty-text-secondary)]">{row.description || '—'}</span>}
                    </td>
                    <td className={tdCls + ' text-[var(--gty-text-muted)] text-xs whitespace-nowrap'}>{new Date(row.reported_at).toLocaleString()}</td>
                    <td className={tdCls}>
                      <div className="flex gap-1">
                        {editId === row.id ? (
                          <>
                            <button onClick={() => saveRepair(row.id)} className="px-2 py-1 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-hover)] text-xs font-bold rounded transition-colors">Save</button>
                            <button onClick={cancelEdit} className="px-2 py-1 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] text-xs rounded transition-colors"><X className="w-3 h-3" /></button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => startEdit(row, { issue_type: row.issue_type, description: row.description || '' })} className="p-1.5 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] rounded transition-colors"><Pencil className="w-3 h-3" /></button>
                            <button onClick={() => deleteRepair(row.id)} className="p-1.5 bg-[var(--gty-bg-hover)] hover:bg-red-900 text-red-400 rounded transition-colors"><Trash2 className="w-3 h-3" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Missing Items tab */}
      {tab === 'missing_items' && (
        missing.length === 0 ? <p className="text-[var(--gty-text-secondary)] text-sm">No missing items reports.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={thCls}>Truck</th>
                  <th className={thCls}>Driver</th>
                  <th className={thCls}>Missing Items</th>
                  <th className={thCls}>Date</th>
                  <th className={thCls}></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--gty-border)]">
                {missing.map(row => (
                  <tr key={row.id}>
                    <td className={tdCls}><span className="font-semibold">{row.truck_name}</span> <span className="text-[var(--gty-text-muted)]">#{row.truck_number}</span></td>
                    <td className={tdCls}>{row.driver_name}</td>
                    <td className={tdCls}>
                      {editId === row.id
                        ? <input className={inputCls} value={editData.missing_items ?? ''} onChange={e => setEditData(d => ({...d, missing_items: e.target.value}))} />
                        : <span className="text-orange-400 text-xs">{row.missing_items}</span>}
                    </td>
                    <td className={tdCls + ' text-[var(--gty-text-muted)] text-xs whitespace-nowrap'}>{new Date(row.reported_at).toLocaleString()}</td>
                    <td className={tdCls}>
                      <div className="flex gap-1">
                        {editId === row.id ? (
                          <>
                            <button onClick={() => saveMissing(row.id)} className="px-2 py-1 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-hover)] text-xs font-bold rounded transition-colors">Save</button>
                            <button onClick={cancelEdit} className="px-2 py-1 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] text-xs rounded transition-colors"><X className="w-3 h-3" /></button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => startEdit(row, { missing_items: row.missing_items })} className="p-1.5 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] rounded transition-colors"><Pencil className="w-3 h-3" /></button>
                            <button onClick={() => deleteMissing(row.id)} className="p-1.5 bg-[var(--gty-bg-hover)] hover:bg-red-900 text-red-400 rounded transition-colors"><Trash2 className="w-3 h-3" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Usage Logs tab (read-only) */}
      {tab === 'logs' && (
        logs.length === 0 ? <p className="text-[var(--gty-text-secondary)] text-sm">No logs yet.</p> : (
          <div className="space-y-2">
            {logs.map((log, i) => (
              <div key={log.id ?? i} className="flex items-center justify-between p-3 bg-[var(--gty-bg-card)] rounded text-sm">
                <div className="flex items-center gap-3">
                  <span className="font-bold">{log.truck_name}</span>
                  <span className="text-[var(--gty-text-muted)]">#{log.truck_number}</span>
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${log.action === 'checked_out' ? 'bg-yellow-900 text-yellow-300' : 'bg-green-900 text-green-300'}`}>
                    {log.action === 'checked_out' ? 'Out' : 'Returned'}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-xs font-semibold">{log.driver_name}</div>
                  <div className="text-xs text-[var(--gty-text-muted)]">{new Date(log.timestamp).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

// ─── Driver Names (management) ────────────────────────────────────────────────

const DriverNamesContent = () => {
  const [driversList,    setDriversList]    = useState([]);
  const [loadingList,    setLoadingList]    = useState(true);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [newName,        setNewName]        = useState('');
  const [saving,         setSaving]         = useState(false);

  const load = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await managementAPI.getDriversList();
      setDriversList(r.data);
    } catch {
      toast.error('Failed to load drivers list');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApply = async () => {
    if (!selectedDriver || !newName.trim()) return;
    setSaving(true);
    try {
      await managementAPI.updateDriverProfile(selectedDriver.driver_id, {
        display_name: newName.trim(),
        sync_discord: false,
      });
      toast.success(`"${selectedDriver.driver_name}" přejmenován na "${newName.trim()}"`);
      setDriversList(prev => prev.map(d =>
        d.driver_id === selectedDriver.driver_id ? { ...d, driver_name: newName.trim() } : d
      ));
      setSelectedDriver(null);
      setNewName('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update driver name');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg space-y-6">
      <p className="text-xs text-[var(--gty-text-muted)] leading-relaxed">
        Override the website display name for any driver. Changes are reflected immediately in logs, reports, and the fleet view.
      </p>

      <div className="bg-[#111114] border border-[var(--gty-border)] rounded-xl p-6 space-y-4">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-faint)]">Select Driver</p>

        {loadingList ? (
          <p className="text-xs text-[var(--gty-text-faint)]">Loading drivers…</p>
        ) : (
          <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
            {driversList.map(d => (
              <button
                key={d.driver_id}
                onClick={() => { setSelectedDriver(d); setNewName(d.driver_name); }}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${
                  selectedDriver?.driver_id === d.driver_id
                    ? 'bg-[var(--gty-accent-soft)] border border-[var(--gty-accent-dim)] text-white'
                    : 'bg-[var(--gty-bg-card)] border border-transparent hover:border-[var(--gty-border2)] text-[var(--gty-text-secondary)] hover:text-white'
                }`}
              >
                <span className="font-medium">{d.driver_name}</span>
                {selectedDriver?.driver_id === d.driver_id && (
                  <Check className="w-3.5 h-3.5 text-[var(--gty-accent)] shrink-0" strokeWidth={3} />
                )}
              </button>
            ))}
            {driversList.length === 0 && (
              <p className="text-xs text-[var(--gty-text-faint)] text-center py-4">No drivers found</p>
            )}
          </div>
        )}
      </div>

      {selectedDriver && (
        <div className="bg-[#111114] border border-[var(--gty-border)] rounded-xl p-6 space-y-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-faint)]">
            New Name — <span className="text-white normal-case">{selectedDriver.driver_name}</span>
          </p>
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleApply()}
            maxLength={32}
            placeholder="New display name…"
            className="w-full px-3 py-2.5 bg-[var(--gty-bg-card)] border border-[var(--gty-border)] rounded-lg text-sm text-white placeholder-[var(--gty-text-faint)] focus:outline-none focus:border-[var(--gty-accent)] transition-colors"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={() => { setSelectedDriver(null); setNewName(''); }}
              className="px-4 py-2 bg-[var(--gty-bg-hover)] hover:bg-[var(--gty-bg-hover2)] text-xs font-bold uppercase rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={saving || !newName.trim()}
              className="flex-1 py-2 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-dim)] text-xs font-bold uppercase rounded disabled:opacity-50 transition-colors"
            >
              {saving ? 'Applying…' : 'Apply'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Badge Generator ──────────────────────────────────────────────────────────

// ── Tweak these numbers if text/photo lands in the wrong spot ──
const BADGE_CFG = {
  W: 1024, H: 1536,
  photo:    { x: 152, y: 523, w: 286, h: 354 },
  name:     { x: 571, y: 641 },
  gtyId:    { x: 692, y: 700 },
  role:     { x: 664, y: 775 },
  hireDate: { x: 708, y: 845 },
  fontSize: 34,
  color: '#FFFFFF',
};

const ROLE_OPTIONS = [
  'Driver', 'Expert Truck Driver', 'Senior Operator',
  'Dispatcher', 'Management', 'Admin', 'Co-Owner', 'Owner',
];

// ⚠️ Field must be defined OUTSIDE BadgeGeneratorContent — otherwise React
//    remounts the input on every keystroke, losing focus after each character.
const BadgeField = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-faint)] mb-1.5">{label}</label>
    {children}
  </div>
);
const badgeInputCls = "w-full px-3 py-2.5 bg-[var(--gty-bg-card)] border border-[var(--gty-border)] rounded-lg text-sm text-white placeholder-[var(--gty-text-faint)] focus:outline-none focus:border-[var(--gty-accent)] transition-colors";

const loadBadgeImage = (src) => new Promise((resolve, reject) => {
  const img = new window.Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error(`Failed to load badge image: ${src}`));
  img.src = src;
});

const BadgeGeneratorContent = () => {
  const [form, setForm] = useState({ name: '', gtyId: '', role: 'Driver', hireDate: '' });
  const [photoURL,    setPhotoURL]    = useState(null);
  const [previewUrl,  setPreviewUrl]  = useState(null);
  const [generating,  setGenerating]  = useState(false);
  const canvasRef = useRef(null);

  const ZERO_OFFSETS = {
    nameX: 0, nameY: 0,
    gtyIdX: 0, gtyIdY: 0,
    roleX: 0, roleY: 0,
    hireDateX: 0, hireDateY: 0,
    allX: 0,        // shifts ALL text at once
    fontSize: 0,
    photoX: 0, photoY: 0, photoSize: 0,
  };
  const [offsets, setOffsets] = useState(ZERO_OFFSETS);
  const setOff = (key) => (e) => setOffsets(p => ({ ...p, [key]: Number(e.target.value) }));

  const f = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoURL(URL.createObjectURL(file));
    setPreviewUrl(null);
  };


  const generate = async () => {
    if (!form.name.trim()) { toast.error('Enter a name'); return; }
    setGenerating(true);
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      canvas.width  = BADGE_CFG.W;
      canvas.height = BADGE_CFG.H;

      // 1 — Draw template
      const template = await loadBadgeImage('/badge_template.png');
      ctx.drawImage(template, 0, 0, BADGE_CFG.W, BADGE_CFG.H);

      // 2 — Draw driver photo with rounded clip
      if (photoURL) {
        const img = await loadBadgeImage(photoURL);
        const px = BADGE_CFG.photo.x + offsets.photoX;
        const py = BADGE_CFG.photo.y + offsets.photoY;
        const pw = BADGE_CFG.photo.w + offsets.photoSize;
        const ph = BADGE_CFG.photo.h + offsets.photoSize;
        const scale = Math.max(pw / img.width, ph / img.height);
        const sw = img.width * scale;
        const sh = img.height * scale;
        ctx.save();
        ctx.beginPath();
        const r = 10;
        ctx.moveTo(px + r, py);
        ctx.lineTo(px + pw - r, py); ctx.quadraticCurveTo(px + pw, py, px + pw, py + r);
        ctx.lineTo(px + pw, py + ph - r); ctx.quadraticCurveTo(px + pw, py + ph, px + pw - r, py + ph);
        ctx.lineTo(px + r, py + ph); ctx.quadraticCurveTo(px, py + ph, px, py + ph - r);
        ctx.lineTo(px, py + r); ctx.quadraticCurveTo(px, py, px + r, py);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(img, px - (sw - pw) / 2, py - (sh - ph) / 2, sw, sh);
        ctx.restore();
      }

      // 3 — Draw text values (base position + individual + global offsets)
      const fSize = BADGE_CFG.fontSize + offsets.fontSize;
      ctx.font      = `bold ${fSize}px Arial, sans-serif`;
      ctx.fillStyle = BADGE_CFG.color;
      const gx = offsets.allX; // global X applied to all
      ctx.fillText(form.name || 'Name',     BADGE_CFG.name.x     + gx + offsets.nameX,     BADGE_CFG.name.y     + offsets.nameY);
      ctx.fillText(form.gtyId || 'GTY ID',  BADGE_CFG.gtyId.x    + gx + offsets.gtyIdX,    BADGE_CFG.gtyId.y    + offsets.gtyIdY);
      ctx.fillText(form.role,               BADGE_CFG.role.x      + gx + offsets.roleX,     BADGE_CFG.role.y     + offsets.roleY);
      ctx.fillText(form.hireDate || 'Date', BADGE_CFG.hireDate.x  + gx + offsets.hireDateX, BADGE_CFG.hireDate.y + offsets.hireDateY);

      // Remove black background from the badge card
      const imageData = ctx.getImageData(0, 0, BADGE_CFG.W, BADGE_CFG.H);
      const d = imageData.data;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i] < 30 && d[i + 1] < 30 && d[i + 2] < 30) {
          d[i + 3] = 0;
        }
      }
      ctx.putImageData(imageData, 0, 0);

      setPreviewUrl(canvas.toDataURL('image/png'));
      toast.success('Badge generated!');
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Badge generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!previewUrl) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = `GTY_Badge_${form.name.trim().replace(/\s+/g, '_') || 'driver'}.png`;
    a.click();
  };

  return (
    <div className="flex gap-8 items-start">

      {/* ── Form ── */}
      <div className="w-72 shrink-0 space-y-4">

        <BadgeField label="Driver Photo">
          <label className="flex flex-col items-center justify-center gap-2 h-32 border-2 border-dashed border-[var(--gty-border)] rounded-lg cursor-pointer hover:border-[var(--gty-accent)] transition-colors overflow-hidden"
            style={{ background: photoURL ? 'repeating-conic-gradient(#18181b 0% 25%, #111114 0% 50%) 0 0 / 16px 16px' : undefined }}>
            {photoURL
              ? <img src={photoURL} alt="preview" className="w-full h-full object-contain" />
              : <><Upload className="w-6 h-6 text-[var(--gty-text-faint)]" /><span className="text-xs text-[var(--gty-text-faint)]">Click to upload photo</span></>
            }
            <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
          </label>
        </BadgeField>

        <BadgeField label="Full Name">
          <input type="text" className={badgeInputCls} placeholder="Jethro Bands" value={form.name} onChange={f('name')} />
        </BadgeField>

        <BadgeField label="GTY ID #">
          <input type="text" className={badgeInputCls} placeholder="96849062" value={form.gtyId} onChange={f('gtyId')} />
        </BadgeField>

        <BadgeField label="Role">
          <select className={badgeInputCls} value={form.role} onChange={f('role')}>
            {ROLE_OPTIONS.map(r => <option key={r}>{r}</option>)}
          </select>
        </BadgeField>

        <BadgeField label="Hire Date">
          <input type="text" className={badgeInputCls} placeholder="01/25/2026" value={form.hireDate} onChange={f('hireDate')} />
        </BadgeField>

        <button onClick={generate} disabled={generating}
          className="w-full py-3 bg-[var(--gty-accent-dim)] hover:bg-[var(--gty-accent-dim)] text-sm font-bold uppercase rounded-lg disabled:opacity-50 transition-colors">
          {generating ? 'Generating…' : 'Generate Badge'}
        </button>

        {previewUrl && (
          <button onClick={handleDownload}
            className="w-full py-3 bg-green-700 hover:bg-green-600 text-sm font-bold uppercase rounded-lg transition-colors">
            ↓ Download PNG
          </button>
        )}

        {/* ── Calibration sliders ── */}
        <div className="bg-[#111114] border border-yellow-900/40 rounded-xl p-4 space-y-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-yellow-600">Calibration</p>
          <p className="text-[10px] text-[var(--gty-text-faint)] leading-relaxed">Adjust → Generate → repeat until correct. Send screenshot when done.</p>

          {/* Global X */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[var(--gty-text-muted)] w-16 shrink-0">All X</span>
            <input type="range" min={-200} max={200} value={offsets.allX} onChange={setOff('allX')} className="flex-1 accent-yellow-500 h-1" />
            <span className="text-[10px] text-yellow-500 w-9 text-right tabular-nums">{offsets.allX > 0 ? '+' : ''}{offsets.allX}</span>
          </div>

          {/* Per-field X + Y */}
          {[
            { labelKey: 'Name',   xKey: 'nameX',     yKey: 'nameY' },
            { labelKey: 'GTY ID', xKey: 'gtyIdX',    yKey: 'gtyIdY' },
            { labelKey: 'Role',   xKey: 'roleX',     yKey: 'roleY' },
            { labelKey: 'Date',   xKey: 'hireDateX', yKey: 'hireDateY' },
          ].map(({ labelKey, xKey, yKey }) => (
            <div key={xKey} className="space-y-1 bg-[var(--gty-bg-card)] rounded-lg p-2">
              <p className="text-[9px] font-bold uppercase tracking-widest text-[var(--gty-text-faint)]">{labelKey}</p>
              {[{ key: xKey, axis: 'X' }, { key: yKey, axis: 'Y' }].map(({ key, axis }) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-[10px] text-[var(--gty-text-muted)] w-4 shrink-0">{axis}</span>
                  <input type="range" min={-200} max={200} value={offsets[key]} onChange={setOff(key)} className="flex-1 accent-yellow-500 h-1" />
                  <span className="text-[10px] text-yellow-500 w-9 text-right tabular-nums">{offsets[key] > 0 ? '+' : ''}{offsets[key]}</span>
                </div>
              ))}
            </div>
          ))}

          <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-faint)] pt-1">Text size</p>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[var(--gty-text-muted)] w-16 shrink-0">Size</span>
            <input type="range" min={-20} max={40} value={offsets.fontSize} onChange={setOff('fontSize')} className="flex-1 accent-yellow-500 h-1" />
            <span className="text-[10px] text-yellow-500 w-9 text-right tabular-nums">{BADGE_CFG.fontSize + offsets.fontSize}px</span>
          </div>

          <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-faint)] pt-1">Photo</p>
          {[
            { key: 'photoX',    label: 'X',    min: -200, max: 200 },
            { key: 'photoY',    label: 'Y',    min: -200, max: 200 },
            { key: 'photoSize', label: 'Size', min: -200, max: 300 },
          ].map(({ key, label, min, max }) => (
            <div key={key} className="flex items-center gap-2">
              <span className="text-[10px] text-[var(--gty-text-muted)] w-16 shrink-0">{label}</span>
              <input type="range" min={min} max={max} value={offsets[key]} onChange={setOff(key)} className="flex-1 accent-yellow-500 h-1" />
              <span className="text-[10px] text-yellow-500 w-9 text-right tabular-nums">{offsets[key] > 0 ? '+' : ''}{offsets[key]}</span>
            </div>
          ))}

          <button onClick={() => setOffsets(ZERO_OFFSETS)}
            className="text-[10px] text-[var(--gty-text-faint)] hover:text-white transition-colors pt-1">
            ↺ Reset all
          </button>
        </div>
      </div>

      {/* ── Preview ── */}
      <div className="flex-1 min-w-0">
        <canvas ref={canvasRef} className="hidden" />
        {previewUrl ? (
          <div className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-faint)]">Preview</p>
            <img src={previewUrl} alt="Generated badge" className="rounded-xl border border-[var(--gty-border)] shadow-xl"
              style={{ maxHeight: '600px', width: 'auto' }} />
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 border-2 border-dashed border-[var(--gty-border)] rounded-xl">
            <p className="text-xs text-[var(--gty-text-faint)]">Badge preview will appear here</p>
          </div>
        )}
      </div>

    </div>
  );
};

// ─── Sidebar nav item ─────────────────────────────────────────────────────────

// ─── TipTap Editor Styles ────────────────────────────────────────────────────

const TipTapStyles = () => (
  <style>{`
    .tiptap-editor .ProseMirror {
      outline: none;
      min-height: 300px;
      padding: 1rem;
      color: #e4e4e7;
      font-size: 0.875rem;
      line-height: 1.7;
    }
    .tiptap-editor .ProseMirror p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: #52525b;
      pointer-events: none;
      height: 0;
    }
    .tiptap-editor .ProseMirror h1 { font-size: 1.5rem; font-weight: 900; margin: 1rem 0 0.5rem; color: #fff; }
    .tiptap-editor .ProseMirror h2 { font-size: 1.25rem; font-weight: 800; margin: 0.75rem 0 0.4rem; color: #fff; }
    .tiptap-editor .ProseMirror h3 { font-size: 1.1rem; font-weight: 700; margin: 0.5rem 0 0.3rem; color: #d4d4d8; }
    .tiptap-editor .ProseMirror ul { list-style: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
    .tiptap-editor .ProseMirror ol { list-style: decimal; padding-left: 1.5rem; margin: 0.5rem 0; }
    .tiptap-editor .ProseMirror li { margin: 0.2rem 0; }
    .tiptap-editor .ProseMirror blockquote { border-left: 3px solid var(--gty-accent, #a855f7); padding-left: 1rem; margin: 0.75rem 0; color: #a1a1aa; font-style: italic; }
    .tiptap-editor .ProseMirror pre { background: #18181b; border: 1px solid #3f3f46; border-radius: 6px; padding: 0.75rem 1rem; margin: 0.5rem 0; overflow-x: auto; }
    .tiptap-editor .ProseMirror pre code { font-family: monospace; font-size: 0.8rem; color: #a78bfa; background: none; padding: 0; border-radius: 0; }
    .tiptap-editor .ProseMirror code { font-family: monospace; font-size: 0.8rem; background: #27272a; color: #a78bfa; padding: 0.1rem 0.35rem; border-radius: 3px; }
    .tiptap-editor .ProseMirror mark { background: #7c3aed33; color: #c4b5fd; border-radius: 2px; padding: 0 2px; }
    .tiptap-editor .ProseMirror hr { border: none; border-top: 2px solid #3f3f46; margin: 1rem 0; }
    .tiptap-editor .ProseMirror img { max-width: 100%; border-radius: 6px; margin: 0.5rem 0; border: 1px solid #3f3f46; }
    .tiptap-editor .ProseMirror strong { color: #fff; font-weight: 700; }
    .tiptap-editor .ProseMirror em { color: #c4b5fd; }
    .tiptap-editor .ProseMirror s { opacity: 0.5; }

    .tutorial-content h1 { font-size: 1.5rem; font-weight: 900; margin: 1rem 0 0.5rem; color: #fff; }
    .tutorial-content h2 { font-size: 1.25rem; font-weight: 800; margin: 0.75rem 0 0.4rem; color: #fff; border-bottom: 1px solid #3f3f46; padding-bottom: 0.25rem; }
    .tutorial-content h3 { font-size: 1.05rem; font-weight: 700; margin: 0.5rem 0 0.3rem; color: #d4d4d8; }
    .tutorial-content p { margin: 0.4rem 0; color: #d4d4d8; font-size: 0.875rem; line-height: 1.7; }
    .tutorial-content ul { list-style: disc; padding-left: 1.5rem; margin: 0.5rem 0; color: #d4d4d8; font-size: 0.875rem; }
    .tutorial-content ol { list-style: decimal; padding-left: 1.5rem; margin: 0.5rem 0; color: #d4d4d8; font-size: 0.875rem; }
    .tutorial-content li { margin: 0.2rem 0; }
    .tutorial-content blockquote { border-left: 3px solid var(--gty-accent, #a855f7); padding-left: 1rem; margin: 0.75rem 0; color: #a1a1aa; font-style: italic; }
    .tutorial-content pre { background: #18181b; border: 1px solid #3f3f46; border-radius: 6px; padding: 0.75rem 1rem; margin: 0.5rem 0; overflow-x: auto; }
    .tutorial-content pre code { font-family: monospace; font-size: 0.8rem; color: #a78bfa; background: none; padding: 0; border-radius: 0; }
    .tutorial-content code { font-family: monospace; font-size: 0.8rem; background: #27272a; color: #a78bfa; padding: 0.1rem 0.35rem; border-radius: 3px; }
    .tutorial-content mark { background: #7c3aed33; color: #c4b5fd; border-radius: 2px; padding: 0 2px; }
    .tutorial-content hr { border: none; border-top: 2px solid #3f3f46; margin: 1rem 0; }
    .tutorial-content img { max-width: 100%; border-radius: 6px; margin: 0.5rem 0; border: 1px solid #3f3f46; }
    .tutorial-content strong { color: #fff; font-weight: 700; }
    .tutorial-content em { color: #c4b5fd; }
    .tutorial-content s { opacity: 0.5; }
  `}</style>
);

const ToolbarBtn = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onMouseDown={e => { e.preventDefault(); onClick(); }}
    disabled={disabled}
    title={title}
    className={`px-2 py-1.5 rounded text-xs transition-colors ${
      active
        ? 'bg-[var(--gty-accent-dim)] text-white'
        : 'text-[var(--gty-text-muted)] hover:bg-[var(--gty-bg-hover)] hover:text-white'
    } disabled:opacity-30 disabled:cursor-not-allowed`}
  >
    {children}
  </button>
);

// ─── Tutorials ────────────────────────────────────────────────────────────────

const EMOJI_CATEGORIES = [
  { label: 'Smileys', emojis: ['😀','😁','😂','🤣','😊','😎','😍','🥳','😅','😆','😇','🙂','😉','😋','😜','🤔','😏','😒','😞','😔','😟','😕','🙁','😣','😖','😫','😩','🥺','😢','😭','😤','😠','😡','🤬','🤯','😱','🤗','🤭','🫡','🫠','🫥'] },
  { label: 'Gestures', emojis: ['👍','👎','👌','🤌','✌️','🤞','🤟','🤘','🤙','👋','🙌','👏','🤝','🙏','✊','👊','🫶','💪','🦾','🖐️','☝️','👆','👇','👈','👉','🫵'] },
  { label: 'Objects', emojis: ['🚛','🚚','🏗️','⚙️','🔧','🔨','🛠️','📦','📋','📝','✅','❌','⚠️','🚨','💡','🔑','🏆','🎯','📢','📣','💰','💵','🧰','⛽','🛞','🔩'] },
  { label: 'Symbols', emojis: ['✅','❌','⚠️','🔴','🟠','🟡','🟢','🔵','🟣','⬛','⬜','🟥','🟧','🟨','🟩','🟦','🟪','❗','❓','💯','🔔','🔕','📌','📍','🏁','🚩','🎌','🏴'] },
];

const EmojiPicker = ({ onSelect }) => {
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onMouseDown={e => { e.preventDefault(); setOpen(p => !p); }}
        title="Emoji"
        className="px-2 py-1.5 rounded text-xs text-[var(--gty-text-muted)] hover:bg-[var(--gty-bg-hover)] hover:text-white transition-colors"
      >
        😊
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-1 z-50 w-64 bg-[var(--gty-bg-card)] border border-[var(--gty-border)] rounded-lg shadow-xl">
          <div className="flex border-b border-[var(--gty-border)]">
            {EMOJI_CATEGORIES.map((c, i) => (
              <button key={i} type="button" onClick={() => setCat(i)}
                className={`flex-1 py-1.5 text-xs font-bold transition-colors ${cat === i ? 'text-[var(--gty-accent)] border-b-2 border-[var(--gty-accent)] -mb-px' : 'text-[var(--gty-text-faint)] hover:text-white'}`}>
                {c.label}
              </button>
            ))}
          </div>
          <div className="p-2 grid grid-cols-8 gap-0.5 max-h-36 overflow-y-auto">
            {EMOJI_CATEGORIES[cat].emojis.map((em, i) => (
              <button key={i} type="button"
                onMouseDown={e => { e.preventDefault(); onSelect(em); setOpen(false); }}
                className="text-lg p-1 rounded hover:bg-[var(--gty-bg-hover)] transition-colors leading-none"
              >
                {em}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AUDIENCE_OPTIONS = [
  { value: 'all',             label: 'All',             color: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  { value: 'management',      label: 'Management',      color: 'bg-red-500/15 text-red-300 border-red-500/30' },
  { value: 'driver',          label: 'Driver',          color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  { value: 'mechanic',        label: 'Mechanic',        color: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  { value: 'senior_operator', label: 'Senior Operator', color: 'bg-green-500/15 text-green-300 border-green-500/30' },
];

const AudienceBadge = ({ audience }) => {
  const opt = AUDIENCE_OPTIONS.find(o => o.value === (audience || 'all')) || AUDIENCE_OPTIONS[0];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${opt.color}`}>
      {opt.label}
    </span>
  );
};

const SortableTutorialCard = ({ t, isManagement, expanded, onToggle, onEdit, onDelete, formatDate }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: t.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="brutalist-card overflow-hidden">
      <div className="flex items-stretch">
        {/* Drag handle — management only */}
        <div
          {...attributes}
          {...listeners}
          className="flex items-center px-2 cursor-grab active:cursor-grabbing text-[var(--gty-text-faint)] hover:text-[var(--gty-text-secondary)] border-r border-[var(--gty-border)] shrink-0"
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-between px-4 py-4 text-left hover:bg-[var(--gty-bg-hover)] transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0">
              <BookMarked className="w-4 h-4 text-[var(--gty-accent)] shrink-0" />
              <span className="font-bold text-sm truncate">{t.title}</span>
              <AudienceBadge audience={t.audience} />
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-xs text-[var(--gty-text-faint)]">{formatDate(t.created_at)}</span>
              <ChevronDown className={`w-4 h-4 text-[var(--gty-text-faint)] transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`} />
            </div>
          </button>
          {expanded && (
            <div className="border-t border-[var(--gty-border)]">
              <div className="tutorial-content px-4 py-5" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t.content || '') }} />
              {isManagement && (
                <div className="flex gap-3 px-4 pb-4 pt-2 border-t border-[var(--gty-border)]">
                  <button onClick={onEdit} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase bg-[var(--gty-bg-card)] hover:bg-[var(--gty-bg-hover)] rounded transition-colors">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={onDelete} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TutorialsContent = ({ isManagement }) => {
  const [tutorials,  setTutorials]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [editId,     setEditId]     = useState(null);
  const [showForm,   setShowForm]   = useState(false);
  const [titleInput, setTitleInput] = useState('');
  const [audience,   setAudience]   = useState('all');
  const [saving,     setSaving]     = useState(false);
  const [expanded,   setExpanded]   = useState({});
  const imageInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder: 'Write the tutorial content here...' }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight,
    ],
    editorProps: {
      attributes: { class: 'focus:outline-none' },
    },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await tutorialsAPI.getAll(); setTutorials(r.data); }
    catch { toast.error('Failed to load tutorials'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditId(null);
    setTitleInput('');
    setAudience('all');
    editor?.commands.setContent('');
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setTitleInput(t.title);
    setAudience(t.audience || 'all');
    editor?.commands.setContent(t.content || '');
    setShowForm(true);
  };

  const cancel = () => { setShowForm(false); setEditId(null); };

  const handleSave = async () => {
    if (!titleInput.trim()) { toast.error('Title is required'); return; }
    const html = editor?.getHTML() || '';
    if (!html || html === '<p></p>') { toast.error('Content cannot be empty'); return; }
    setSaving(true);
    try {
      if (editId) {
        await tutorialsAPI.update(editId, { title: titleInput.trim(), content: html, audience });
        toast.success('Tutorial updated');
      } else {
        await tutorialsAPI.create({ title: titleInput.trim(), content: html, audience });
        toast.success('Tutorial created');
      }
      cancel();
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tutorial?')) return;
    try { await tutorialsAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    try {
      const r = await tutorialsAPI.uploadImage(file);
      editor?.chain().focus().setImage({ src: r.data.url }).run();
    } catch { toast.error('Image upload failed'); }
  };

  const toggleExpand = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tutorials.findIndex(t => t.id === active.id);
    const newIndex = tutorials.findIndex(t => t.id === over.id);
    const newOrder = arrayMove(tutorials, oldIndex, newIndex);
    setTutorials(newOrder);
    try {
      await tutorialsAPI.reorder(newOrder.map(t => t.id));
    } catch { toast.error('Failed to save order'); load(); }
  };

  return (
    <div className="space-y-6">
      <TipTapStyles />

      {isManagement && !showForm && (
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--gty-accent-dim)] hover:opacity-80 text-sm font-bold uppercase rounded transition-opacity"
        >
          <Plus className="w-4 h-4" /> New Tutorial
        </button>
      )}

      {isManagement && showForm && (
        <div className="brutalist-card overflow-hidden">
          {/* Title + Audience */}
          <div className="px-4 pt-4 pb-3 border-b border-[var(--gty-border)] space-y-3">
            <input
              type="text"
              placeholder="Tutorial title..."
              value={titleInput}
              onChange={e => setTitleInput(e.target.value)}
              className="w-full bg-transparent text-white text-lg font-bold placeholder-[var(--gty-text-faint)] focus:outline-none"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-faint)]">Visible to:</span>
              {AUDIENCE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAudience(opt.value)}
                  className={`px-2.5 py-1 rounded text-[11px] font-bold uppercase tracking-wider border transition-all ${
                    audience === opt.value
                      ? opt.color + ' opacity-100'
                      : 'bg-transparent border-[var(--gty-border)] text-[var(--gty-text-faint)] opacity-50 hover:opacity-75'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Toolbar */}
          {editor && (
            <div className="flex flex-wrap items-center gap-0.5 px-3 py-2 border-b border-[var(--gty-border)] bg-[var(--gty-bg-card)]">
              {/* History */}
              <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><Undo2 className="w-3.5 h-3.5" /></ToolbarBtn>
              <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><Redo2 className="w-3.5 h-3.5" /></ToolbarBtn>
              <span className="w-px h-5 bg-[var(--gty-border)] mx-1" />

              {/* Text style */}
              <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><BoldIcon className="w-3.5 h-3.5" /></ToolbarBtn>
              <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic"><ItalicIcon className="w-3.5 h-3.5" /></ToolbarBtn>
              <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough"><Strikethrough className="w-3.5 h-3.5" /></ToolbarBtn>
              <ToolbarBtn onClick={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive('highlight')} title="Highlight"><Highlighter className="w-3.5 h-3.5" /></ToolbarBtn>
              <ToolbarBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline Code"><Code2 className="w-3.5 h-3.5" /></ToolbarBtn>
              <span className="w-px h-5 bg-[var(--gty-border)] mx-1" />

              {/* Headings */}
              <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1"><span className="font-black text-xs">H1</span></ToolbarBtn>
              <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2"><span className="font-black text-xs">H2</span></ToolbarBtn>
              <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3"><span className="font-black text-xs">H3</span></ToolbarBtn>
              <span className="w-px h-5 bg-[var(--gty-border)] mx-1" />

              {/* Lists & blocks */}
              <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List"><List className="w-3.5 h-3.5" /></ToolbarBtn>
              <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered List"><ListOrdered className="w-3.5 h-3.5" /></ToolbarBtn>
              <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote"><Quote className="w-3.5 h-3.5" /></ToolbarBtn>
              <ToolbarBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code Block"><span className="font-mono text-xs font-bold">{`</>`}</span></ToolbarBtn>
              <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider"><Minus className="w-3.5 h-3.5" /></ToolbarBtn>
              <span className="w-px h-5 bg-[var(--gty-border)] mx-1" />

              {/* Align */}
              <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align Left"><AlignLeft className="w-3.5 h-3.5" /></ToolbarBtn>
              <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align Center"><AlignCenter className="w-3.5 h-3.5" /></ToolbarBtn>
              <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align Right"><AlignRight className="w-3.5 h-3.5" /></ToolbarBtn>
              <span className="w-px h-5 bg-[var(--gty-border)] mx-1" />

              {/* Image */}
              <ToolbarBtn onClick={() => imageInputRef.current?.click()} title="Insert Image">
                <ImageIcon className="w-3.5 h-3.5" />
              </ToolbarBtn>
              <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={e => { handleImageUpload(e.target.files?.[0]); e.target.value = ''; }} />

              {/* Emoji */}
              <EmojiPicker onSelect={em => editor?.chain().focus().insertContent(em).run()} />
            </div>
          )}

          {/* Editor */}
          <div className="tiptap-editor bg-[var(--gty-bg-base)]">
            <EditorContent editor={editor} />
          </div>

          {/* Actions */}
          <div className="flex gap-3 px-4 py-3 border-t border-[var(--gty-border)] bg-[var(--gty-bg-card)]">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-[var(--gty-accent-dim)] hover:opacity-80 text-sm font-bold uppercase rounded disabled:opacity-50 transition-opacity"
            >
              {saving ? 'Saving...' : editId ? 'Save Changes' : 'Create Tutorial'}
            </button>
            <button
              onClick={cancel}
              className="px-6 py-2 bg-[var(--gty-bg-card)] hover:bg-[var(--gty-bg-hover)] border border-[var(--gty-border)] text-sm font-bold uppercase rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-[var(--gty-text-muted)] text-xs uppercase">Loading...</div>
      ) : tutorials.length === 0 ? (
        <p className="text-[var(--gty-text-secondary)] text-sm">No tutorials yet.</p>
      ) : isManagement ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={tutorials.map(t => t.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {tutorials.map(t => (
                <SortableTutorialCard
                  key={t.id}
                  t={t}
                  isManagement={isManagement}
                  expanded={expanded[t.id]}
                  onToggle={() => toggleExpand(t.id)}
                  onEdit={() => openEdit(t)}
                  onDelete={() => handleDelete(t.id)}
                  formatDate={formatDate}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="space-y-4">
          {tutorials.map(t => (
            <div key={t.id} className="brutalist-card overflow-hidden">
              <button
                onClick={() => toggleExpand(t.id)}
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-[var(--gty-bg-hover)] transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <BookMarked className="w-4 h-4 text-[var(--gty-accent)] shrink-0" />
                  <span className="font-bold text-sm truncate">{t.title}</span>
                  <AudienceBadge audience={t.audience} />
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-[var(--gty-text-faint)]">{formatDate(t.created_at)}</span>
                  <ChevronDown className={`w-4 h-4 text-[var(--gty-text-faint)] transition-transform duration-200 ${expanded[t.id] ? '' : '-rotate-90'}`} />
                </div>
              </button>
              {expanded[t.id] && (
                <div className="border-t border-[var(--gty-border)]">
                  <div className="tutorial-content px-6 py-5" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t.content || '') }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const NavItem = ({ item: { key, label, icon: Icon }, active, onClick, badge = 0 }) => (
  <button onClick={() => onClick(key)}
    className={`flex items-center gap-3 w-full px-3 py-2 rounded text-sm transition-colors text-left
      ${active ? 'bg-[var(--gty-bg-hover)] text-white font-semibold' : 'text-[var(--gty-text-muted)] hover:bg-[var(--gty-bg-card)] hover:text-white font-medium'}`}>
    <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-[var(--gty-accent)]' : ''}`} strokeWidth={2} />
    <span className="truncate flex-1">{label}</span>
    {badge > 0 && (
      <span className="ml-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white px-1 shrink-0">
        {badge > 99 ? '99+' : badge}
      </span>
    )}
  </button>
);

const SidebarGroup = ({ label, children }) => (
  <div>
    <p className="px-3 mb-1 text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-faint)]">{label}</p>
    <div className="space-y-0.5">{children}</div>
  </div>
);

const CollapsibleSidebarGroup = ({ label, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        onClick={() => setOpen(p => !p)}
        className="flex items-center justify-between w-full px-3 mb-1 group"
      >
        <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--gty-text-faint)]">{label}</p>
        <ChevronDown className={`w-3 h-3 text-[var(--gty-text-faint)] transition-transform duration-200 ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="space-y-0.5">{children}</div>}
    </div>
  );
};

// ─── LOA (Leave of Absence) ───────────────────────────────────────────────────

const LOA_TYPES_CONFIG = [
  { value: 'Personal',     label: 'Personal',     icon: '👤', desc: 'Personal matters' },
  { value: 'Vacation',     label: 'Vacation',     icon: '🌴', desc: 'Time off & travel' },
  { value: 'Lack of Time', label: 'Lack of Time', icon: '⏰', desc: 'Scheduling conflicts' },
];

const LOA_STATUS_COLORS = {
  pending:  { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20', label: 'Pending' },
  approved: { bg: 'bg-green-500/10',  text: 'text-green-400',  border: 'border-green-500/20',  label: 'Approved' },
  denied:   { bg: 'bg-red-500/10',    text: 'text-red-400',    border: 'border-red-500/20',    label: 'Denied' },
};

const LOAStatusBadge = ({ status }) => {
  const cfg = {
    pending:  { label: 'PENDING',  cls: 'border-amber-400/70 text-amber-400 bg-amber-400/5',  rot: '-rotate-1' },
    approved: { label: 'APPROVED', cls: 'border-green-400/70 text-green-400 bg-green-400/5',  rot: 'rotate-1'  },
    denied:   { label: 'DENIED',   cls: 'border-red-400/70   text-red-400   bg-red-400/5',    rot: '-rotate-2' },
  };
  const c = cfg[status] || cfg.pending;
  return (
    <span className={`inline-block px-2 py-0.5 border-2 text-[10px] font-black tracking-[0.15em] uppercase font-mono ${c.cls} ${c.rot}`}>
      {c.label}
    </span>
  );
};

const LOARequestContent = ({ user }) => {
  const [selectedType, setSelectedType] = useState('');
  const [startDate,    setStartDate]    = useState('');
  const [endDate,      setEndDate]      = useState('');
  const [reason,       setReason]       = useState('');
  const [file,         setFile]         = useState(null);
  const [submitting,   setSubmitting]   = useState(false);
  const [submitted,    setSubmitted]    = useState(false);
  const [history,      setHistory]      = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const fileRef = useRef(null);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await loaAPI.myRequests();
      setHistory(res.data || []);
    } catch {
      // silent
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedType) { toast.error('Please select an LOA type.'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('type', selectedType);
      if (startDate)  fd.append('start_date', startDate);
      if (endDate)    fd.append('end_date', endDate);
      if (reason)     fd.append('reason', reason);
      if (file)       fd.append('attachment', file);
      await loaAPI.submit(fd);
      setSubmitted(true);
      toast.success('LOA request submitted!');
      setSelectedType(''); setStartDate(''); setEndDate(''); setReason(''); setFile(null);
      await loadHistory();
      setTimeout(() => setSubmitted(false), 3000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit LOA request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-8">
      {/* Header — industrial dispatch style */}
      <div className="border-b-2 border-[var(--gty-border)] pb-4">
        <p className="text-[10px] font-mono text-[var(--gty-text-faint)] tracking-[0.3em] uppercase mb-1">GTY Fleet Management // Form LOA-01</p>
        <h2 className="text-3xl font-black tracking-tight uppercase">Leave of Absence</h2>
        <p className="text-[var(--gty-text-secondary)] text-sm mt-1 font-mono">Submit a formal leave request to management</p>
      </div>

      {/* Form card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
        className="border border-[var(--gty-border)] bg-[var(--gty-bg-card)] relative overflow-hidden"
      >
        {/* Corner decoration */}
        <div className="absolute top-0 right-0 w-8 h-8 border-b border-l border-[var(--gty-border)] bg-[var(--gty-bg-base)]" />

        {submitted ? (
          <motion.div
            className="flex flex-col items-center gap-4 py-16 px-6"
            initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 200 }}
          >
            <motion.div
              initial={{ rotate: -20, scale: 0.5 }} animate={{ rotate: 2, scale: 1 }} transition={{ type: 'spring', stiffness: 150, delay: 0.1 }}
              className="border-4 border-green-500 px-8 py-4 rotate-2"
            >
              <p className="text-2xl font-black tracking-[0.2em] text-green-400 font-mono">SUBMITTED</p>
            </motion.div>
            <p className="text-[var(--gty-text-secondary)] text-sm font-mono tracking-wide">Management will review your request shortly.</p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="divide-y divide-[var(--gty-border)]">

            {/* Type selector */}
            <div className="p-6 space-y-3">
              <p className="text-[10px] font-mono tracking-[0.25em] text-[var(--gty-text-faint)] uppercase">01 — LOA Type <span className="text-red-400">*</span></p>
              <div className="grid grid-cols-3 gap-3">
                {LOA_TYPES_CONFIG.map((t, idx) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setSelectedType(t.value)}
                    className={`relative flex flex-col items-center gap-2 p-5 border-2 transition-all duration-150 group
                      ${selectedType === t.value
                        ? 'border-[var(--gty-accent)] bg-[var(--gty-accent)]/10 shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)]'
                        : 'border-[var(--gty-border)] bg-[var(--gty-bg-base)] hover:border-[var(--gty-border2)]'
                      }`}
                  >
                    <span className="absolute top-1.5 left-2 text-[9px] font-mono text-[var(--gty-text-faint)] tracking-widest">
                      0{idx + 1}
                    </span>
                    <span className="text-3xl mt-1">{t.icon}</span>
                    <span className="text-[11px] font-black tracking-[0.12em] uppercase">{t.label}</span>
                    {selectedType === t.value && (
                      <motion.div layoutId="type-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--gty-accent)]" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Dates */}
            <div className="p-6 space-y-3">
              <p className="text-[10px] font-mono tracking-[0.25em] text-[var(--gty-text-faint)] uppercase">02 — Date Range <span className="font-normal">(optional)</span></p>
              <div className="grid grid-cols-2 gap-6">
                {[['Start Date', startDate, setStartDate], ['End Date', endDate, setEndDate]].map(([label, val, setter]) => (
                  <div key={label} className="space-y-1">
                    <label className="text-[9px] font-mono tracking-widest text-[var(--gty-text-faint)] uppercase">{label}</label>
                    <div className="relative">
                      <input
                        type="date"
                        value={val}
                        onChange={e => setter(e.target.value)}
                        className="w-full bg-transparent border-0 border-b-2 border-[var(--gty-border)] rounded-none px-0 py-1.5 text-sm font-mono focus:outline-none focus:border-[var(--gty-accent)] transition-colors"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reason */}
            <div className="p-6 space-y-3">
              <p className="text-[10px] font-mono tracking-[0.25em] text-[var(--gty-text-faint)] uppercase">03 — Reason <span className="font-normal">(optional)</span></p>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Briefly describe your reason..."
                rows={3}
                className="w-full bg-[var(--gty-bg-base)] border border-[var(--gty-border)] px-3 py-2.5 text-sm font-mono resize-none focus:outline-none focus:border-[var(--gty-accent)] transition-colors placeholder:text-[var(--gty-text-faint)]"
              />
            </div>

            {/* Attachment */}
            <div className="p-6 space-y-3">
              <p className="text-[10px] font-mono tracking-[0.25em] text-[var(--gty-text-faint)] uppercase">04 — Attachment <span className="font-normal">(optional)</span></p>
              <div
                className="flex items-center gap-3 border border-dashed border-[var(--gty-border)] px-4 py-3 cursor-pointer hover:border-[var(--gty-accent)]/60 transition-colors font-mono"
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="w-4 h-4 text-[var(--gty-text-secondary)] shrink-0" />
                <span className="text-xs text-[var(--gty-text-secondary)] truncate font-mono">
                  {file ? file.name : '[ click to attach file ]'}
                </span>
                {file && (
                  <button type="button" onClick={e => { e.stopPropagation(); setFile(null); }} className="ml-auto shrink-0 text-[var(--gty-text-secondary)] hover:text-red-400">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
            </div>

            {/* Submit */}
            <div className="p-6">
              <button
                type="submit"
                disabled={submitting || !selectedType}
                className="w-full py-3 bg-[var(--gty-accent)] text-white font-black text-xs tracking-[0.25em] uppercase disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity font-mono relative overflow-hidden"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : 'Submit Request →'}
              </button>
            </div>
          </form>
        )}
      </motion.div>

      {/* History */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono tracking-[0.3em] text-[var(--gty-text-faint)] uppercase">Request History</span>
          <div className="flex-1 h-px bg-[var(--gty-border)]" />
        </div>
        {loadingHistory ? (
          <div className="py-8 text-center font-mono text-xs text-[var(--gty-text-faint)] tracking-widest">LOADING...</div>
        ) : history.length === 0 ? (
          <div className="border border-dashed border-[var(--gty-border)] py-8 text-center font-mono text-xs text-[var(--gty-text-faint)] tracking-widest">NO RECORDS FOUND</div>
        ) : (
          <AnimatePresence>
            {history.map((r, i) => {
              const typeConf = LOA_TYPES_CONFIG.find(t => t.value === r.type) || {};
              const stripeColor = r.status === 'approved' ? 'bg-green-500' : r.status === 'denied' ? 'bg-red-500' : 'bg-amber-500';
              return (
                <motion.div
                  key={r.id}
                  className="flex border border-[var(--gty-border)] bg-[var(--gty-bg-card)] overflow-hidden"
                  initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                >
                  <div className={`w-1 shrink-0 ${stripeColor}`} />
                  <div className="flex-1 p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span>{typeConf.icon}</span>
                        <div>
                          <p className="text-xs font-black tracking-wide uppercase">{r.type}</p>
                          <p className="text-[10px] font-mono text-[var(--gty-text-faint)]">LOA-#{String(r.id).padStart(4, '0')}</p>
                        </div>
                      </div>
                      <LOAStatusBadge status={r.status} />
                    </div>
                    {(r.start_date || r.end_date) && (
                      <p className="text-[11px] font-mono text-[var(--gty-text-secondary)]">
                        {r.start_date && `${r.start_date}`}{r.start_date && r.end_date && ' → '}{r.end_date && `${r.end_date}`}
                      </p>
                    )}
                    {r.reason && <p className="text-xs text-[var(--gty-text-secondary)] italic font-mono">"{r.reason}"</p>}
                    {r.deny_reason && <p className="text-xs text-red-400 font-mono">↳ {r.deny_reason}</p>}
                    <p className="text-[10px] font-mono text-[var(--gty-text-faint)]">
                      Filed {new Date(r.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {r.reviewed_by && ` · Reviewed by ${r.reviewed_by}`}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

const LOAManagementContent = () => {
  const [requests,   setRequests]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState('all');
  const [denyTarget, setDenyTarget] = useState(null); // { id, name }
  const [denyReason, setDenyReason] = useState('');
  const [processing, setProcessing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await loaAPI.list();
      setRequests(res.data || []);
    } catch {
      toast.error('Failed to load LOA requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      await loaAPI.approve(id);
      toast.success('LOA approved.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to approve.');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeny = async () => {
    if (!denyTarget) return;
    setProcessing(denyTarget.id);
    try {
      await loaAPI.deny(denyTarget.id, denyReason);
      toast.success('LOA denied.');
      setDenyTarget(null); setDenyReason('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to deny.');
    } finally {
      setProcessing(null);
    }
  };

  const handleReturn = async (id) => {
    setProcessing(id);
    try {
      await loaAPI.markReturned(id);
      toast.success('Driver marked as returned. Payment cycle exempt until this Friday.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark as returned.');
    } finally {
      setProcessing(null);
    }
  };

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const counts   = {
    all:      requests.length,
    pending:  requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    returned: requests.filter(r => r.status === 'returned').length,
    denied:   requests.filter(r => r.status === 'denied').length,
  };

  return (
    <div className="max-w-3xl space-y-6">
      {/* Header */}
      <div className="border-b-2 border-[var(--gty-border)] pb-4 flex items-end justify-between">
        <div>
          <p className="text-[10px] font-mono text-[var(--gty-text-faint)] tracking-[0.3em] uppercase mb-1">GTY Fleet Management // LOA Review Panel</p>
          <h2 className="text-3xl font-black tracking-tight uppercase">LOA Requests</h2>
        </div>
        <button onClick={load} className="p-2 border border-[var(--gty-border)] hover:border-[var(--gty-border2)] transition-colors" title="Refresh">
          <RefreshCw className="w-4 h-4 text-[var(--gty-text-secondary)]" />
        </button>
      </div>

      {/* Filter tabs — ticket-tab style */}
      <div className="flex gap-0 border-b border-[var(--gty-border)]">
        {['all', 'pending', 'approved', 'returned', 'denied'].map(f => {
          const dotColor = f === 'pending' ? 'bg-amber-400' : f === 'approved' ? 'bg-green-400' : f === 'returned' ? 'bg-purple-400' : f === 'denied' ? 'bg-red-400' : 'bg-[var(--gty-text-faint)]';
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`flex items-center gap-2 px-5 py-2.5 text-[11px] font-mono font-bold tracking-[0.15em] uppercase border-b-2 transition-all -mb-px
                ${filter === f ? 'border-[var(--gty-accent)] text-white' : 'border-transparent text-[var(--gty-text-secondary)] hover:text-white'}`}
            >
              {f !== 'all' && <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />}
              {f} {counts[f] > 0 && <span className="opacity-50">({counts[f]})</span>}
            </button>
          );
        })}
      </div>

      {/* Deny modal */}
      <AnimatePresence>
        {denyTarget && (
          <motion.div
            className="border border-red-500/40 bg-red-500/5 p-5 space-y-3"
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
          >
            <p className="text-xs font-mono tracking-widest text-red-400 uppercase">
              ↓ Deny Request — <span className="text-white">{denyTarget.name}</span>
            </p>
            <textarea
              placeholder="Reason for denial (optional)..."
              value={denyReason}
              onChange={e => setDenyReason(e.target.value)}
              rows={2}
              className="w-full bg-transparent border border-red-500/30 px-3 py-2 text-sm font-mono resize-none focus:outline-none focus:border-red-400 transition-colors placeholder:text-[var(--gty-text-faint)]"
            />
            <div className="flex gap-2">
              <button onClick={handleDeny} disabled={!!processing} className="px-5 py-1.5 bg-red-500 text-white text-xs font-black tracking-[0.15em] uppercase font-mono hover:bg-red-600 disabled:opacity-50 transition-colors">
                Confirm Deny
              </button>
              <button onClick={() => { setDenyTarget(null); setDenyReason(''); }} className="px-4 py-1.5 border border-[var(--gty-border)] text-xs font-mono hover:bg-[var(--gty-bg-card)] transition-colors">
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Request list */}
      {loading ? (
        <div className="py-16 text-center font-mono text-xs text-[var(--gty-text-faint)] tracking-[0.3em]">LOADING RECORDS...</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[var(--gty-border)] py-12 text-center font-mono text-xs text-[var(--gty-text-faint)] tracking-widest">
          NO {filter.toUpperCase()} REQUESTS FOUND
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {filtered.map((r, i) => {
              const typeConf = LOA_TYPES_CONFIG.find(t => t.value === r.type) || {};
              const isPending = r.status === 'pending';
              const stripeColor = r.status === 'approved' ? 'bg-green-500' : r.status === 'denied' ? 'bg-red-500' : 'bg-amber-400';
              return (
                <motion.div
                  key={r.id}
                  className={`flex border border-[var(--gty-border)] bg-[var(--gty-bg-card)] overflow-hidden ${isPending ? 'border-amber-500/20' : ''}`}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }} transition={{ delay: i * 0.04 }}
                >
                  <div className={`w-1 shrink-0 ${stripeColor} ${isPending ? 'animate-pulse' : ''}`} />
                  <div className="flex-1 p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2.5">
                          <span className="text-xl">{typeConf.icon}</span>
                          <div>
                            <p className="font-black tracking-wide text-sm uppercase">{r.driver_name}</p>
                            <p className="text-[10px] font-mono text-[var(--gty-text-faint)]">
                              LOA-#{String(r.id).padStart(4, '0')} · {r.type}
                            </p>
                          </div>
                          <LOAStatusBadge status={r.status} />
                        </div>
                        {(r.start_date || r.end_date) && (
                          <p className="text-xs font-mono text-[var(--gty-text-secondary)] pl-9">
                            <Clock className="w-3 h-3 inline mr-1 -mt-0.5" />
                            {r.start_date && r.start_date}{r.start_date && r.end_date && ' → '}{r.end_date && r.end_date}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0 flex-wrap">
                        {isPending && (<>
                          <button
                            onClick={() => handleApprove(r.id)}
                            disabled={!!processing}
                            className="flex items-center gap-1.5 px-4 py-1.5 border-2 border-green-500/40 text-green-400 text-xs font-mono font-bold tracking-widest hover:bg-green-500/10 disabled:opacity-50 transition-colors uppercase"
                          >
                            <CheckCircle className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button
                            onClick={() => { setDenyTarget({ id: r.id, name: r.driver_name }); setDenyReason(''); }}
                            disabled={!!processing}
                            className="flex items-center gap-1.5 px-4 py-1.5 border-2 border-red-500/40 text-red-400 text-xs font-mono font-bold tracking-widest hover:bg-red-500/10 disabled:opacity-50 transition-colors uppercase"
                          >
                            <XCircle className="w-3.5 h-3.5" /> Deny
                          </button>
                        </>)}
                        {r.status === 'approved' && (
                          <button
                            onClick={() => handleReturn(r.id)}
                            disabled={!!processing}
                            className="flex items-center gap-1.5 px-4 py-1.5 border-2 border-purple-500/40 text-purple-400 text-xs font-mono font-bold tracking-widest hover:bg-purple-500/10 disabled:opacity-50 transition-colors uppercase"
                          >
                            <CheckCircle className="w-3.5 h-3.5" /> Mark Returned
                          </button>
                        )}
                      </div>
                    </div>
                    {r.reason && (
                      <p className="mt-3 text-xs font-mono text-[var(--gty-text-secondary)] border-l-2 border-[var(--gty-border)] pl-3 italic">"{r.reason}"</p>
                    )}
                    {r.deny_reason && (
                      <p className="mt-2 text-xs font-mono text-red-400 border-l-2 border-red-500/40 pl-3">Denial reason: {r.deny_reason}</p>
                    )}
                    <p className="mt-3 text-[10px] font-mono text-[var(--gty-text-faint)]">
                      Filed {new Date(r.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {r.reviewed_by && ` · Reviewed by ${r.reviewed_by}`}
                      {r.reviewed_at && ` on ${new Date(r.reviewed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

// ─── CDL Management ───────────────────────────────────────────────────────────

const CDL_STATUS_META = {
  pending:   { label: 'Pending',   color: 'text-yellow-400', border: 'border-yellow-500/30', bg: 'bg-yellow-500/10' },
  scheduled: { label: 'Scheduled', color: 'text-green-400',  border: 'border-green-500/30',  bg: 'bg-green-500/10' },
  completed: { label: 'Completed', color: 'text-blue-400',   border: 'border-blue-500/30',   bg: 'bg-blue-500/10' },
  closed:    { label: 'Closed',    color: 'text-zinc-400',   border: 'border-zinc-600/30',   bg: 'bg-zinc-700/10' },
};

const CdlManagementContent = () => {
  const [requests, setRequests]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState('all');
  const [expanded, setExpanded]       = useState(null);
  const [noteInput, setNoteInput]     = useState({});
  const [updating, setUpdating]       = useState(null);

  useEffect(() => {
    cdlAPI.list()
      .then(r => setRequests(r.data.requests || []))
      .catch(() => toast.error('Failed to load CDL requests.'))
      .finally(() => setLoading(false));
  }, []);

  const handleStatus = async (id, status) => {
    const note = noteInput[id] || '';
    setUpdating(id);
    try {
      const r = await cdlAPI.setStatus(id, status, note);
      setRequests(prev => prev.map(req => req.id === id ? r.data.request : req));
      if (status === 'scheduled') toast.success('Marked as scheduled. DM sent to driver.');
      else toast.success(`Status updated to ${status}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status.');
    } finally {
      setUpdating(null);
    }
  };

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const fmtDate  = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <motion.div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full"
        animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }} />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center">
        {['all', 'pending', 'scheduled', 'completed', 'closed'].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 border transition-all ${
              filter === s
                ? 'border-purple-500 text-purple-400 bg-purple-500/10'
                : 'border-[var(--gty-border)] text-[var(--gty-text-secondary)] hover:border-purple-500/50'
            }`}
          >
            {s === 'all' ? `All (${requests.length})` : `${s} (${requests.filter(r => r.status === s).length})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="brutalist-card p-10 text-center text-[var(--gty-text-faint)] text-sm">
          No CDL requests found.
        </div>
      )}

      <AnimatePresence>
        {filtered.map(req => {
          const meta = CDL_STATUS_META[req.status] || CDL_STATUS_META.pending;
          const isOpen = expanded === req.id;
          return (
            <motion.div
              key={req.id}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="brutalist-card overflow-hidden"
            >
              {/* Header row */}
              <button
                onClick={() => setExpanded(isOpen ? null : req.id)}
                className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-white/3 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 border ${meta.color} ${meta.border} ${meta.bg} shrink-0`}>
                    {meta.label}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white truncate">{req.ic_name}</p>
                    <p className="text-[10px] text-[var(--gty-text-secondary)] font-mono">{req.discord_name} · {fmtDate(req.created_at)}</p>
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-[var(--gty-text-faint)] shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22 }}
                    className="overflow-hidden border-t border-[var(--gty-border)]"
                  >
                    <div className="p-4 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-[9px] font-black uppercase tracking-widest text-[var(--gty-text-faint)] mb-0.5">IC Phone</p>
                          <p className="font-mono text-white">{req.ic_phone}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-black uppercase tracking-widest text-[var(--gty-text-faint)] mb-0.5">Driver Name</p>
                          <p className="font-mono text-white">{req.driver_name}</p>
                        </div>
                        <div className="md:col-span-2">
                          <p className="text-[9px] font-black uppercase tracking-widest text-[var(--gty-text-faint)] mb-0.5">Availability</p>
                          <p className="text-sm font-mono text-[var(--gty-text-secondary)] whitespace-pre-wrap">{req.availability}</p>
                        </div>
                        {req.preferred_date && (
                          <div>
                            <p className="text-[9px] font-black uppercase tracking-widest text-[var(--gty-text-faint)] mb-0.5">Preferred Date</p>
                            <p className="font-mono text-white">{req.preferred_date}</p>
                          </div>
                        )}
                        {req.notes && (
                          <div className="md:col-span-2">
                            <p className="text-[9px] font-black uppercase tracking-widest text-[var(--gty-text-faint)] mb-0.5">Notes</p>
                            <p className="text-sm font-mono text-[var(--gty-text-secondary)] whitespace-pre-wrap">{req.notes}</p>
                          </div>
                        )}
                      </div>

                      {/* Management note input */}
                      {req.status === 'pending' && (
                        <div>
                          <p className="text-[9px] font-black uppercase tracking-widest text-[var(--gty-text-faint)] mb-1">Management Note (optional)</p>
                          <textarea
                            rows={2}
                            className="w-full bg-[var(--gty-bg)] border border-[var(--gty-border)] text-sm text-white font-mono p-2 resize-none focus:outline-none focus:border-purple-500 transition-colors"
                            placeholder="Optional note visible to the driver..."
                            value={noteInput[req.id] || ''}
                            onChange={e => setNoteInput(p => ({ ...p, [req.id]: e.target.value }))}
                          />
                        </div>
                      )}

                      {/* Existing management note */}
                      {req.management_note && (
                        <div className="border border-purple-500/20 bg-purple-500/5 p-3">
                          <p className="text-[9px] font-black uppercase tracking-widest text-purple-400 mb-1">Management Note</p>
                          <p className="text-sm font-mono text-[var(--gty-text-secondary)] whitespace-pre-wrap">{req.management_note}</p>
                          {req.reviewed_by && (
                            <p className="text-[10px] text-[var(--gty-text-faint)] mt-2">— {req.reviewed_by} on {fmtDate(req.reviewed_at)}</p>
                          )}
                        </div>
                      )}

                      {/* Action buttons */}
                      {req.status !== 'completed' && req.status !== 'closed' && (
                        <div className="flex flex-wrap gap-2 pt-2">
                          {req.status === 'pending' && (
                            <button
                              onClick={() => handleStatus(req.id, 'scheduled')}
                              disabled={updating === req.id}
                              className="text-[10px] font-black uppercase tracking-widest px-4 py-2 bg-green-600 hover:bg-green-500 text-white transition-colors disabled:opacity-50"
                            >
                              {updating === req.id ? 'Saving…' : '✓ Mark Scheduled'}
                            </button>
                          )}
                          {req.status === 'scheduled' && (
                            <button
                              onClick={() => handleStatus(req.id, 'completed')}
                              disabled={updating === req.id}
                              className="text-[10px] font-black uppercase tracking-widest px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white transition-colors disabled:opacity-50"
                            >
                              {updating === req.id ? 'Saving…' : '✓ Mark Completed'}
                            </button>
                          )}
                          <button
                            onClick={() => handleStatus(req.id, 'closed')}
                            disabled={updating === req.id}
                            className="text-[10px] font-black uppercase tracking-widest px-4 py-2 border border-zinc-600 text-zinc-400 hover:border-zinc-400 hover:text-zinc-200 transition-colors disabled:opacity-50"
                          >
                            Close
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

// ─── Inventory ────────────────────────────────────────────────────────────────

const INVENTORY_CATEGORY_COLORS = [
  '#a855f7', '#22c55e', '#f59e0b', '#ef4444', '#3b82f6',
  '#ec4899', '#14b8a6', '#eab308', '#8b5cf6', '#64748b',
];
const INVENTORY_ICONS = {
  Package, Boxes, Truck, Wrench, FileText, Hash, Tag, Shield, BookOpen, ClipboardList,
};

const formatPrice = (n) => {
  if (n === null || n === undefined || n === '') return null;
  const num = Number(n);
  if (Number.isNaN(num)) return null;
  return '$' + num.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

// Horizontal segmented fuel-gauge bar — fills left-to-right.
// Color shifts: purple > 30%, amber 10–30%, red < 10%.
const CapacityGauge = ({ current, max, segments = 24, animate = true, compact = false }) => {
  const pct = max > 0 ? Math.min(1, Math.max(0, current / max)) : 0;
  const filled = Math.round(pct * segments);
  let color = 'var(--gty-accent, #a855f7)';
  if (pct < 0.10) color = '#ef4444';
  else if (pct < 0.30) color = '#f59e0b';
  const barHeight = compact ? 18 : 22;
  return (
    <div className="w-full">
      <div className="flex items-baseline justify-between mb-1.5 font-mono leading-none">
        <span className="text-white font-bold text-xs tabular-nums">{Math.round(pct * 100)}%</span>
        <span className="text-[10px] text-[var(--gty-text-faint)] tabular-nums">
          {current.toLocaleString()} / {max.toLocaleString()}
        </span>
      </div>
      <div
        className="relative flex gap-[2px] border border-[var(--gty-border)] bg-black/40 px-1 py-1 w-full"
        style={{ height: barHeight }}
      >
        {Array.from({ length: segments }).map((_, i) => {
          const isOn = i < filled;
          return (
            <motion.div
              key={i}
              initial={animate ? { scaleX: 0, opacity: 0 } : false}
              animate={{ scaleX: isOn ? 1 : 0.15, opacity: isOn ? 1 : 0.15 }}
              transition={{ delay: animate ? i * 0.014 : 0, duration: 0.18, ease: 'easeOut' }}
              style={{
                flex: 1,
                background: isOn ? color : 'transparent',
                borderTop: isOn ? `2px solid ${color}` : '1px dashed #3f3f46',
                transformOrigin: 'left',
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

// Drag handle wrapper for sortable cards.
const SortableInvCard = ({ id, disabled, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.85 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="relative">
      {!disabled && (
        <button
          {...attributes}
          {...listeners}
          className="absolute top-2 left-2 z-10 p-1 bg-black/60 border border-[var(--gty-border)] text-[var(--gty-text-faint)] hover:text-white cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
          title="Drag to reorder"
        >
          <GripVertical className="w-3 h-3" />
        </button>
      )}
      {children}
    </div>
  );
};

// Smart adjust helper — runs the API call, fires UNDO toast, returns the new qty
async function adjustWithUndo(item, delta, reason, { onAfter, isMgmt } = {}) {
  try {
    const r = await inventoryAPI.adjust(item.id, delta, reason || '');
    if (onAfter) onAfter(r.data);
    const verb = delta > 0 ? 'Added' : 'Removed';
    const sign = delta > 0 ? '+' : '−';
    toast.success(`${verb} ${Math.abs(delta)} · ${item.name}`, {
      description: `${item.quantity} ${sign} ${Math.abs(delta)} = ${item.quantity + delta}`,
      action: {
        label: 'UNDO',
        onClick: async () => {
          try {
            const undo = await inventoryAPI.adjust(item.id, -delta, 'undo');
            if (onAfter) onAfter(undo.data);
            toast.message('Reverted');
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Undo failed');
          }
        },
      },
      duration: 6000,
    });
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Adjustment failed');
    throw e;
  }
}

export const InventoryContent = ({ user, isManagement, canWrite }) => {
  const [items, setItems]               = useState([]);
  const [categories, setCategories]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [view, setView]                 = useState('item');     // 'item' | 'warehouse'
  const [layout, setLayout]             = useState('grid');     // 'grid' | 'list'
  const [activeCat, setActiveCat]       = useState('all');
  const [search, setSearch]             = useState('');
  const [showItemModal, setShowItemModal]     = useState(false);
  const [editingItem, setEditingItem]         = useState(null);
  const [showCatModal, setShowCatModal]       = useState(false);
  const [adjustItem, setAdjustItem]           = useState(null);
  const [showLog, setShowLog]                 = useState(false);
  const [detailItem, setDetailItem]           = useState(null);
  // Bulk
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected]     = useState(() => new Set());
  const [bulkBusy, setBulkBusy]     = useState(false);
  // Live polling
  const [lastSync, setLastSync]   = useState(Date.now());
  const [livePulse, setLivePulse] = useState(0);
  const [tickClock, setTickClock] = useState(0);  // re-renders the "Xs ago" label every second
  // Floater animations (transient {id, itemId, value} entries removed after ~1.4s)
  const [floaters, setFloaters] = useState([]);
  const floaterIdRef = useRef(0);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [itemsR, catsR] = await Promise.all([
        inventoryAPI.listItems(),
        inventoryAPI.listCategories(),
      ]);
      setItems(itemsR.data);
      setCategories(catsR.data);
      setLastSync(Date.now());
      setLivePulse(p => p + 1);
    } catch {
      if (!silent) toast.error('Failed to load inventory');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => { load(); }, [load]);

  // Live polling every 10s (silent)
  useEffect(() => {
    const id = setInterval(() => load(true), 10000);
    return () => clearInterval(id);
  }, [load]);

  // 1s tick for the "Xs ago" indicator
  useEffect(() => {
    const id = setInterval(() => setTickClock(c => c + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const secsSinceSync = Math.max(0, Math.floor((Date.now() - lastSync) / 1000));

  const visible = items
    .filter(i => i.kind === view)
    .filter(i => activeCat === 'all' ? true : (activeCat === 'none' ? !i.category_id : i.category_id === activeCat))
    .filter(i => !search.trim() || i.name.toLowerCase().includes(search.trim().toLowerCase()));

  const counts = {
    item:      items.filter(i => i.kind === 'item').length,
    warehouse: items.filter(i => i.kind === 'warehouse').length,
  };

  // Track allowed editing — for non-management, items in mgmt-only category are read-only
  const mgmtOnlyCatIds = new Set(categories.filter(c => c.mgmt_only).map(c => c.id));
  const itemWritable = (item) => {
    if (!canWrite) return false;
    if (isManagement) return true;
    return !mgmtOnlyCatIds.has(item.category_id);
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = visible.map(i => i.id);
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(visible, oldIdx, newIdx);
    const reorderedIds = reordered.map(i => i.id);
    setItems(prev => {
      const others = prev.filter(i => i.kind !== view);
      return [...others, ...reordered];
    });
    try {
      await inventoryAPI.reorder(view, reorderedIds);
    } catch {
      toast.error('Reorder failed');
      load();
    }
  };

  const openCreate = () => { setEditingItem(null); setShowItemModal(true); };
  const openEdit   = (item) => { setEditingItem(item); setShowItemModal(true); };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      await inventoryAPI.deleteItem(item.id);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // Patch a single item locally + spawn floater animation
  const updateLocalItem = (next, deltaForFloater = 0) => {
    setItems(prev => prev.map(i => i.id === next.id ? { ...i, ...next } : i));
    if (deltaForFloater) {
      const id = ++floaterIdRef.current;
      setFloaters(f => [...f, { id, itemId: next.id, value: deltaForFloater }]);
      setTimeout(() => setFloaters(f => f.filter(x => x.id !== id)), 1400);
    }
  };

  const handleQuickAdjust = async (item, delta) => {
    if (!itemWritable(item)) return;
    // Optimistic UI
    const newQty = Math.max(0, (item.max_capacity != null ? Math.min(item.max_capacity, item.quantity + delta) : item.quantity + delta));
    if (newQty === item.quantity) return;
    const realDelta = newQty - item.quantity;
    updateLocalItem({ ...item, quantity: newQty }, realDelta);
    try {
      await adjustWithUndo(item, realDelta, '', {
        onAfter: (saved) => updateLocalItem(saved),
        isMgmt: isManagement,
      });
    } catch {
      load(true);
    }
  };

  // Bulk
  const toggleSelect = (id) => {
    setSelected(s => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const selectAll = () => setSelected(new Set(visible.map(i => i.id)));
  const clearSelection = () => setSelected(new Set());
  const exitBulk = () => { setSelectMode(false); clearSelection(); };

  const runBulk = async (action, payload = {}) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await inventoryAPI.bulk({ ids: [...selected], action, ...payload });
      toast.success(`Bulk ${action} · ${r.data.affected} affected`);
      exitBulk();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div data-testid="inventory-page" className="inv-root">
      <InventoryStyles />

      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          {/* View toggle — Items vs Warehouses */}
          <div className="inv-toggle">
            <button
              onClick={() => setView('item')}
              className={`inv-toggle-btn ${view === 'item' ? 'active' : ''}`}
            >
              <Package className="w-3.5 h-3.5" />
              <span>Items</span>
              <span className="inv-toggle-count">{counts.item}</span>
            </button>
            <button
              onClick={() => setView('warehouse')}
              className={`inv-toggle-btn ${view === 'warehouse' ? 'active' : ''}`}
            >
              <Boxes className="w-3.5 h-3.5" />
              <span>Warehouses</span>
              <span className="inv-toggle-count">{counts.warehouse}</span>
            </button>
          </div>

          {/* Layout toggle */}
          <div className="inv-toggle inv-toggle-sm" title="Switch layout">
            <button onClick={() => setLayout('grid')} className={`inv-toggle-btn ${layout === 'grid' ? 'active' : ''}`}>
              <LayoutGrid className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setLayout('list')} className={`inv-toggle-btn ${layout === 'list' ? 'active' : ''}`}>
              <Rows3 className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Live indicator */}
          <div className="inv-live" title={`Auto-refresh every 10s · ${secsSinceSync}s ago`}>
            <span key={livePulse} className="inv-live-dot" />
            <span className="inv-live-label">LIVE</span>
            <span className="inv-live-time">·  {tickClock >= 0 ? `${secsSinceSync}s ago` : ''}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="inv-search">
            <Search className="w-3.5 h-3.5 text-[var(--gty-text-faint)]" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search"
              className="bg-transparent outline-none text-sm text-white w-40"
            />
          </div>
          {canWrite && (
            <>
              <button
                onClick={() => { setSelectMode(s => !s); clearSelection(); }}
                className={`inv-btn-ghost ${selectMode ? 'inv-btn-ghost-active' : ''}`}
                title="Toggle bulk select"
              >
                {selectMode ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                Select
              </button>
              <button onClick={() => setShowCatModal(true)} className="inv-btn-ghost" title="Manage categories">
                <Tag className="w-3.5 h-3.5" /> Categories
              </button>
              <button onClick={openCreate} className="inv-btn-primary">
                <Plus className="w-4 h-4" /> Add {view === 'item' ? 'Item' : 'Warehouse'}
              </button>
            </>
          )}
          {isManagement && (
            <button onClick={() => setShowLog(true)} className="inv-btn-ghost" title="Activity log">
              <History className="w-3.5 h-3.5" /> Log
            </button>
          )}
        </div>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        <CategoryChip label="All" color="#a855f7" icon="Boxes"
          active={activeCat === 'all'} onClick={() => setActiveCat('all')} />
        {categories.map(c => (
          <CategoryChip key={c.id} label={c.name} color={c.color} icon={c.icon}
            mgmtOnly={c.mgmt_only}
            active={activeCat === c.id} onClick={() => setActiveCat(c.id)} />
        ))}
        <CategoryChip label="Uncategorized" color="#52525b" icon="Tag"
          active={activeCat === 'none'} onClick={() => setActiveCat('none')} />
      </div>

      {/* Body */}
      {loading ? (
        <SkeletonGrid layout={layout} />
      ) : visible.length === 0 ? (
        <InventoryEmptyState view={view} canWrite={canWrite} onAdd={openCreate} />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visible.map(i => i.id)} strategy={verticalListSortingStrategy}>
            <div className={layout === 'grid'
              ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4'
              : 'flex flex-col gap-2'}>
              {visible.map(item => {
                const writable = itemWritable(item);
                const selectedHere = selected.has(item.id);
                const itemFloaters = floaters.filter(f => f.itemId === item.id);
                const cat = categories.find(c => c.id === item.category_id);
                return (
                  <SortableInvCard key={item.id} id={item.id} disabled={!writable || selectMode}>
                    {layout === 'grid' ? (
                      <InventoryCard
                        item={item}
                        category={cat}
                        canWrite={writable}
                        selectMode={selectMode}
                        selected={selectedHere}
                        onSelect={() => toggleSelect(item.id)}
                        onCardClick={() => selectMode ? toggleSelect(item.id) : setDetailItem(item)}
                        onAdjust={() => setAdjustItem(item)}
                        onQuickAdjust={(d) => handleQuickAdjust(item, d)}
                        onEdit={() => openEdit(item)}
                        onDelete={() => handleDelete(item)}
                        floaters={itemFloaters}
                      />
                    ) : (
                      <InventoryRow
                        item={item}
                        category={cat}
                        canWrite={writable}
                        selectMode={selectMode}
                        selected={selectedHere}
                        onSelect={() => toggleSelect(item.id)}
                        onCardClick={() => selectMode ? toggleSelect(item.id) : setDetailItem(item)}
                        onAdjust={() => setAdjustItem(item)}
                        onQuickAdjust={(d) => handleQuickAdjust(item, d)}
                        onEdit={() => openEdit(item)}
                        onDelete={() => handleDelete(item)}
                      />
                    )}
                  </SortableInvCard>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Bulk action bar */}
      <AnimatePresence>
        {selectMode && (
          <BulkActionBar
            count={selected.size}
            visibleCount={visible.length}
            categories={categories.filter(c => isManagement || !c.mgmt_only)}
            busy={bulkBusy}
            onSelectAll={selectAll}
            onClear={clearSelection}
            onClose={exitBulk}
            onDelete={() => {
              if (!window.confirm(`Delete ${selected.size} item(s)? This cannot be undone.`)) return;
              runBulk('delete');
            }}
            onMove={(catId) => runBulk('move', { category_id: catId })}
            onPrice={(price) => runBulk('price', { price })}
          />
        )}
      </AnimatePresence>

      {/* Modals */}
      {showItemModal && (
        <InventoryItemModal
          kind={view}
          editing={editingItem}
          categories={categories.filter(c => isManagement || !c.mgmt_only)}
          onClose={() => { setShowItemModal(false); setEditingItem(null); }}
          onSaved={() => { setShowItemModal(false); setEditingItem(null); load(); }}
        />
      )}
      {showCatModal && (
        <InventoryCategoriesModal
          categories={categories}
          isManagement={isManagement}
          onClose={() => setShowCatModal(false)}
          onChanged={() => { load(); }}
        />
      )}
      {adjustItem && (
        <InventoryAdjustModal
          item={adjustItem}
          onClose={() => setAdjustItem(null)}
          onAdjusted={(saved, delta) => {
            setAdjustItem(null);
            if (saved) updateLocalItem(saved, delta || 0);
            load(true);
          }}
        />
      )}
      {showLog && isManagement && (
        <InventoryLogDrawer onClose={() => setShowLog(false)} />
      )}
      {detailItem && (
        <ItemDetailDrawer
          item={items.find(i => i.id === detailItem.id) || detailItem}
          category={categories.find(c => c.id === detailItem.category_id)}
          canWrite={itemWritable(detailItem)}
          isManagement={isManagement}
          onClose={() => setDetailItem(null)}
          onAdjust={() => { setDetailItem(null); setAdjustItem(detailItem); }}
          onEdit={() => { setDetailItem(null); openEdit(detailItem); }}
          onDelete={() => { setDetailItem(null); handleDelete(detailItem); }}
        />
      )}
    </div>
  );
};

const SkeletonGrid = ({ layout }) => (
  layout === 'grid' ? (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="inv-skeleton" style={{ animationDelay: `${i * 0.07}s` }}>
          <div className="inv-skeleton-img" />
          <div className="inv-skeleton-line w-3/4" />
          <div className="inv-skeleton-line w-1/2 h-2" />
          <div className="inv-skeleton-num" />
        </div>
      ))}
    </div>
  ) : (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="inv-skeleton-row" style={{ animationDelay: `${i * 0.07}s` }} />
      ))}
    </div>
  )
);

const CategoryChip = ({ label, color, icon, active, onClick, mgmtOnly = false }) => {
  const Icon = INVENTORY_ICONS[icon] || Tag;
  return (
    <button
      onClick={onClick}
      className={`inv-chip ${active ? 'active' : ''}`}
      style={active ? { borderColor: color, color: '#fff', background: `${color}22` } : { color }}
    >
      <Icon className="w-3 h-3" />
      <span>{label}</span>
      {mgmtOnly && <Lock className="w-2.5 h-2.5 opacity-80" />}
    </button>
  );
};

// AnimatedCount — number that animates between values when prop changes.
const AnimatedCount = ({ value, padding = 3, className = '' }) => {
  const padded = String(Math.max(0, value)).padStart(padding, '0');
  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span
        key={value}
        initial={{ y: 14, opacity: 0 }}
        animate={{ y: 0,  opacity: 1 }}
        exit={{    y: -14, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 400, damping: 24 }}
        className={`inline-block font-mono tabular-nums ${className}`}
      >
        {padded}
      </motion.span>
    </AnimatePresence>
  );
};

const InventoryCard = ({
  item, category, canWrite, onAdjust, onQuickAdjust, onEdit, onDelete,
  selectMode = false, selected = false, onSelect, onCardClick,
  floaters = [],
}) => {
  const isWarehouse = item.kind === 'warehouse';
  const price       = formatPrice(item.price);

  const stopProp = (e, fn) => { e.stopPropagation(); fn?.(); };

  return (
    <div
      className={`inv-card group ${selectMode ? 'inv-card-selectable' : ''} ${selected ? 'inv-card-selected' : ''}`}
      onClick={() => onCardClick?.()}
    >
      {/* Selection overlay */}
      {selectMode && (
        <div className="inv-card-selectbox" onClick={(e) => stopProp(e, onSelect)}>
          {selected
            ? <div className="inv-checkbox checked"><Check className="w-3 h-3" /></div>
            : <div className="inv-checkbox" />
          }
        </div>
      )}

      {/* Floater layer (+/− values flying up) */}
      <div className="inv-floater-layer">
        <AnimatePresence>
          {floaters.map(f => (
            <motion.span
              key={f.id}
              initial={{ y: 0, opacity: 0, scale: 0.7 }}
              animate={{ y: -54, opacity: 1, scale: 1 }}
              exit={{ y: -90, opacity: 0, scale: 0.85 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className={`inv-floater ${f.value > 0 ? 'inv-floater-up' : 'inv-floater-dn'}`}
            >
              {f.value > 0 ? '+' : '−'}{Math.abs(f.value)}
            </motion.span>
          ))}
        </AnimatePresence>
      </div>

      {/* Image */}
      <div className="inv-card-img">
        {item.image_url
          ? <img src={item.image_url} alt={item.name} loading="lazy" />
          : <div className="inv-card-img-placeholder"><Package className="w-8 h-8" /></div>
        }
        <div className="inv-card-ribbon" data-kind={item.kind}>
          {isWarehouse ? 'STORE' : 'ITEM'}
        </div>
        {canWrite && !selectMode && (
          <div className="inv-card-actions">
            <button onClick={(e) => stopProp(e, onEdit)} className="inv-icon-btn" title="Edit"><Edit3 className="w-3.5 h-3.5" /></button>
            <button onClick={(e) => stopProp(e, onDelete)} className="inv-icon-btn inv-icon-btn-danger" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="inv-card-body">
        <div className="flex items-start justify-between gap-2">
          <h3 className="inv-card-name">{item.name}</h3>
          {price && <span className="inv-card-price">{price}</span>}
        </div>

        {category && (
          <span className="inv-card-cat" style={{ borderColor: category.color, color: category.color }}>
            {category.name}{category.mgmt_only && <Lock className="w-2.5 h-2.5 inline ml-1" />}
          </span>
        )}

        {item.description && (
          <p className="inv-card-desc">{item.description}</p>
        )}

        {/* Quantity area */}
        {isWarehouse ? (
          <div className="inv-card-qty-stack">
            <CapacityGauge current={item.quantity} max={item.max_capacity || 1} compact />
            {canWrite && (
              <div className="flex items-stretch gap-1">
                <button onClick={(e) => stopProp(e, () => onQuickAdjust?.(-Math.max(1, Math.round((item.max_capacity || 100) * 0.1))))}
                  className="inv-quick-btn" title="Withdraw 10%">
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <button onClick={(e) => stopProp(e, onAdjust)} className="inv-card-adjust flex-1 justify-center" title="Adjust capacity">
                  <Activity className="w-3.5 h-3.5" />
                  <span>Adjust</span>
                </button>
                <button onClick={(e) => stopProp(e, () => onQuickAdjust?.(+Math.max(1, Math.round((item.max_capacity || 100) * 0.1))))}
                  className="inv-quick-btn" title="Add 10%">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="inv-card-qty">
            <div className="flex items-baseline gap-1.5">
              <AnimatedCount value={item.quantity} className="text-3xl font-black text-white" />
              <span className="text-[10px] uppercase tracking-widest text-[var(--gty-text-faint)]">in stock</span>
            </div>
            {canWrite && (
              <div className="flex items-stretch gap-1">
                <button onClick={(e) => stopProp(e, () => onQuickAdjust?.(-1))}
                  disabled={item.quantity <= 0}
                  className="inv-quick-btn" title="Remove 1">
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <button onClick={(e) => stopProp(e, () => onQuickAdjust?.(+1))}
                  className="inv-quick-btn inv-quick-btn-plus" title="Add 1">
                  <Plus className="w-3.5 h-3.5" />
                </button>
                <button onClick={(e) => stopProp(e, onAdjust)} className="inv-card-adjust" title="Adjust">
                  <Edit3 className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const InventoryRow = ({
  item, category, canWrite, onAdjust, onQuickAdjust, onEdit, onDelete,
  selectMode = false, selected = false, onSelect, onCardClick,
}) => {
  const isWarehouse = item.kind === 'warehouse';
  const price       = formatPrice(item.price);
  const stopProp = (e, fn) => { e.stopPropagation(); fn?.(); };

  return (
    <div
      className={`inv-row group ${selected ? 'inv-row-selected' : ''}`}
      onClick={() => onCardClick?.()}
    >
      {selectMode && (
        <div className="shrink-0" onClick={(e) => stopProp(e, onSelect)}>
          {selected
            ? <div className="inv-checkbox checked"><Check className="w-3 h-3" /></div>
            : <div className="inv-checkbox" />
          }
        </div>
      )}
      <div className="inv-row-thumb">
        {item.image_url
          ? <img src={item.image_url} alt="" loading="lazy" />
          : <Package className="w-5 h-5 text-[var(--gty-text-faint)]" />
        }
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-bold truncate">{item.name}</h3>
          <span className="inv-row-kind">{isWarehouse ? 'STORE' : 'ITEM'}</span>
          {category && (
            <span className="inv-card-cat" style={{ borderColor: category.color, color: category.color }}>
              {category.name}
            </span>
          )}
        </div>
        {item.description && <p className="text-xs text-[var(--gty-text-faint)] truncate">{item.description}</p>}
      </div>
      <div className="inv-row-qty">
        {isWarehouse ? (
          <div className="w-48">
            <CapacityGauge current={item.quantity} max={item.max_capacity || 1} compact animate={false} />
          </div>
        ) : (
          <div className="flex items-baseline gap-1.5">
            <AnimatedCount value={item.quantity} padding={3} className="text-2xl font-black text-white" />
            <span className="text-[9px] uppercase tracking-widest text-[var(--gty-text-faint)]">qty</span>
          </div>
        )}
      </div>
      {price && <span className="inv-card-price">{price}</span>}
      {canWrite && !selectMode && (
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={(e) => stopProp(e, () => onQuickAdjust?.(-1))} className="inv-quick-btn"><Minus className="w-3.5 h-3.5" /></button>
          <button onClick={(e) => stopProp(e, () => onQuickAdjust?.(+1))} className="inv-quick-btn inv-quick-btn-plus"><Plus className="w-3.5 h-3.5" /></button>
          <button onClick={(e) => stopProp(e, onAdjust)} className="inv-card-adjust"><Activity className="w-3.5 h-3.5" /></button>
          <button onClick={(e) => stopProp(e, onEdit)} className="inv-icon-btn"><Edit3 className="w-3.5 h-3.5" /></button>
          <button onClick={(e) => stopProp(e, onDelete)} className="inv-icon-btn inv-icon-btn-danger"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      )}
    </div>
  );
};

const InventoryEmptyState = ({ view, canWrite, onAdd }) => (
  <div className="inv-empty">
    <div className="inv-empty-stripes" />
    <div className="relative">
      {view === 'item' ? <Package className="w-12 h-12 mx-auto mb-4 text-[var(--gty-text-faint)]" />
                       : <Boxes  className="w-12 h-12 mx-auto mb-4 text-[var(--gty-text-faint)]" />}
      <p className="text-xs font-black uppercase tracking-[0.3em] text-[var(--gty-text-secondary)] mb-2">
        No {view === 'item' ? 'items' : 'warehouses'} yet
      </p>
      <p className="text-sm text-[var(--gty-text-faint)] mb-6">
        {canWrite
          ? `Add the first ${view === 'item' ? 'item' : 'warehouse'} to get started.`
          : `Senior Operator+ can create the first ${view === 'item' ? 'item' : 'warehouse'}.`}
      </p>
      {canWrite && (
        <button onClick={onAdd} className="inv-btn-primary mx-auto">
          <Plus className="w-4 h-4" /> Add {view === 'item' ? 'Item' : 'Warehouse'}
        </button>
      )}
    </div>
  </div>
);

// ─── Item create / edit modal ──────────────────────────────────────────────────
const InventoryItemModal = ({ kind, editing, categories, onClose, onSaved }) => {
  const isEdit = !!editing;
  // If editing, lock to existing kind; otherwise use the current `view` kind
  const itemKind = isEdit ? editing.kind : kind;
  const [name, setName]               = useState(editing?.name || '');
  const [description, setDescription] = useState(editing?.description || '');
  const [imageUrl, setImageUrl]       = useState(editing?.image_url || '');
  const [categoryId, setCategoryId]   = useState(editing?.category_id ?? null);
  const [maxCap, setMaxCap]           = useState(editing?.max_capacity ?? '');
  const [initQty, setInitQty]         = useState(isEdit ? '' : '0');
  const [price, setPrice]             = useState(editing?.price ?? '');
  const [uploading, setUploading]     = useState(false);
  const [saving, setSaving]           = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await inventoryAPI.uploadImage(file);
      setImageUrl(r.data.url);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f && f.type.startsWith('image/')) handleFile(f);
  };

  const submit = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (itemKind === 'warehouse' && (!maxCap || Number(maxCap) <= 0)) {
      toast.error('Max capacity must be greater than zero');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await inventoryAPI.updateItem(editing.id, {
          name: name.trim(),
          description,
          image_url: imageUrl || null,
          category_id: categoryId || null,
          max_capacity: itemKind === 'warehouse' ? Number(maxCap) : null,
          price: price === '' ? null : Number(price),
        });
        toast.success('Updated');
      } else {
        await inventoryAPI.createItem({
          name: name.trim(),
          description,
          image_url: imageUrl || null,
          category_id: categoryId || null,
          kind: itemKind,
          quantity: Number(initQty || 0),
          max_capacity: itemKind === 'warehouse' ? Number(maxCap) : null,
          price: price === '' ? null : Number(price),
        });
        toast.success('Created');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="!max-w-2xl bg-[var(--gty-bg-card)] border-[var(--gty-border)] text-white inv-dialog">
        <DialogHeader>
          <DialogTitle className="text-xs font-black uppercase tracking-[0.3em] flex items-center gap-2">
            {itemKind === 'warehouse' ? <Boxes className="w-4 h-4" /> : <Package className="w-4 h-4" />}
            {isEdit ? 'Edit' : 'New'} {itemKind === 'warehouse' ? 'Warehouse' : 'Item'}
          </DialogTitle>
          <DialogDescription className="text-[var(--gty-text-faint)] text-xs">
            {itemKind === 'warehouse'
              ? 'Capacity-based storage with fill percentage.'
              : 'Countable item — increase or decrease quantity.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-5 pt-2">
          {/* Image drop */}
          <div>
            <Label className="inv-label">Image</Label>
            <div
              className="inv-drop"
              onClick={() => fileRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={onDrop}
            >
              {imageUrl
                ? <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                : (
                  <div className="text-center px-2">
                    {uploading
                      ? <div className="text-xs text-[var(--gty-text-faint)]">Uploading…</div>
                      : <>
                          <Upload className="w-5 h-5 mx-auto mb-1 text-[var(--gty-text-faint)]" />
                          <p className="text-[10px] uppercase tracking-widest text-[var(--gty-text-faint)]">Drop / click</p>
                        </>
                    }
                  </div>
                )
              }
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={e => handleFile(e.target.files?.[0])}
              />
            </div>
            {imageUrl && (
              <button
                type="button"
                onClick={() => setImageUrl('')}
                className="mt-2 text-[10px] uppercase tracking-widest text-[var(--gty-text-faint)] hover:text-red-400"
              >
                Remove image
              </button>
            )}
          </div>

          {/* Form fields */}
          <div className="space-y-4">
            <div>
              <Label className="inv-label">Name *</Label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Name"
                className="inv-input"
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="inv-label">Category</Label>
                <select
                  value={categoryId ?? ''}
                  onChange={e => setCategoryId(e.target.value ? Number(e.target.value) : null)}
                  className="inv-input"
                >
                  <option value="">— None —</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="inv-label">Price <span className="text-[var(--gty-text-faint)]">(optional)</span></Label>
                <input
                  value={price}
                  onChange={e => setPrice(e.target.value)}
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  className="inv-input"
                />
              </div>
            </div>

            {itemKind === 'warehouse' ? (
              <div>
                <Label className="inv-label">Max capacity *</Label>
                <input
                  value={maxCap}
                  onChange={e => setMaxCap(e.target.value)}
                  type="number"
                  min="1"
                  placeholder="Max capacity"
                  className="inv-input"
                />
              </div>
            ) : !isEdit && (
              <div>
                <Label className="inv-label">Initial quantity</Label>
                <input
                  value={initQty}
                  onChange={e => setInitQty(e.target.value)}
                  type="number"
                  min="0"
                  className="inv-input"
                />
              </div>
            )}

            <div>
              <Label className="inv-label">Description</Label>
              <Textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                placeholder="Description"
                className="inv-input resize-none"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-[var(--gty-border)] mt-4">
          <button onClick={onClose} className="inv-btn-ghost">Cancel</button>
          <button onClick={submit} disabled={saving} className="inv-btn-primary disabled:opacity-50">
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Categories manager ────────────────────────────────────────────────────────
const InventoryCategoriesModal = ({ categories, isManagement = false, onClose, onChanged }) => {
  const [editing, setEditing]     = useState(null);
  const [name, setName]           = useState('');
  const [color, setColor]         = useState(INVENTORY_CATEGORY_COLORS[0]);
  const [icon, setIcon]           = useState('Package');
  const [mgmtOnly, setMgmtOnly]   = useState(false);
  const [busy, setBusy]           = useState(false);

  const reset = () => {
    setEditing(null); setName('');
    setColor(INVENTORY_CATEGORY_COLORS[0]); setIcon('Package');
    setMgmtOnly(false);
  };

  const startEdit = (c) => {
    setEditing(c); setName(c.name); setColor(c.color); setIcon(c.icon);
    setMgmtOnly(!!c.mgmt_only);
  };

  const submit = async () => {
    if (!name.trim()) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      const payload = { name: name.trim(), color, icon, mgmt_only: mgmtOnly };
      if (editing) {
        await inventoryAPI.updateCategory(editing.id, payload);
        toast.success('Updated');
      } else {
        await inventoryAPI.createCategory(payload);
        toast.success('Created');
      }
      reset();
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? Items in it become uncategorized.`)) return;
    try {
      await inventoryAPI.deleteCategory(c.id);
      toast.success('Deleted');
      onChanged();
    } catch {
      toast.error('Delete failed');
    }
  };

  const IconCmp = INVENTORY_ICONS[icon] || Package;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="!max-w-xl bg-[var(--gty-bg-card)] border-[var(--gty-border)] text-white inv-dialog">
        <DialogHeader>
          <DialogTitle className="text-xs font-black uppercase tracking-[0.3em] flex items-center gap-2">
            <Tag className="w-4 h-4" /> Categories
          </DialogTitle>
        </DialogHeader>

        {/* Editor */}
        <div className="space-y-3 pt-2 pb-4 border-b border-[var(--gty-border)]">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 border flex items-center justify-center shrink-0"
              style={{ borderColor: color, background: `${color}22` }}
            >
              <IconCmp className="w-5 h-5" style={{ color }} />
            </div>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Category name"
              className="inv-input flex-1"
            />
          </div>

          <div>
            <Label className="inv-label">Color</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {INVENTORY_CATEGORY_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-6 h-6 border-2 transition-transform ${color === c ? 'scale-110' : 'opacity-70 hover:opacity-100'}`}
                  style={{ background: c, borderColor: color === c ? '#fff' : c }}
                />
              ))}
            </div>
          </div>

          <div>
            <Label className="inv-label">Icon</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {Object.keys(INVENTORY_ICONS).map(key => {
                const I = INVENTORY_ICONS[key];
                return (
                  <button
                    key={key}
                    onClick={() => setIcon(key)}
                    className={`w-9 h-9 border flex items-center justify-center ${icon === key ? 'border-[var(--gty-accent,#a855f7)] text-white bg-[var(--gty-accent,#a855f7)]/15' : 'border-[var(--gty-border)] text-[var(--gty-text-faint)] hover:text-white'}`}
                  >
                    <I className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </div>

          {isManagement && (
            <label className="inv-mgmt-only" title="When checked, only Management can see or modify items in this category.">
              <input
                type="checkbox"
                checked={mgmtOnly}
                onChange={e => setMgmtOnly(e.target.checked)}
              />
              <span className="inv-mgmt-only-box">{mgmtOnly && <Check className="w-3 h-3" />}</span>
              <Lock className="w-3.5 h-3.5" />
              <span>Management only — hide this category and its items from non-management users</span>
            </label>
          )}

          <div className="flex justify-end gap-2 pt-1">
            {editing && (
              <button onClick={reset} className="inv-btn-ghost">Cancel edit</button>
            )}
            <button onClick={submit} disabled={busy} className="inv-btn-primary disabled:opacity-50">
              {editing ? 'Save category' : <><Plus className="w-4 h-4" />Add category</>}
            </button>
          </div>
        </div>

        {/* List */}
        <div className="max-h-64 overflow-y-auto pt-3 space-y-1">
          {categories.length === 0 ? (
            <p className="text-xs text-[var(--gty-text-faint)] text-center py-6">No categories yet.</p>
          ) : categories.map(c => {
            const I = INVENTORY_ICONS[c.icon] || Package;
            return (
              <div key={c.id} className="flex items-center gap-3 px-2 py-2 border border-transparent hover:border-[var(--gty-border)] transition-colors">
                <div className="w-8 h-8 border flex items-center justify-center shrink-0"
                  style={{ borderColor: c.color, background: `${c.color}22` }}>
                  <I className="w-4 h-4" style={{ color: c.color }} />
                </div>
                <span className="flex-1 text-sm flex items-center gap-1.5">
                  {c.name}
                  {c.mgmt_only && <span className="inv-mgmt-badge"><Lock className="w-2.5 h-2.5" /> MGMT</span>}
                </span>
                <button onClick={() => startEdit(c)} className="inv-icon-btn"><Edit3 className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(c)} className="inv-icon-btn inv-icon-btn-danger"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end pt-3">
          <button onClick={onClose} className="inv-btn-ghost">Close</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Adjust modal ──────────────────────────────────────────────────────────────
const InventoryAdjustModal = ({ item, onClose, onAdjusted }) => {
  const isWarehouse = item.kind === 'warehouse';
  const max = isWarehouse ? (item.max_capacity || 0) : null;
  const [delta, setDelta]   = useState(isWarehouse ? Math.max(1, Math.round((max || 100) * 0.1)) : 1);
  const [mode, setMode]     = useState('add'); // 'add' | 'remove'
  const [reason, setReason] = useState('');
  const [busy, setBusy]     = useState(false);

  const signedDelta = mode === 'add' ? Math.abs(Number(delta)) : -Math.abs(Number(delta));
  const projected = item.quantity + signedDelta;
  const valid = signedDelta !== 0 && projected >= 0 && (max === null || projected <= max);

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await adjustWithUndo(item, signedDelta, reason, {
        onAfter: (saved) => onAdjusted(saved, signedDelta),
      });
      // Close even if user dismisses toast
      onAdjusted(null, signedDelta);
    } catch {
      // adjustWithUndo already toasted
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="!max-w-md bg-[var(--gty-bg-card)] border-[var(--gty-border)] text-white inv-dialog">
        <DialogHeader>
          <DialogTitle className="text-xs font-black uppercase tracking-[0.3em] flex items-center gap-2">
            {isWarehouse ? <Boxes className="w-4 h-4" /> : <Package className="w-4 h-4" />}
            Adjust — {item.name}
          </DialogTitle>
        </DialogHeader>

        <div className="pt-2 space-y-4">
          {/* Visual: capacity gauge stacks vertically; items side-by-side */}
          {isWarehouse ? (
            <div className="space-y-3 py-4 border-y border-[var(--gty-border)]">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[var(--gty-text-faint)] mb-1">Now</p>
                <CapacityGauge current={item.quantity} max={max} animate={false} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[var(--gty-text-faint)] mb-1">After</p>
                <CapacityGauge current={Math.max(0, Math.min(max, projected))} max={max} />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-8 py-4 border-y border-[var(--gty-border)]">
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-widest text-[var(--gty-text-faint)] mb-1">Now</p>
                <span className="font-mono text-4xl font-black tabular-nums">{String(item.quantity).padStart(3, '0')}</span>
              </div>
              <ChevronRight className="w-6 h-6 text-[var(--gty-text-faint)]" />
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-widest text-[var(--gty-text-faint)] mb-1">After</p>
                <motion.span
                  key={projected}
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 320, damping: 18 }}
                  className={`font-mono text-4xl font-black tabular-nums ${projected < 0 ? 'text-red-500' : 'text-[var(--gty-accent,#a855f7)]'}`}
                >
                  {String(Math.max(0, projected)).padStart(3, '0')}
                </motion.span>
              </div>
            </div>
          )}

          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-1 p-1 border border-[var(--gty-border)]">
            <button
              onClick={() => setMode('add')}
              className={`py-2 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-1.5 ${mode === 'add' ? 'bg-[var(--gty-accent,#a855f7)]/15 text-[var(--gty-accent,#a855f7)]' : 'text-[var(--gty-text-faint)] hover:text-white'}`}
            >
              <ArrowUpFromLine className="w-3.5 h-3.5" /> Add
            </button>
            <button
              onClick={() => setMode('remove')}
              className={`py-2 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-1.5 ${mode === 'remove' ? 'bg-red-500/15 text-red-400' : 'text-[var(--gty-text-faint)] hover:text-white'}`}
            >
              <ArrowDownToLine className="w-3.5 h-3.5" /> Remove
            </button>
          </div>

          {/* Delta input + quick chips */}
          <div>
            <Label className="inv-label">Amount</Label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                value={delta}
                onChange={e => setDelta(Math.max(1, Number(e.target.value || 0)))}
                className="inv-input flex-1"
              />
              <div className="flex gap-1">
                {(isWarehouse
                  ? [Math.round(max * 0.05), Math.round(max * 0.1), Math.round(max * 0.25), Math.round(max * 0.5)].filter(n => n > 0)
                  : [1, 5, 10, 50]
                ).map(n => (
                  <button
                    key={n}
                    onClick={() => setDelta(n)}
                    className="px-2 py-1 text-[10px] font-mono border border-[var(--gty-border)] text-[var(--gty-text-faint)] hover:text-white hover:border-[var(--gty-accent,#a855f7)]"
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            {!valid && signedDelta !== 0 && (
              <p className="text-xs text-red-400 mt-1">
                {projected < 0 ? 'Cannot go below zero.' : 'Exceeds max capacity.'}
              </p>
            )}
          </div>

          <div>
            <Label className="inv-label">Reason <span className="text-[var(--gty-text-faint)]">(optional)</span></Label>
            <input
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Reason"
              className="inv-input"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-[var(--gty-border)] mt-4">
          <button onClick={onClose} className="inv-btn-ghost">Cancel</button>
          <button onClick={submit} disabled={!valid || busy} className="inv-btn-primary disabled:opacity-50">
            {busy ? 'Saving…' : `Confirm ${mode === 'add' ? '+' : '−'}${Math.abs(Number(delta) || 0)}`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Activity log drawer ───────────────────────────────────────────────────────
const InventoryLogDrawer = ({ onClose }) => {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    inventoryAPI.listLogs()
      .then(r => setLogs(r.data))
      .catch(() => toast.error('Failed to load logs'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 240 }}
        className="relative w-full max-w-md h-full bg-[var(--gty-bg-card)] border-l border-[var(--gty-border)] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--gty-border)]">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-[var(--gty-accent,#a855f7)]" />
            <span className="text-xs font-black uppercase tracking-[0.3em]">Activity Log</span>
          </div>
          <button onClick={onClose} className="inv-icon-btn"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="p-6 text-xs text-[var(--gty-text-faint)] uppercase tracking-widest">Loading…</p>
          ) : logs.length === 0 ? (
            <p className="p-6 text-xs text-[var(--gty-text-faint)] text-center">No activity yet.</p>
          ) : logs.map(l => {
            const action = l.action;
            const tone = action === 'created' ? 'text-green-400'
                       : action === 'deleted' ? 'text-red-400'
                       : action === 'edited'  ? 'text-blue-300'
                       : 'text-[var(--gty-accent,#a855f7)]';
            return (
              <div key={l.id} className="px-5 py-3 border-b border-[var(--gty-border)]/60 hover:bg-black/30">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className={`text-[10px] font-black uppercase tracking-widest ${tone}`}>{action}</span>
                  <span className="text-[10px] text-[var(--gty-text-faint)] font-mono">
                    {new Date(l.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm font-bold mb-0.5">{l.item_name_snapshot}</p>
                <p className="text-xs text-[var(--gty-text-faint)]">
                  by <span className="text-white">{l.user_name}</span>
                  {l.delta !== null && l.delta !== undefined && (
                    <> · <span className="font-mono">{l.delta > 0 ? '+' : ''}{l.delta}</span>
                    {l.quantity_before !== null && l.quantity_after !== null && (
                      <> ({l.quantity_before} → {l.quantity_after})</>
                    )}</>
                  )}
                </p>
                {l.reason && <p className="text-xs text-[var(--gty-text-secondary)] mt-1 italic">"{l.reason}"</p>}
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};

// ─── Inventory styles ──────────────────────────────────────────────────────────
// ─── Item detail drawer ────────────────────────────────────────────────────────
const ItemDetailDrawer = ({ item, category, canWrite, isManagement, onClose, onAdjust, onEdit, onDelete }) => {
  const [history, setHistory]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const isWarehouse = item.kind === 'warehouse';
  const price = formatPrice(item.price);

  useEffect(() => {
    if (!isManagement) return; // logs are management-only
    setLoading(true);
    inventoryAPI.listLogs(item.id)
      .then(r => setHistory(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [item.id, isManagement]);

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />
      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 260 }}
        className="relative w-full max-w-lg h-full bg-[var(--gty-bg-card)] border-l border-[var(--gty-border)] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header strip */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--gty-border)]">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--gty-text-faint)]">
            <span className={`inv-card-ribbon !static !top-auto !right-auto`} data-kind={item.kind}>
              {isWarehouse ? 'STORE' : 'ITEM'}
            </span>
            <span>· #{String(item.id).padStart(4, '0')}</span>
          </div>
          <button onClick={onClose} className="inv-icon-btn"><X className="w-4 h-4" /></button>
        </div>

        {/* Hero image */}
        <div className="inv-detail-hero">
          {item.image_url
            ? <img src={item.image_url} alt={item.name} />
            : <div className="inv-card-img-placeholder w-full h-full"><Package className="w-14 h-14" /></div>
          }
          {category && (
            <span className="inv-detail-cat" style={{ borderColor: category.color, color: category.color }}>
              {category.name}{category.mgmt_only && <Lock className="w-3 h-3 ml-1" />}
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-5 py-5 border-b border-[var(--gty-border)]">
            <h2 className="text-2xl font-black uppercase tracking-tight mb-1">{item.name}</h2>
            {price && <p className="text-base font-mono text-green-400 mb-3">{price}</p>}
            {item.description && (
              <p className="text-sm text-[var(--gty-text-secondary)] leading-relaxed mb-4">
                {item.description}
              </p>
            )}

            {/* Quantity block */}
            <div className="inv-detail-qty">
              {isWarehouse ? (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[var(--gty-text-faint)] mb-2">Capacity</p>
                  <CapacityGauge current={item.quantity} max={item.max_capacity || 1} segments={32} animate={false} />
                </div>
              ) : (
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-5xl font-black text-white tabular-nums">
                    {String(item.quantity).padStart(3, '0')}
                  </span>
                  <span className="text-xs uppercase tracking-widest text-[var(--gty-text-faint)]">in stock</span>
                </div>
              )}
            </div>

            {canWrite && (
              <div className="flex items-center gap-2 mt-5">
                <button onClick={onAdjust} className="inv-btn-primary"><Activity className="w-4 h-4" /> Adjust</button>
                <button onClick={onEdit}   className="inv-btn-ghost"><Edit3 className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={onDelete} className="inv-btn-ghost inv-btn-danger ml-auto"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            )}
          </div>

          {/* History (mgmt only) */}
          {isManagement && (
            <div className="px-5 py-4">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[var(--gty-text-faint)] mb-3">
                Activity for this item
              </p>
              {loading ? (
                <p className="text-xs text-[var(--gty-text-faint)]">Loading…</p>
              ) : history.length === 0 ? (
                <p className="text-xs text-[var(--gty-text-faint)] italic">No activity recorded.</p>
              ) : (
                <div className="space-y-2">
                  {history.map(l => {
                    const tone = l.action === 'created' ? 'text-green-400'
                               : l.action === 'deleted' ? 'text-red-400'
                               : l.action === 'edited'  ? 'text-blue-300'
                               : 'text-[var(--gty-accent,#a855f7)]';
                    return (
                      <div key={l.id} className="border-l-2 border-[var(--gty-border)] pl-3 py-1">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span className={`text-[10px] font-black uppercase tracking-widest ${tone}`}>{l.action}</span>
                          <span className="text-[10px] font-mono text-[var(--gty-text-faint)]">
                            {new Date(l.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-xs text-[var(--gty-text-secondary)]">
                          <span className="text-white font-medium">{l.user_name}</span>
                          {l.delta !== null && l.delta !== undefined && (
                            <> · <span className="font-mono">{l.delta > 0 ? '+' : ''}{l.delta}</span>
                              {l.quantity_before !== null && l.quantity_after !== null && (
                                <> ({l.quantity_before} → {l.quantity_after})</>
                              )}
                            </>
                          )}
                        </p>
                        {l.reason && <p className="text-xs text-[var(--gty-text-faint)] italic mt-0.5">"{l.reason}"</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// ─── Bulk action bar ──────────────────────────────────────────────────────────
const BulkActionBar = ({ count, visibleCount, categories, busy, onSelectAll, onClear, onClose, onDelete, onMove, onPrice }) => {
  const [moveOpen, setMoveOpen]   = useState(false);
  const [priceOpen, setPriceOpen] = useState(false);
  const [priceVal, setPriceVal]   = useState('');

  return (
    <motion.div
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0,  opacity: 1 }}
      exit={{    y: 80, opacity: 0 }}
      transition={{ type: 'spring', damping: 26, stiffness: 280 }}
      className="inv-bulk-bar"
    >
      <div className="inv-bulk-count">
        <span className="font-mono tabular-nums text-2xl font-black">{String(count).padStart(2, '0')}</span>
        <span className="text-[10px] font-black uppercase tracking-[0.3em] text-[var(--gty-text-faint)] mt-0.5">selected</span>
      </div>

      <div className="inv-bulk-actions">
        <button onClick={onSelectAll} className="inv-btn-ghost">
          <CheckSquare className="w-3.5 h-3.5" /> Select All ({visibleCount})
        </button>
        <button onClick={onClear} className="inv-btn-ghost" disabled={count === 0}>
          <Square className="w-3.5 h-3.5" /> Clear
        </button>

        <div className="inv-bulk-divider" />

        {/* Move dropdown */}
        <div className="relative">
          <button
            onClick={() => { setMoveOpen(o => !o); setPriceOpen(false); }}
            disabled={count === 0 || busy}
            className="inv-btn-ghost"
          >
            <Move className="w-3.5 h-3.5" /> Move
          </button>
          {moveOpen && (
            <div className="inv-bulk-popover">
              <button onClick={() => { onMove(null); setMoveOpen(false); }} className="inv-bulk-popover-item">
                — Uncategorized —
              </button>
              {categories.map(c => (
                <button key={c.id} onClick={() => { onMove(c.id); setMoveOpen(false); }}
                  className="inv-bulk-popover-item">
                  <span className="inv-bulk-dot" style={{ background: c.color }} />
                  {c.name}
                  {c.mgmt_only && <Lock className="w-3 h-3 ml-auto opacity-60" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Price popover */}
        <div className="relative">
          <button
            onClick={() => { setPriceOpen(o => !o); setMoveOpen(false); }}
            disabled={count === 0 || busy}
            className="inv-btn-ghost"
          >
            <DollarSign className="w-3.5 h-3.5" /> Price
          </button>
          {priceOpen && (
            <div className="inv-bulk-popover" style={{ minWidth: 220 }}>
              <p className="text-[10px] uppercase tracking-widest text-[var(--gty-text-faint)] px-3 pt-2 pb-1">Set price for {count} items</p>
              <div className="px-3 pb-3 flex items-center gap-2">
                <input
                  type="number" min="0" step="0.01"
                  placeholder="Price"
                  value={priceVal}
                  onChange={e => setPriceVal(e.target.value)}
                  className="inv-input flex-1"
                />
                <button
                  onClick={() => { onPrice(priceVal === '' ? null : Number(priceVal)); setPriceOpen(false); setPriceVal(''); }}
                  className="inv-btn-primary"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        <button onClick={onDelete} disabled={count === 0 || busy}
          className="inv-btn-ghost inv-btn-danger">
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>

        <div className="inv-bulk-divider" />

        <button onClick={onClose} className="inv-btn-ghost">
          <X className="w-3.5 h-3.5" /> Done
        </button>
      </div>
    </motion.div>
  );
};

const InventoryStyles = () => (
  <style>{`
    /* Root scope: typographic features for the entire inventory page.
       Tabular figures, slashed zero, and stylistic alternates are turned on
       so every digit aligns and 0 never gets confused with O. */
    .inv-root, .inv-dialog, .inv-detail-hero, .inv-bulk-bar {
      font-feature-settings: 'tnum' 1, 'zero' 1, 'ss01' 1, 'cv01' 1;
      font-variant-numeric: tabular-nums slashed-zero;
    }

    /* Custom cursors per zone */
    .inv-card, .inv-row { cursor: pointer; }
    .inv-card-img img,
    .inv-detail-hero img { cursor: zoom-in; }
    .inv-card-img-placeholder,
    .inv-detail-hero .inv-card-img-placeholder { cursor: default; }
    .inv-quick-btn, .inv-card-adjust, .inv-icon-btn,
    .inv-btn-primary, .inv-btn-ghost, .inv-toggle-btn { cursor: pointer; }
    .inv-quick-btn:disabled, .inv-btn-primary:disabled, .inv-btn-ghost:disabled { cursor: not-allowed; }
    .inv-floater-layer { pointer-events: none; }

    .inv-toggle {
      display: inline-flex;
      border: 1px solid var(--gty-border);
      background: rgba(0,0,0,0.3);
    }
    .inv-toggle-sm .inv-toggle-btn { padding: 8px 10px; }
    .inv-toggle-btn {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 8px 14px;
      font-size: 11px; font-weight: 900;
      text-transform: uppercase; letter-spacing: 0.2em;
      color: var(--gty-text-faint, #71717a);
      transition: all 0.15s;
      position: relative;
    }
    .inv-toggle-btn:hover { color: #fff; }
    .inv-toggle-btn.active {
      color: #fff;
      background: var(--gty-accent, #a855f7);
      box-shadow: inset 0 -2px 0 rgba(0,0,0,0.4);
    }
    .inv-toggle-count {
      font-family: ui-monospace, monospace;
      font-size: 10px;
      padding: 1px 5px;
      background: rgba(255,255,255,0.1);
      border: 1px solid rgba(255,255,255,0.15);
      letter-spacing: 0;
    }
    .inv-toggle-btn.active .inv-toggle-count {
      background: rgba(0,0,0,0.3);
      border-color: rgba(255,255,255,0.3);
    }
    .inv-search {
      display: flex; align-items: center; gap: 6px;
      padding: 7px 10px;
      border: 1px solid var(--gty-border);
      background: rgba(0,0,0,0.3);
    }
    .inv-btn-primary {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 8px 14px;
      font-size: 11px; font-weight: 900;
      text-transform: uppercase; letter-spacing: 0.18em;
      background: var(--gty-accent, #a855f7);
      color: #fff;
      border: 1px solid var(--gty-accent, #a855f7);
      transition: all 0.15s;
      cursor: pointer;
    }
    .inv-btn-primary:hover:not(:disabled) {
      background: #fff; color: #000;
      box-shadow: 4px 4px 0 0 var(--gty-accent, #a855f7);
      transform: translate(-2px, -2px);
    }
    .inv-btn-ghost {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 7px 12px;
      font-size: 11px; font-weight: 900;
      text-transform: uppercase; letter-spacing: 0.18em;
      background: transparent;
      color: var(--gty-text-secondary, #a1a1aa);
      border: 1px solid var(--gty-border);
      transition: all 0.15s;
      cursor: pointer;
    }
    .inv-btn-ghost:hover { color: #fff; border-color: var(--gty-accent, #a855f7); }

    .inv-chip {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 5px 11px;
      font-size: 10px; font-weight: 900;
      text-transform: uppercase; letter-spacing: 0.18em;
      background: transparent;
      border: 1px solid var(--gty-border);
      transition: all 0.15s;
      cursor: pointer;
    }
    .inv-chip:hover { background: rgba(168, 85, 247, 0.08); }
    .inv-chip.active { font-weight: 900; }

    .inv-card {
      background: var(--gty-bg-card, #18181b);
      border: 1px solid var(--gty-border);
      transition: all 0.18s ease;
      position: relative;
      overflow: hidden;
    }
    .inv-card:hover {
      border-color: var(--gty-accent, #a855f7);
      transform: translate(-2px, -2px);
      box-shadow: 4px 4px 0 0 var(--gty-accent, #a855f7);
    }
    .inv-card-img {
      aspect-ratio: 1 / 1;
      background: #0a0a0a;
      position: relative;
      overflow: hidden;
      border-bottom: 1px solid var(--gty-border);
    }
    .inv-card-img img {
      width: 100%; height: 100%; object-fit: cover;
      filter: saturate(0.95);
    }
    .inv-card-img-placeholder {
      width: 100%; height: 100%;
      display: flex; align-items: center; justify-content: center;
      color: #3f3f46;
      background:
        repeating-linear-gradient(
          45deg,
          transparent 0 8px,
          rgba(168, 85, 247, 0.04) 8px 9px
        );
    }
    .inv-card-ribbon {
      position: absolute;
      top: 8px; right: 8px;
      font-size: 9px; font-weight: 900;
      letter-spacing: 0.25em;
      padding: 2px 6px;
      background: rgba(0,0,0,0.85);
      color: #fff;
      border: 1px solid rgba(255,255,255,0.15);
    }
    .inv-card-ribbon[data-kind="warehouse"] {
      color: #fbbf24;
      border-color: rgba(251, 191, 36, 0.4);
    }
    .inv-card-actions {
      position: absolute;
      bottom: 8px; right: 8px;
      display: flex; gap: 4px;
      opacity: 0;
      transition: opacity 0.15s;
    }
    .inv-card:hover .inv-card-actions { opacity: 1; }
    .inv-icon-btn {
      width: 28px; height: 28px;
      display: inline-flex; align-items: center; justify-content: center;
      background: rgba(0,0,0,0.7);
      border: 1px solid var(--gty-border);
      color: #fff;
      cursor: pointer;
      transition: all 0.12s;
    }
    .inv-icon-btn:hover { border-color: var(--gty-accent, #a855f7); color: var(--gty-accent, #a855f7); }
    .inv-icon-btn-danger:hover { border-color: #ef4444; color: #ef4444; }

    .inv-card-body { padding: 12px 12px 14px; display: flex; flex-direction: column; gap: 6px; }
    .inv-card-name {
      font-weight: 900;
      font-size: 14px;
      color: #fff;
      line-height: 1.25;
      letter-spacing: -0.01em;
    }
    .inv-card-price {
      font-family: ui-monospace, monospace;
      font-size: 11px;
      color: #4ade80;
      white-space: nowrap;
      padding-top: 2px;
    }
    .inv-card-cat {
      align-self: flex-start;
      font-size: 9px; font-weight: 900;
      text-transform: uppercase; letter-spacing: 0.18em;
      padding: 2px 7px;
      border: 1px solid;
      background: transparent;
    }
    .inv-card-desc {
      font-size: 11px;
      color: var(--gty-text-faint, #71717a);
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .inv-card-qty {
      display: flex; align-items: flex-end; justify-content: space-between;
      gap: 8px; margin-top: 6px;
      padding-top: 8px;
      border-top: 1px dashed var(--gty-border);
    }
    .inv-card-qty-stack {
      display: flex; flex-direction: column; gap: 8px;
      margin-top: 6px;
      padding-top: 10px;
      border-top: 1px dashed var(--gty-border);
    }
    .inv-card-adjust {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 5px 8px;
      font-size: 10px; font-weight: 900;
      text-transform: uppercase; letter-spacing: 0.15em;
      background: transparent;
      color: var(--gty-text-secondary, #a1a1aa);
      border: 1px solid var(--gty-border);
      cursor: pointer;
      transition: all 0.12s;
    }
    .inv-card-adjust:hover {
      color: #fff;
      border-color: var(--gty-accent, #a855f7);
      background: rgba(168, 85, 247, 0.1);
    }

    .inv-card-skeleton {
      aspect-ratio: 1 / 1.4;
      background: linear-gradient(90deg, #18181b 0%, #27272a 50%, #18181b 100%);
      background-size: 200% 100%;
      border: 1px solid var(--gty-border);
      animation: inv-shimmer 1.4s linear infinite;
    }
    @keyframes inv-shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    .inv-empty {
      position: relative;
      padding: 80px 24px;
      border: 2px dashed var(--gty-border);
      text-align: center;
      overflow: hidden;
    }
    .inv-empty-stripes {
      position: absolute; inset: 0;
      background:
        repeating-linear-gradient(
          45deg,
          transparent 0 12px,
          rgba(168, 85, 247, 0.03) 12px 13px
        );
      pointer-events: none;
    }

    .inv-dialog .inv-label {
      display: block;
      font-size: 10px; font-weight: 900;
      text-transform: uppercase; letter-spacing: 0.2em;
      color: var(--gty-text-faint, #71717a);
      margin-bottom: 6px;
    }
    .inv-dialog .inv-input {
      width: 100%;
      padding: 9px 12px;
      background: rgba(0,0,0,0.3);
      border: 1px solid var(--gty-border);
      color: #fff;
      font-size: 14px;
      outline: none;
      transition: border-color 0.12s;
    }
    .inv-dialog .inv-input:focus { border-color: var(--gty-accent, #a855f7); }

    .inv-drop {
      width: 180px;
      height: 180px;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0,0,0,0.3);
      border: 2px dashed var(--gty-border);
      cursor: pointer;
      overflow: hidden;
      transition: all 0.15s;
    }
    .inv-drop:hover { border-color: var(--gty-accent, #a855f7); background: rgba(168, 85, 247, 0.05); }

    /* ─── Live indicator ────────────────────────────────────────────────── */
    .inv-live {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 7px 11px;
      border: 1px solid var(--gty-border);
      background: rgba(0,0,0,0.3);
      font-size: 10px; font-weight: 900;
      letter-spacing: 0.2em; text-transform: uppercase;
      color: var(--gty-text-faint, #71717a);
      cursor: default;
    }
    .inv-live-dot {
      width: 7px; height: 7px;
      background: #22c55e;
      border-radius: 999px;
      animation: inv-pulse 2s ease-out infinite;
      box-shadow: 0 0 0 0 rgba(34,197,94,0.6);
    }
    .inv-live-label { color: #22c55e; }
    .inv-live-time { font-family: ui-monospace, monospace; letter-spacing: 0; text-transform: none; }
    @keyframes inv-pulse {
      0%   { box-shadow: 0 0 0 0 rgba(34,197,94,0.7); }
      70%  { box-shadow: 0 0 0 8px rgba(34,197,94,0); }
      100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
    }

    /* ─── Inline quick-adjust buttons (cards) ─────────────────────────── */
    .inv-quick-btn {
      width: 28px; height: 28px;
      display: inline-flex; align-items: center; justify-content: center;
      border: 1px solid var(--gty-border);
      background: rgba(0,0,0,0.4);
      color: var(--gty-text-secondary, #a1a1aa);
      transition: all 0.12s;
    }
    .inv-quick-btn:hover:not(:disabled) {
      color: #fff; border-color: var(--gty-accent, #a855f7);
      background: rgba(168, 85, 247, 0.12);
    }
    .inv-quick-btn:disabled { opacity: 0.35; }
    .inv-quick-btn-plus:hover:not(:disabled) {
      color: #4ade80; border-color: #4ade80; background: rgba(74,222,128,0.1);
    }
    .inv-btn-ghost-active {
      color: #fff !important;
      border-color: var(--gty-accent, #a855f7) !important;
      background: rgba(168, 85, 247, 0.15) !important;
    }
    .inv-btn-danger { color: #f87171 !important; }
    .inv-btn-danger:hover { border-color: #ef4444 !important; background: rgba(239,68,68,0.08) !important; }

    /* ─── Floater layer (+N flying up from card) ────────────────────────── */
    .inv-floater-layer {
      position: absolute;
      inset: 0;
      pointer-events: none;
      overflow: visible;
      z-index: 5;
    }
    .inv-floater {
      position: absolute;
      bottom: 60px; right: 20px;
      font-family: ui-monospace, monospace;
      font-size: 22px; font-weight: 900;
      letter-spacing: -0.02em;
      padding: 4px 10px;
      border: 2px solid;
      background: rgba(0,0,0,0.85);
      pointer-events: none;
    }
    .inv-floater-up { color: #4ade80; border-color: #4ade80; box-shadow: 0 0 18px rgba(74,222,128,0.35); }
    .inv-floater-dn { color: #f87171; border-color: #f87171; box-shadow: 0 0 18px rgba(248,113,113,0.35); }

    /* ─── Bulk select state on cards ─────────────────────────────────── */
    .inv-card-selectable { user-select: none; }
    .inv-card-selectbox {
      position: absolute;
      top: 8px; left: 8px;
      z-index: 6;
      padding: 4px;
    }
    .inv-checkbox {
      width: 22px; height: 22px;
      border: 2px solid var(--gty-border);
      background: rgba(0,0,0,0.7);
      display: flex; align-items: center; justify-content: center;
      transition: all 0.12s;
    }
    .inv-card-selectable:hover .inv-checkbox { border-color: var(--gty-accent, #a855f7); }
    .inv-checkbox.checked {
      background: var(--gty-accent, #a855f7);
      border-color: var(--gty-accent, #a855f7);
      color: #000;
    }
    .inv-card-selected {
      border-color: var(--gty-accent, #a855f7) !important;
      box-shadow: 0 0 0 2px var(--gty-accent, #a855f7), 4px 4px 0 0 rgba(168,85,247,0.4) !important;
      transform: translate(-2px, -2px);
    }
    .inv-row-selected {
      border-color: var(--gty-accent, #a855f7) !important;
      background: rgba(168, 85, 247, 0.06) !important;
    }

    /* ─── List (compact) view ─────────────────────────────────────────── */
    .inv-row {
      display: flex; align-items: center; gap: 12px;
      padding: 10px 14px;
      background: var(--gty-bg-card, #18181b);
      border: 1px solid var(--gty-border);
      transition: all 0.12s;
    }
    .inv-row:hover { border-color: var(--gty-accent, #a855f7); background: rgba(168, 85, 247, 0.03); }
    .inv-row-thumb {
      width: 44px; height: 44px;
      display: flex; align-items: center; justify-content: center;
      background: #0a0a0a; border: 1px solid var(--gty-border);
      flex-shrink: 0; overflow: hidden;
    }
    .inv-row-thumb img { width: 100%; height: 100%; object-fit: cover; }
    .inv-row-kind {
      font-size: 8px; font-weight: 900; letter-spacing: 0.25em;
      padding: 1px 5px;
      border: 1px solid var(--gty-border);
      color: var(--gty-text-faint, #71717a);
    }
    .inv-row-qty { min-width: 100px; display: flex; justify-content: flex-end; }

    /* ─── Bulk action bar (sticky bottom) ─────────────────────────────── */
    .inv-bulk-bar {
      position: fixed;
      left: 50%;
      bottom: 24px;
      transform: translateX(-50%);
      z-index: 40;
      display: flex; align-items: stretch; gap: 0;
      background: var(--gty-bg-card, #18181b);
      border: 1px solid var(--gty-accent, #a855f7);
      box-shadow: 0 14px 40px -12px rgba(0,0,0,0.7), 0 0 0 1px rgba(168,85,247,0.3);
      max-width: calc(100vw - 32px);
    }
    .inv-bulk-count {
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      padding: 10px 18px;
      background: var(--gty-accent, #a855f7);
      color: #000;
      min-width: 80px;
    }
    .inv-bulk-actions {
      display: flex; align-items: center; gap: 6px;
      padding: 8px 12px;
      flex-wrap: wrap;
    }
    .inv-bulk-divider {
      width: 1px;
      align-self: stretch;
      background: var(--gty-border);
      margin: 4px 4px;
    }
    .inv-bulk-popover {
      position: absolute;
      bottom: calc(100% + 8px);
      left: 0;
      min-width: 200px;
      background: var(--gty-bg-card, #18181b);
      border: 1px solid var(--gty-accent, #a855f7);
      box-shadow: 0 12px 28px -8px rgba(0,0,0,0.7);
      max-height: 280px;
      overflow-y: auto;
      z-index: 50;
    }
    .inv-bulk-popover-item {
      display: flex; align-items: center; gap: 8px;
      width: 100%;
      padding: 9px 12px;
      font-size: 12px;
      color: #fff;
      text-align: left;
      transition: background 0.1s;
      border-bottom: 1px solid var(--gty-border);
    }
    .inv-bulk-popover-item:last-child { border-bottom: 0; }
    .inv-bulk-popover-item:hover { background: rgba(168, 85, 247, 0.1); }
    .inv-bulk-dot { width: 10px; height: 10px; border: 1px solid rgba(255,255,255,0.2); flex-shrink: 0; }

    /* ─── Mgmt-only flag UI ─────────────────────────────────────────── */
    .inv-mgmt-only {
      display: flex; align-items: center; gap: 8px;
      padding: 9px 12px;
      border: 1px dashed var(--gty-border);
      background: rgba(0,0,0,0.25);
      cursor: pointer;
      font-size: 11px;
      color: var(--gty-text-secondary, #a1a1aa);
      user-select: none;
    }
    .inv-mgmt-only:hover { border-color: var(--gty-accent, #a855f7); color: #fff; }
    .inv-mgmt-only input { display: none; }
    .inv-mgmt-only-box {
      width: 16px; height: 16px;
      display: flex; align-items: center; justify-content: center;
      border: 1.5px solid var(--gty-border);
      background: rgba(0,0,0,0.5);
      flex-shrink: 0;
      color: #000;
    }
    .inv-mgmt-only input:checked + .inv-mgmt-only-box {
      background: var(--gty-accent, #a855f7);
      border-color: var(--gty-accent, #a855f7);
    }
    .inv-mgmt-badge {
      display: inline-flex; align-items: center; gap: 3px;
      padding: 1px 5px;
      font-size: 8px; font-weight: 900;
      letter-spacing: 0.2em;
      color: #fbbf24;
      border: 1px solid rgba(251, 191, 36, 0.4);
      background: rgba(251, 191, 36, 0.08);
    }

    /* ─── Item detail drawer ─────────────────────────────────────────── */
    .inv-detail-hero {
      position: relative;
      width: 100%;
      aspect-ratio: 16 / 9;
      background: #0a0a0a;
      border-bottom: 1px solid var(--gty-border);
      overflow: hidden;
    }
    .inv-detail-hero img { width: 100%; height: 100%; object-fit: cover; cursor: zoom-in; }
    .inv-detail-cat {
      position: absolute;
      top: 12px; left: 12px;
      font-size: 9px; font-weight: 900;
      text-transform: uppercase; letter-spacing: 0.2em;
      padding: 3px 8px;
      border: 1px solid;
      background: rgba(0,0,0,0.85);
      display: inline-flex; align-items: center;
    }
    .inv-detail-qty {
      padding: 14px;
      border: 1px solid var(--gty-border);
      background: rgba(0,0,0,0.3);
    }

    /* ─── Breathing blueprint skeleton (replaces shimmer) ──────────────── */
    .inv-skeleton {
      aspect-ratio: 1 / 1.4;
      padding: 12px;
      display: flex; flex-direction: column; gap: 10px;
      border: 1px dashed var(--gty-border);
      background: transparent;
      animation: inv-breathe 2.4s ease-in-out infinite;
    }
    .inv-skeleton-img {
      flex: 1;
      border: 1px dashed var(--gty-border);
      background:
        repeating-linear-gradient(
          45deg,
          transparent 0 8px,
          rgba(168,85,247,0.04) 8px 9px
        );
    }
    .inv-skeleton-line {
      height: 10px;
      border: 1px dashed var(--gty-border);
    }
    .inv-skeleton-num {
      width: 60%;
      height: 24px;
      border: 1px dashed var(--gty-border);
    }
    .inv-skeleton-row {
      height: 64px;
      border: 1px dashed var(--gty-border);
      animation: inv-breathe 2.4s ease-in-out infinite;
    }
    @keyframes inv-breathe {
      0%, 100% { opacity: 0.30; }
      50%      { opacity: 0.62; }
    }

    /* Old shimmer skeleton kept for backwards compat (not used after this update) */
    .inv-card-skeleton {
      aspect-ratio: 1 / 1.4;
      background: linear-gradient(90deg, #18181b 0%, #27272a 50%, #18181b 100%);
      background-size: 200% 100%;
      border: 1px solid var(--gty-border);
      animation: inv-shimmer 1.4s linear infinite;
    }
    @keyframes inv-shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `}</style>
);


// ─── Dashboard ────────────────────────────────────────────────────────────────

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser]             = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [drivers, setDrivers]       = useState([]);
  const [newsPosts, setNewsPosts]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [driversLoading, setDriversLoading] = useState(false);
  const [activePage, setActivePage] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isManagement    = user?.role_names?.some(r => MANAGEMENT_ROLES.includes(r));
  const isSeniorOperator = user?.role_names?.includes('Senior Operator');
  const isMechanic      = user?.roles?.includes('1468525796371992576');
  const isRental        = isManagement || isSeniorOperator ||
                          user?.roles?.includes('1512490348662755418') ||
                          user?.id === '1512490348662755418';
  const canEditTeam     = isManagement || isSeniorOperator || isMechanic ||
                          user?.role_names?.includes('Expert Truck Driver');

  const handleLogout = () => { localStorage.removeItem('gty_token'); navigate('/'); };

  const refreshProfile = useCallback(async () => {
    try {
      const p = await authAPI.getUserProfile();
      setUser(p.data);
    } catch { /* silent */ }
  }, []);

  const fetchDrivers = useCallback(async () => {
    setDriversLoading(true);
    try { const r = await managementAPI.getDriversStatus(); setDrivers(r.data); }
    catch (e) { console.error(e); }
    finally { setDriversLoading(false); }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, s, n] = await Promise.all([
          authAPI.getUserProfile(),
          authAPI.getPaymentStatus(),
          newsAPI.getNews(),
        ]);
        setUser(p.data);
        setPaymentStatus(s.data);
        setNewsPosts(n.data);
      } catch (e) {
        // 401 is handled by the axios interceptor (clears token + redirects)
        // For other errors (network / server cold start) retry once after 3s
        if (e.response?.status !== 401) {
          setTimeout(async () => {
            try {
              const [p, s, n] = await Promise.all([
                authAPI.getUserProfile(),
                authAPI.getPaymentStatus(),
                newsAPI.getNews(),
              ]);
              setUser(p.data);
              setPaymentStatus(s.data);
              setNewsPosts(n.data);
            } catch { /* silent */ }
            finally { setLoading(false); }
          }, 3000);
          return; // don't setLoading(false) yet — wait for retry
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!isManagement) return;
    fetchDrivers();
    const t = setInterval(fetchDrivers, 30000);
    return () => clearInterval(t);
  }, [isManagement, fetchDrivers]);

  if (loading) return <div className="min-h-screen pt-24 flex items-center justify-center"><div className="text-slate-400 text-lg">Loading...</div></div>;

  const avatarUrl = user?.custom_avatar_url
    ? `${process.env.REACT_APP_BACKEND_URL || ''}${user.custom_avatar_url}`
    : user?.avatar
      ? `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`
      : `https://cdn.discordapp.com/embed/avatars/0.png`;

  const renderContent = () => {
    switch (activePage) {
      case 'overview':  return <OverviewContent user={user} paymentStatus={paymentStatus} drivers={drivers} isManagement={isManagement} newsPosts={newsPosts} onOpenNews={() => setActivePage('news')} />;
      case 'news':      return <NewsContent isManagement={isManagement} />;
      case 'documents': return <DocumentsContent isManagement={isManagement} />;
      case 'trucks':    return <TrucksContent user={user} />;
      case 'drivers':   return <DriversContent drivers={drivers} driversLoading={driversLoading} />;
      case 'rental':    return isRental ? <TruckRentalContent isManagement={isManagement} /> : null;
      case 'profile':      return <ProfileContent user={user} onProfileUpdate={refreshProfile} />;
      case 'mgmt_docs':    return isManagement ? <MgmtDocumentsContent /> : null;
      case 'driver_names': return isManagement ? <DriverNamesContent /> : null;
      case 'badge_gen':    return isManagement ? <BadgeGeneratorContent /> : null;
      case 'applications': return isManagement ? <ApplicationsContent /> : null;
      case 'broadcast':        return isManagement ? <BroadcastContent /> : null;
      case 'mechanic':         return (isManagement || isMechanic) ? <MechanicContent /> : null;
      case 'create_channel':   return isManagement ? <CreateChannelContent /> : null;
      case 'team_editor':    return canEditTeam ? <TeamEditorContent /> : null;
      case 'staff_notes':    return isManagement ? <StaffNotesContent /> : null;
      case 'my_payments':    return !isManagement ? <PaymentsContent isManagement={false} user={user} paymentStatus={paymentStatus} /> : null;
      case 'payments':       return isManagement ? <PaymentsContent isManagement={true} user={user} paymentStatus={paymentStatus} /> : null;
      case 'loa':            return <LOARequestContent user={user} />;
      case 'loa_mgmt':       return isManagement ? <LOAManagementContent /> : null;
      case 'cdl_mgmt':       return isManagement ? <CdlManagementContent /> : null;
      case 'tutorials': return <TutorialsContent isManagement={isManagement} />;
      default:            return null;
    }
  };

  const handleNavClick = (key) => {
    setActivePage(key);
    setSidebarOpen(false);
  };

  const SidebarContents = () => (
    <>
      {/* Logo area */}
      <div className="flex items-center gap-2.5 px-4 py-4 border-b border-[var(--gty-border)]">
        <img src="/logo.png" alt="GTY" className="w-7 h-7 rounded shrink-0" />
        <span className="text-sm font-black uppercase tracking-wide truncate">Go Truck Yourself</span>
        <span className="w-2 h-2 rounded-full bg-green-400 shrink-0 ml-auto" title="Online" />
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-5">
        <div className="space-y-0.5">
          <NavItem item={{ key: 'tutorials', label: 'Tutorials', icon: BookMarked }} active={activePage === 'tutorials'} onClick={handleNavClick} />
        </div>

        <div className="space-y-0.5">
          {NAV_GENERAL.map(item => <NavItem key={item.key} item={item} active={activePage === item.key} onClick={handleNavClick} />)}
        </div>

        <SidebarGroup label="My Work">
          {NAV_MYWORK.filter(item => !(item.key === 'my_payments' && isManagement)).map(item => (
            <NavItem key={item.key} item={item} active={activePage === item.key} onClick={handleNavClick} />
          ))}
        </SidebarGroup>

        <SidebarGroup label="Resources">
          <NavItem
            item={{ key: 'inventory', label: 'Inventory', icon: Package }}
            active={false}
            onClick={() => { window.open('/inventory', '_blank', 'noopener'); setSidebarOpen(false); }}
          />
        </SidebarGroup>

        {isManagement && (
          <SidebarGroup label="Management">
            {NAV_MANAGEMENT.map(item => (
              <NavItem key={item.key} item={item} active={activePage === item.key} onClick={handleNavClick} />
            ))}
            <NavItem item={{ key: 'rental', label: 'Truck Rental', icon: Key }} active={activePage === 'rental'} onClick={handleNavClick} />
            <CollapsibleSidebarGroup label="Tools">
              {NAV_TOOLS.map(item => <NavItem key={item.key} item={item} active={activePage === item.key} onClick={handleNavClick} />)}
            </CollapsibleSidebarGroup>
          </SidebarGroup>
        )}

        {!isManagement && isSeniorOperator && (
          <SidebarGroup label="Operations">
            <NavItem item={{ key: 'rental', label: 'Truck Rental', icon: Key }} active={activePage === 'rental'} onClick={handleNavClick} />
            <NavItem item={{ key: 'team_editor', label: 'Team Page', icon: Users }} active={activePage === 'team_editor'} onClick={handleNavClick} />
          </SidebarGroup>
        )}

        {!isManagement && !isSeniorOperator && isRental && (
          <SidebarGroup label="Operations">
            <NavItem item={{ key: 'rental', label: 'Truck Rental', icon: Key }} active={activePage === 'rental'} onClick={handleNavClick} />
          </SidebarGroup>
        )}

        {!isManagement && isMechanic && (
          <SidebarGroup label="Mechanic">
            <NavItem item={{ key: 'mechanic', label: 'Mechanic', icon: Wrench }} active={activePage === 'mechanic'} onClick={handleNavClick} />
            <NavItem item={{ key: 'team_editor', label: 'Team Page', icon: Users }} active={activePage === 'team_editor'} onClick={handleNavClick} />
          </SidebarGroup>
        )}

        {!isManagement && !isSeniorOperator && !isMechanic && canEditTeam && (
          <SidebarGroup label="Team">
            <NavItem item={{ key: 'team_editor', label: 'Team Page', icon: Users }} active={activePage === 'team_editor'} onClick={handleNavClick} />
          </SidebarGroup>
        )}

        <div className="border-t border-[var(--gty-border)] pt-4">
          <a href="/" className="flex items-center gap-2.5 px-3 py-2 text-sm text-[var(--gty-text-muted)] hover:text-white transition-colors">
            <span className="text-xs">↗</span> View Website
          </a>
        </div>
      </nav>

      {/* User bar */}
      <div className="border-t border-[var(--gty-border)] p-3">
        <div className="flex items-center gap-2.5 px-2 py-2 rounded hover:bg-[var(--gty-bg-card)] transition-colors">
          <img src={avatarUrl} alt={user?.username} className="w-8 h-8 rounded-full border-2 border-[var(--gty-accent)] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold truncate">{user?.display_name || user?.username}</div>
            <div className="text-[var(--gty-text-muted)] text-xs truncate">{user?.role_names?.[0] ?? 'Driver'}</div>
          </div>
          <button onClick={handleLogout} title="Logout" className="text-[var(--gty-text-muted)] hover:text-red-400 transition-colors shrink-0 p-1">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen pt-16 bg-[var(--gty-bg-base)] flex" data-testid="dashboard-page">

      {/* ── Sidebar: desktop (sticky) ── */}
      <aside className="hidden md:flex w-56 shrink-0 border-r border-[var(--gty-border)] flex-col sticky top-16 h-[calc(100vh-4rem)]">
        <SidebarContents />
      </aside>

      {/* ── Sidebar: mobile drawer ── */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden fixed inset-0 z-40 bg-black/60"
              onClick={() => setSidebarOpen(false)}
            />
            {/* Drawer */}
            <motion.aside
              key="drawer"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.22 }}
              className="md:hidden fixed left-0 top-16 bottom-0 z-50 w-64 border-r border-[var(--gty-border)] flex flex-col bg-[var(--gty-bg-base)]"
            >
              <SidebarContents />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ── Content ── */}
      <main className="flex-1 overflow-y-auto min-w-0">
        {/* Mobile topbar */}
        <div className="md:hidden flex items-center gap-3 px-4 py-3 border-b-2 border-purple-600 sticky top-0 bg-[var(--gty-bg-base)] z-30">
          <button
            onClick={() => setSidebarOpen(o => !o)}
            className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white transition-colors rounded-sm font-black text-xs uppercase tracking-widest flex-shrink-0"
            aria-label="Open navigation menu"
          >
            {sidebarOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
            <span>Menu</span>
          </button>
          <span className="text-sm font-black uppercase tracking-wide truncate text-white">{PAGE_TITLES[activePage]}</span>
        </div>

        <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-10">
          <AnimatePresence mode="wait">
            <motion.div key={activePage} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.15 }}>
              <h1 className="hidden md:block text-3xl font-black uppercase tracking-tight mb-8">{PAGE_TITLES[activePage]}</h1>
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

    </div>
  );
};

export default Dashboard;
